export const definitionsTree = [
  {
    math: "derivative",
    text: (
      <div>
        <h1>Function derivative</h1>

        <p>
          Takes the derivative of an expression expressed in parser Nodes. The derivative will be
          taken over the supplied variable in the second parameter. If there are multiple variables
          in the expression, it will return a partial derivative.
        </p>

        <p>This uses rules of differentiation which can be found here:</p>

        <ul>
          <li>
            <a href="https://en.wikipedia.org/wiki/Differentiation_rules">
              Differentiation rules (Wikipedia)
            </a>
          </li>
        </ul>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            derivative(expr, variable)
            <br />
            derivative(expr, variable, options)
            <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>expr</code>
              </td>
              <td>Node | string</td>
              <td>The expression to differentiate</td>
            </tr>
            <tr>
              <td>
                <code>variable</code>
              </td>
              <td>SymbolNode | string</td>
              <td>The variable over which to differentiate</td>
            </tr>
            <tr>
              <td>
                <code>options</code>
              </td>
              <td>{"{simplify: boolean}"}</td>
              <td>
                There is one option available, <code>simplify</code>, which is true by default. When
                false, output will not be simplified.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>ConstantNode | SymbolNode | ParenthesisNode | FunctionNode | OperatorNode</td>
              <td>
                The derivative of <code>expr</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p className="highlight">Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <div>
          <div>
            <pre className="highlight">
              <code>
                derivative(&apos;x^2&apos;, &apos;x&apos;)
                <br />
                {`// Node '2 * x'`}
                <br />
                <br />
                derivative(&apos;x^2&apos;, &apos;x&apos;, {"{simplify: false}"})<br />
                {`// Node '2 * 1 * x ^ (2 - 1)'`}
                <br />
                <br />
                derivative(&apos;sin(2x)&apos;, &apos;x&apos;) <br />
                {`// Node '2 * cos(2 * x)'`}
                <br />
                <br />
                derivative(&apos;2*x&apos;, &apos;x&apos;).evaluate() <br />
                {"// number 2"} <br />
                <br />
                derivative(&apos;x^2&apos;, &apos;x&apos;).evaluate({"{x: 4}"}) <br />
                {"// number 8"} <br />
              </code>
            </pre>
          </div>
        </div>
      </div>
    ),
  },
  {
    math: "intercept",
    text: (
      <div>
        <h1>Function intercept</h1>

        <p>
          Calculates the Y-intercept of a linear regression line that best fits a set of (x, y) data
          points. The <b>intercept</b> function determines the expected value of Y when X is zero.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"intercept(xValues, yValues)"} <br />
            {"// xValues: Array of x-axis numerical values."} <br />
            {"// yValues: Array of y-axis numerical values."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">xValues</code>
              </td>
              <td>Array&lt;Number&gt;</td>
              <td>An array of numerical values representing the independent variable (X).</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">yValues</code>
              </td>
              <td>Array&lt;Number&gt;</td>
              <td>An array of numerical values representing the dependent variable (Y).</td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"intercept([1, 2, 3, 4, 5], [3, 5, 7, 9, 11])"} <br />
            {"//"} Returns 1 — indicating the line crosses the Y-axis at value 1 when X is 0. <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "leafCount",
    text: (
      <div>
        <h1>Function leafCount</h1>

        <p>
          Gives the number of “leaf nodes” in the parse tree of the given expression A leaf node is
          one that has no subexpressions, essentially either a symbol or a constant. Note that{" "}
          <code className="language-plaintext highlighter-rouge">5!</code> has just one leaf, the
          `5`; the unary factorial operator does not add a leaf. On the other hand, function symbols
          do add leaves, so{" "}
          <code className="language-plaintext highlighter-rouge">sin(x)/cos(x)</code> has four
          leaves.
        </p>

        <p>
          The <code className="language-plaintext highlighter-rouge">simplify()</code> function
          should generally not increase the{" "}
          <code className="language-plaintext highlighter-rouge">leafCount()</code>
          of an expression, although currently there is no guarantee that it never does so. In many
          cases, <code className="language-plaintext highlighter-rouge">simplify()</code> reduces
          the leaf count.
        </p>

        <h2>Syntax</h2>

        <div>
          <div>
            <pre className="highlight">
              <code>leafCount(expr)</code>
            </pre>
          </div>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">expr</code>
              </td>
              <td>Node | string</td>
              <td>The expression to count the leaves of</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number</td>
              <td>
                The number of leaves of{" "}
                <code className="language-plaintext highlighter-rouge">expr</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>
        <pre className="highlight">
          <code>
            leafCount(&apos;x&apos;) <br />
            {"// 1"} <br /> <br />
            leafCount(parse(&apos;a*d-b*c&apos;))
            <br />
            {"// 4"} <br /> <br />
            leafCount(&apos;[a,b;c,d][0,1]&apos;) <br />
            {"// 6"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "lsolve",
    text: (
      <div>
        <h1>Function lsolve</h1>

        <p>
          Finds one solution of a linear equation system by forwards substitution. Matrix must be a
          lower triangular matrix. Throws an error if there’s no solution.
        </p>

        <p>
          <code className="language-plaintext highlighter-rouge">L * x = b</code>
        </p>

        <h2>Syntax</h2>

        <div>
          <div>
            <pre className="highlight">
              <code>lsolve(L, b)</code>
            </pre>
          </div>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">L</code>
              </td>
              <td>Matrix, Array</td>
              <td>A N x N matrix or array (L)</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">b</code>
              </td>
              <td>Matrix, Array</td>
              <td>A column vector with the b values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>DenseMatrix | Array</td>
              <td>A column vector with the linear system solution (x)</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>
        <pre className="highlight">
          <code>
            const a = [[-2, 3], [2, 1]]
            <br />
            const b = [11, 9]
            <br />
            const x = lsolve(a, b)
            <br />
            {"// [[-5.5], [20]]"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "lsolveAll",
    text: (
      <div>
        <h1>Function lsolveAll</h1>

        <p>
          Finds all solutions of a linear equation system by forwards substitution. Matrix must be a
          lower triangular matrix.
        </p>

        <p>
          <code className="language-plaintext highlighter-rouge">L * x = b</code>
        </p>

        <h2>Syntax</h2>

        <div>
          <div>
            <pre className="highlight">
              <code>lsolveAll(L, b)</code>
            </pre>
          </div>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">L</code>
              </td>
              <td>Matrix, Array</td>
              <td>A N x N matrix or array (L)</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">b</code>
              </td>
              <td>Matrix, Array</td>
              <td>A column vector with the b values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>DenseMatrix[] | Array[]</td>
              <td>
                An array of affine-independent column vectors (x) that solve the linear system
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const a = [[-2, 3], [2, 1]]
            <br />
            const b = [11, 9]
            <br />
            const x = lsolveAll(a,b) <br />
            {"// [ [[-5.5], [20]] ]"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "lup",
    text: (
      <div>
        <h1>Function lup</h1>

        <p>
          Calculate the Matrix LU decomposition with partial pivoting. Matrix{" "}
          <code className="language-plaintext highlighter-rouge">A</code> is decomposed in two
          matrices (<code className="language-plaintext highlighter-rouge">L</code>,{" "}
          <code className="language-plaintext highlighter-rouge">U</code>) and a row permutation
          vector <code className="language-plaintext highlighter-rouge">p</code> where{" "}
          <code className="language-plaintext highlighter-rouge">A[p,:] = L * U</code>
        </p>

        <h2>Syntax</h2>

        <div>
          <div>
            <pre className="highlight">
              <code>lup(A)</code>
            </pre>
          </div>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">A</code>
              </td>
              <td>Matrix | Array</td>
              <td>A two dimensional matrix or array for which to get the LUP decomposition.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{"{L: Array | Matrix, U: Array | Matrix, P: Array.&lt;number&gt;}"}</td>
              <td>
                The lower triangular matrix, the upper triangular matrix and the permutation matrix.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const m = [[2, 1], [1, 4]] <br />
            const r = lup(m) <br />
            {"// r = {"} <br />
            {"//   L: [[1, 0], [0.5, 1]],"} <br />
            {"//   U: [[2, 1], [0, 3.5]],"} <br />
            {"//   P: [0, 1]"} <br />
            {"// }"} <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "lusolve",
    text: (
      <div>
        <h1>Function lusolve</h1>

        <p>
          Solves the linear system{" "}
          <code className="language-plaintext highlighter-rouge">A * x = b</code> where{" "}
          <code className="language-plaintext highlighter-rouge">A</code> is an [n x n] matrix and{" "}
          <code className="language-plaintext highlighter-rouge">b</code> is a [n] column vector.
        </p>

        <h2>Syntax</h2>

        <div className="language-js highlighter-rouge">
          <div className="highlight">
            <pre className="highlight">
              <code>
                lusolve(A, b) <br />
                {"// returns column vector with the solution to the linear"} <br />
                {"// system A * x = b"} <br /> <br />
                lusolve(lup, b) <br />
                {"// returns column vector with the solution to the linear"} <br />
                {"// system A * x = b, lup = lup(A)"} <br />
              </code>
            </pre>
          </div>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">A</code>
              </td>
              <td>Matrix | Array | Object</td>
              <td>Invertible Matrix or the Matrix LU decomposition</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">b</code>
              </td>
              <td>Matrix | Array</td>
              <td>Column Vector</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">order</code>
              </td>
              <td>number</td>
              <td>
                The Symbolic Ordering and Analysis order, see slu for details. Matrix must be a
                SparseMatrix
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">threshold</code>
              </td>
              <td>Number</td>
              <td>
                Partial pivoting threshold (1 for partial pivoting), see slu for details. Matrix
                must be a SparseMatrix.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>DenseMatrix | Array</td>
              <td>Column vector with the solution to the linear system A * x = b</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const m = <br />
            [[1, 0, 0, 0], [0, 2, 0, 0], <br />
            [0, 0, 3, 0], [0, 0, 0, 4]] <br />
            const x = lusolve(m, [-1, -1, -1, -1]) <br />
            {"// x = [[-1], [-0.5], [-1/3], [-0.25]]"} <br />
            <br />
            const f = lup(m) <br />
            const x1 = lusolve(f, [-1, -1, -1, -1]) <br />
            {"// x1 = [[-1], [-0.5], [-1/3], [-0.25]]"} <br />
            const x2 = lusolve(f, [1, 2, 1, -1]) <br />
            {"// x2 = [[1], [1], [1/3], [-0.25]]"} <br />
            <br />
            const a = [[-2, 3], [2, 1]] <br />
            const b = [11, 9] <br />
            const x = lusolve(a, b) <br />
            {"// [[2], [5]]"} <br />
            <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "lyap",
    text: (
      <div>
        <h1>Function lyap</h1>

        <p>
          Solves the Continuous-time Lyapunov equation AP+PA’+Q=0 for P, where Q is an input matrix.
          When Q is symmetric, P is also symmetric. Notice that different equivalent definitions
          exist for the Continuous-time Lyapunov equation. <br />
          <a href="https://en.wikipedia.org/wiki/Lyapunov_equation">Lyapunov equation</a>
        </p>

        <h2>Syntax</h2>

        <div>
          <div>
            <pre className="highlight">
              <code>lyap(A, Q)</code>
            </pre>
          </div>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">A</code>
              </td>
              <td>Matrix | Array</td>
              <td>Matrix A</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">Q</code>
              </td>
              <td>Matrix | Array</td>
              <td>Matrix Q</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Matrix | Array</td>
              <td>Matrix P solution to the Continuous-time Lyapunov equation AP+PA’=Q</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const A = [[-2, 0], [1, -4]] <br />
            const Q = [[3, 1], [1, 3]] <br />
            const P = lyap(A, Q) <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "polynomialRoot",
    text: (
      <div>
        <h1>Function polynomialRoot</h1>

        <p>
          Finds the numerical values of the distinct roots of a polynomial with real or complex
          coefficients. Currently operates only on linear, quadratic, and cubic polynomials using
          the standard formulas for the roots.
        </p>

        <h2>Syntax</h2>

        <div>
          <div>
            <pre className="highlight">
              <code>
                polynomialRoot( <br />
                {"    "}constant, <br />
                {"    "}linearCoeff, <br />
                {"    "}quadraticCoeff, <br />
                {"    "}cubicCoeff <br />)
              </code>
            </pre>
          </div>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">coeffs</code>
              </td>
              <td>… number | Complex</td>
              <td>
                The coefficients of the polynomial, starting with with the constant coefficent,
                followed by the linear coefficient and subsequent coefficients of increasing powers.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array</td>
              <td>The distinct roots of the polynomial</td>
            </tr>
          </tbody>
        </table>

        <h3 id="throws">Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"// linear"} <br />
            polynomialRoot(6, 3) <br />
            {"// [-2]"} <br />
            polynomialRoot(complex(6,3), 3) <br />
            {"// [-2 - i]"} <br />
            polynomialRoot(complex(6,3), complex(2,1))
            {"// [-3 + 0i]"} <br /> <br />
            {"// quadratic"} <br />
            polynomialRoot(2, -3, 1) <br />
            {"// [2, 1]"} <br />
            polynomialRoot(8, 8, 2) <br />
            {"// [-2]"} <br />
            polynomialRoot(-2, 0, 1) <br />
            {"// [1.4142135623730951, -1.4142135623730951]"} <br />
            polynomialRoot(2, -2, 1) <br />
            {"// [1 + i, 1 - i]"} <br />
            polynomialRoot(complex(1,3), complex(-3, -2), 1) <br />
            {"// [2 + i, 1 + i]"} <br /> <br />
            {"// cubic"} <br />
            polynomialRoot(-6, 11, -6, 1) <br />
            {"// [1, 3, 2]"} <br />
            polynomialRoot(-8, 0, 0, 1) <br />
            {"// [-1 - 1.7320508075688774i, 2, -1 + 1.7320508075688774i]"} <br />
            polynomialRoot(0, 8, 8, 2) <br />
            {"// [0, -2]"} <br />
            polynomialRoot(1, 1, 1, 1) <br />
            {"// [-1 + 0i, 0 - i, 0 + i]"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "qr",
    text: (
      <div>
        <h1>Function qr</h1>

        <p>
          Calculate the Matrix QR decomposition. Matrix{" "}
          <code className="language-plaintext highlighter-rouge">A</code> is decomposed in two
          matrices (<code className="language-plaintext highlighter-rouge">Q</code>,{" "}
          <code className="language-plaintext highlighter-rouge">R</code>) where{" "}
          <code className="language-plaintext highlighter-rouge">Q</code> is an orthogonal matrix
          and <code className="language-plaintext highlighter-rouge">R</code> is an upper triangular
          matrix.
        </p>

        <h2>Syntax</h2>

        <div>
          <div>
            <pre className="highlight">
              <code>qr(A)</code>
            </pre>
          </div>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">A</code>
              </td>
              <td>Matrix | Array</td>
              <td>A two dimensional matrix or array for which to get the QR decomposition.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{"{Q: Array | Matrix, R: Array | Matrix}"}</td>
              <td>Q: the orthogonal matrix and R: the upper triangular matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const m = [ <br />
            {"    "}[1, -1, 4], <br />
            {"    "}[1, 4, -2], <br />
            {"    "}[1, 4, 2], <br />
            {"    "}[1, -1, 0] <br />
            ] <br />
            const result = qr(m) <br />
            {"// r = {"} <br />
            {"//"}
            {"    "}Q: {"["} <br />
            {"//"}
            {"    "}
            {"    "}[0.5, -0.5, 0.5], <br />
            {"//"}
            {"    "}
            {"    "}[0.5, 0.5, -0.5], <br />
            {"//"}
            {"    "}
            {"    "}[0.5, 0.5, 0.5], <br />
            {"//"}
            {"    "}
            {"    "}[0.5, -0.5, -0.5], <br />
            {"//"}
            {"    "}
            {"]"}, <br />
            {"//"}
            {"    "}R: [ <br />
            {"//"}
            {"    "}
            {"    "}[2, 3, 2], <br />
            {"//"}
            {"    "}
            {"    "}[0, 5, -2], <br />
            {"//"}
            {"    "}
            {"    "}[0, 0, 4], <br />
            {"//"}
            {"    "}
            {"    "}[0, 0, 0] <br />
            {"//"}
            {"    "}] <br />
            {"//"}
            {"}"} <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "rationalize",
    text: (
      <div>
        <h1>Function rationalize</h1>

        <p>
          Transform a rationalizable expression in a rational fraction. If rational fraction is one
          variable polynomial then converts the numerator and denominator in canonical form, with
          decreasing exponents, returning the coefficients of numerator.
        </p>

        <h2>Syntax</h2>

        <div>
          <pre className="highlight">
            <code>
              rationalize(expr) <br />
              rationalize(expr, detailed) <br />
              rationalize(expr, scope) <br />
              rationalize(expr, scope, detailed) <br />
            </code>
          </pre>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">expr</code>
              </td>
              <td>Node | string</td>
              <td>The expression to check if is a polynomial expression</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">optional</code>
              </td>
              <td>Object | boolean</td>
              <td>
                scope of expression or true for already evaluated rational expression at input
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">detailed</code>
              </td>
              <td>Boolean</td>
              <td>optional True if return an object, false if return expression node (default)</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Object | Node</td>
              <td>
                The rational polynomial of{" "}
                <code className="language-plaintext highlighter-rouge">expr</code> or an object{" "}
                <br />
                <code className="language-plaintext highlighter-rouge">
                  {"{"}
                  (expression, numerator, denominator, variables, coefficients)
                  {"}"} <br />
                </code>
                where <code className="language-plaintext highlighter-rouge">expression</code> is a{" "}
                <code className="language-plaintext highlighter-rouge">Node</code> with the node
                simplified expression,{" "}
                <code className="language-plaintext highlighter-rouge">numerator</code> is a{" "}
                <code className="language-plaintext highlighter-rouge">Node</code> with the
                simplified numerator of expression,{" "}
                <code className="language-plaintext highlighter-rouge">denominator</code> is a{" "}
                <code className="language-plaintext highlighter-rouge">Node</code> or{" "}
                <code className="language-plaintext highlighter-rouge">boolean</code> with the
                simplified denominator or{" "}
                <code className="language-plaintext highlighter-rouge">false</code> (if there is no
                denominator),{" "}
                <code className="language-plaintext highlighter-rouge">variables</code> is an array
                with variable names, and{" "}
                <code className="language-plaintext highlighter-rouge">coefficients</code> is an
                array with coefficients of numerator sorted by increased exponent{" "}
                {"{Expression Node}"} node simplified expression
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            rationalize(&apos;sin(x)+y&apos;) <br />
            {"// Error: There is an unsolved function call"} <br />
            rationalize(&apos;2x/y - y/(x+1)&apos;) <br />
            {"// (2*x^2-y^2+2*x)/(x*y+y)"} <br />
            rationalize(&apos;(2x+1)^6&apos;) <br />
            {"// 64*x^6+192*x^5+240*x^4+160*x^3+60*x^2+12*x+1"} <br />
            rationalize(&apos;2x/((2x-1)/(3x+2))-5x/((3x+4)/(2x^2-5))+3&apos;) <br />
            {"// -20*x^4+28*x^3+104*x^2+6*x-12)/(6*x^2+5*x-4)"} <br />
            rationalize(&apos;x+x+x+y&apos;,{"{y:1}"}) <br />
            {"// 3*x+1"} <br />
            rationalize(&apos;x+x+x+y&apos;,{}) <br />
            {"// 3*x+y"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "resolve",
    text: (
      <div>
        <h1>Function resolve</h1>

        <p>resolve(expr, scope) replaces variable nodes with their scoped values</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>resolve(expr, scope)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">node</code>
              </td>
              <td>Node | Node[]</td>
              <td>The expression tree (or trees) to be simplified</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">scope</code>
              </td>
              <td>Object</td>
              <td>Scope specifying variables to be resolved</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Node | Node[]</td>
              <td>
                Returns <code className="language-plaintext highlighter-rouge">node</code> with
                variables recursively substituted.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>ReferenceError</td>
              <td>
                If there is a cyclic dependency among the variables in{" "}
                <code className="language-plaintext highlighter-rouge">scope</code>, resolution is
                impossible and a ReferenceError is thrown.
              </td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            resolve(&apos;x + y&apos;, {"{x:1, y:2}"}) <br />
            {"// Node '1 + 2' <br />"} <br />
            resolve(parse(&apos;x+y&apos;), {"{x:1, y:2}"}) <br />
            {"// Node '1 + 2'"} <br />
            simplify(&apos;x+y&apos;, {"{x:2, y: parse('x+x')}"}).toString() <br />
            {"//"} &quot;6&quot;
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "schur",
    text: (
      <div>
        <h1>Function schur</h1>

        <p>
          Performs a real Schur decomposition of the real matrix A = UTU’ where U is orthogonal and
          T is upper quasi-triangular. <br />
          <a href="https://en.wikipedia.org/wiki/Schur_decomposition">Schur decomposition</a>
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>schur(A)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">A</code>
              </td>
              <td>Array | Matrix</td>
              <td>Matrix A</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{"{U: Array | Matrix, T: Array | Matrix}"}</td>
              <td>Object containing both matrix U and T of the Schur Decomposition A=UTU’</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const A = [[1, 0], [-4, 3]] <br />
            schur(A) <br />
            {"// returns {T: [[3, 4], [0, 1]], R: [[0, 1], [-1, 0]]}"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "simplify",
    text: (
      <div>
        <h1>Function simplify</h1>

        <p>Simplify an expression tree.</p>

        <p>
          A list of rules are applied to an expression, repeating over the list until no further
          changes are made. It’s possible to pass a custom set of rules to the function as second
          argument. A rule can be specified as an object, string, or function:
        </p>

        <div className="language-plaintext highlighter-rouge">
          <div className="highlight">
            <pre className="highlight">
              <code>
                const rules = [{"{ l: 'n1*n3 + n2*n3', r: '(n1+n2)*n3' },"} <br />
                &apos;n1*n3 + n2*n3 -&gt; (n1+n2)*n3&apos;, <br />
                {"function (node) {"} <br />
                {"// ... return a new node or return the node unchanged"} <br />
                {"return node"} <br />
                {"}"}]
              </code>
            </pre>
          </div>
        </div>

        <p>
          String and object rules consist of a left and right pattern. The left is used to match
          against the expression and the right determines what matches are replaced with. The main
          difference between a pattern and a normal expression is that variables starting with the
          following characters are interpreted as wildcards:
        </p>

        <ul>
          <li>‘n’ - Matches any node [Node]</li>
          <li>‘c’ - Matches a constant literal (5 or 3.2) [ConstantNode]</li>
          <li>‘cl’ - Matches a constant literal; same as c [ConstantNode]</li>
          <li>
            ‘cd’ - Matches a decimal literal (5 or -3.2) [ConstantNode or unaryMinus wrapping a
            ConstantNode]
          </li>
          <li>
            ‘ce’ - Matches a constant expression (-5 or √3) [Expressions consisting of only
            ConstantNodes, functions, and operators]
          </li>
          <li>
            ‘v’ - Matches a variable; anything not matched by c (-5 or x) [Node that is not a
            ConstantNode]
          </li>
          <li>‘vl’ - Matches a variable literal (x or y) [SymbolNode]</li>
          <li>
            ‘vd’ - Matches a non-decimal expression; anything not matched by cd (x or √3) [Node that
            is not a ConstantNode or unaryMinus that is wrapping a ConstantNode]
          </li>
          <li>
            ‘ve’ - Matches a variable expression; anything not matched by ce (x or 2x) [Expressions
            that contain a SymbolNode or other non-constant term]
          </li>
        </ul>

        <p>
          The default list of rules is exposed on the function as{" "}
          <code className="language-plaintext highlighter-rouge">simplify.rules</code>
          and can be used as a basis to built a set of custom rules. Note that since the{" "}
          <code className="language-plaintext highlighter-rouge">simplifyCore</code> function is in
          the default list of rules, by default simplify will convert any function calls in the
          expression that have operator equivalents to their operator forms.
        </p>

        <p>
          To specify a rule as a string, separate the left and right pattern by ‘-&gt;’ When
          specifying a rule as an object, the following keys are meaningful:
        </p>
        <ul>
          <li>l - the left pattern</li>
          <li>r - the right pattern</li>
          <li>s - in lieu of l and r, the string form that is broken at -&gt; to give them</li>
          <li>repeat - whether to repeat this rule until the expression stabilizes</li>
          <li>
            assuming - gives a context object, as in the ‘context’ option to simplify. Every
            property in the context object must match the current context in order, or else the rule
            will not be applied.
          </li>
          <li>
            imposeContext - gives a context object, as in the ‘context’ option to simplify. Any
            settings specified will override the incoming context for all matches of this rule.
          </li>
        </ul>

        <p>For more details on the theory, see:</p>

        <ul>
          <li>
            <a href="https://stackoverflow.com/questions/7540227/strategies-for-simplifying-math-expressions">
              Strategies for simplifying math expressions (Stackoverflow)
            </a>
          </li>
          <li>
            <a href="https://en.wikipedia.org/wiki/Symbolic_computation#Simplification">
              Symbolic computation - Simplification (Wikipedia)
            </a>
          </li>
        </ul>

        <p>
          An optional <code className="language-plaintext highlighter-rouge">options</code> argument
          can be passed as last argument of{" "}
          <code className="language-plaintext highlighter-rouge">simplify</code>. Currently
          available options (defaults in parentheses):
        </p>
        <ul>
          <li>
            <code className="language-plaintext highlighter-rouge">consoleDebug</code> (false):
            whether to write the expression being simplified and any changes to it, along with the
            rule responsible, to console
          </li>
          <li>
            <code className="language-plaintext highlighter-rouge">context</code>{" "}
            (simplify.defaultContext): an object giving properties of each operator, which determine
            what simplifications are allowed. The currently meaningful properties are commutative,
            associative, total (whether the operation is defined for all arguments), and trivial
            (whether the operation applied to a single argument leaves that argument unchanged). The
            default context is very permissive and allows almost all simplifications. Only
            properties differing from the default need to be specified; the default context is used
            as a fallback. Additional contexts{" "}
            <code className="language-plaintext highlighter-rouge">simplify.realContext</code> and
            <code className="language-plaintext highlighter-rouge">
              simplify.positiveContext
            </code>{" "}
            are supplied to cause simplify to perform just simplifications guaranteed to preserve
            all values of the expression assuming all variables and subexpressions are real numbers
            or positive real numbers, respectively. (Note that these are in some cases more
            restrictive than the default context; for example, the default context will allow{" "}
            <code className="language-plaintext highlighter-rouge">x/x</code> to simplify to 1,
            whereas
            <code className="language-plaintext highlighter-rouge">simplify.realContext</code> will
            not, as <code className="language-plaintext highlighter-rouge">0/0</code> is not equal
            to 1.)
          </li>
          <li>
            <code className="language-plaintext highlighter-rouge">exactFractions</code> (true):
            whether to try to convert all constants to exact rational numbers.
          </li>
          <li>
            <code className="language-plaintext highlighter-rouge">fractionsLimit</code> (10000):
            when <code className="language-plaintext highlighter-rouge">exactFractions</code> is
            true, constants will be expressed as fractions only when both numerator and denominator
            are smaller than{" "}
            <code className="language-plaintext highlighter-rouge">fractionsLimit</code>.
          </li>
        </ul>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            simplify(expr) <br />
            simplify(expr, rules) <br />
            simplify(expr, rules) <br />
            simplify(expr, rules, scope) <br />
            simplify(expr, rules, scope, options) <br />
            simplify(expr, scope) <br />
            simplify(expr, scope, options) <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">expr</code>
              </td>
              <td>Node | string</td>
              <td>The expression to be simplified</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">rules</code>
              </td>
              <td>SimplifyRule[]</td>
              <td>Optional list with custom rules</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">scope</code>
              </td>
              <td>Object</td>
              <td>Optional scope with variables</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">options</code>
              </td>
              <td>SimplifyOptions</td>
              <td>Optional configuration settings</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Node</td>
              <td>
                Returns the simplified form of{" "}
                <code className="language-plaintext highlighter-rouge">expr</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            simplify(&apos;2 * 1 * x ^ (2 - 1)&apos;) <br />
            {"//"} Node &quot;2 * x&quot; <br />
            simplify(&apos;2 * 3 * x&apos;, {"{x: 4}"}) <br />
            {"//"} Node &quot;24&quot; <br />
            const f = parse(&apos;2 * 1 * x ^ (2 - 1)&apos;) <br />
            simplify(f) <br />
            {"//"} Node &quot;2 * x&quot; <br />
            simplify(&apos;0.4 * x&apos;, {}, {"{exactFractions: true}"}) <br />
            {"//"} Node &quot;x * 2 / 5&quot; <br />
            simplify(&apos;0.4 * x&apos;, {}, {"{exactFractions: false}"}) <br />
            {"//"} Node &quot;0.4 * x&quot; <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "simplifyConstant",
    text: (
      <div>
        <h1>Function simplifyConstant</h1>

        <p>
          simplifyConstant() takes a mathjs expression (either a Node representing a parse tree or a
          string which it parses to produce a node), and replaces any subexpression of it consisting
          entirely of constants with the computed value of that subexpression.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            simplifyConstant(expr) <br />
            simplifyConstant(expr, options)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">node</code>
              </td>
              <td>Node | string</td>
              <td>The expression to be simplified</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">options</code>
              </td>
              <td>Object</td>
              <td>Simplification options, as per simplify()</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Node</td>
              <td>Returns expression with constant subexpressions evaluated</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            simplifyConstant(&apos;x + 4*3/6&apos;) <br />
            {"//"} Node &quot;x + 2&quot; <br />
            simplifyConstant(&apos;z cos(0)&apos;) <br />
            {"//"} Node &quot;z 1&quot; <br />
            simplifyConstant(&apos;(5.2 + 1.08)t&apos;, <br />
            {"    "}
            {"{exactFractions: false}"}) <br />
            {"//"} Node &quot;6.28 t&quot; <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "simplifyCore",
    text: (
      <div>
        <h1>Function simplifyCore</h1>

        <p>
          simplifyCore() performs single pass simplification suitable for applications requiring
          ultimate performance. To roughly summarize, it handles cases along the lines of
          simplifyConstant() but where knowledge of a single argument is sufficient to determine the
          value. In contrast, simplify() extends simplifyCore() with additional passes to provide
          deeper simplification (such as gathering like terms).
        </p>

        <p>Specifically, simplifyCore:</p>

        <ul>
          <li>Converts all function calls with operator equivalents to their operator forms.</li>
          <li>
            Removes operators or function calls that are guaranteed to have no effect (such as unary
            ‘+’).
          </li>
          <li>Removes double unary ‘-‘, ‘~’, and ‘not’</li>
          <li>
            Eliminates addition/subtraction of 0 and multiplication/division/powers by 1 or 0.
          </li>
          <li>Converts addition of a negation into subtraction.</li>
          <li>Eliminates logical operations with constant true or false leading arguments.</li>
          <li>
            Puts constants on the left of a product, if multiplication is considered commutative by
            the options (which is the default)
          </li>
        </ul>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>simplifyCore(expr) simplifyCore(expr, options)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">node</code>
              </td>
              <td>Node | string</td>
              <td>The expression to be simplified</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">options</code>
              </td>
              <td>Object</td>
              <td>Simplification options, as per simplify()</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Node</td>
              <td>Returns expression with basic simplifications applied</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const f = parse(&apos;2 * 1 * x ^ (1 - 0)&apos;) <br />
            simplifyCore(f) <br />
            {"//"} Node &quot;2 * x&quot; <br />
            simplify(&apos;2 * 1 * x ^ (1 - 0)&apos;, [simplifyCore]) <br />
            {"//"} Node &quot;2 * x&quot; <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "slope",
    text: (
      <div>
        <h1>Function slope</h1>

        <p>
          Calculates the slope of a linear regression line that best fits a set of (x, y) data
          points. The <b>slope</b> function is useful for identifying the rate of change or the
          strength of a linear relationship between two numeric datasets.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"slope(xValues, yValues)"} <br />
            {"// xValues: Array of x-axis numerical values."} <br />
            {"// yValues: Array of y-axis numerical values."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">xValues</code>
              </td>
              <td>Array&lt;Number&gt;</td>
              <td>An array of numerical values representing the independent variable (X).</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">yValues</code>
              </td>
              <td>Array&lt;Number&gt;</td>
              <td>An array of numerical values representing the dependent variable (Y).</td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"slope([1, 2, 3, 4, 5], [3, 5, 7, 9, 11])"} <br />
            {"//"} Returns 2 — indicating that Y increases by 2 for each unit increase in X. <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "slu",
    text: (
      <div>
        <h1>Function slu</h1>

        <p>
          Calculate the Sparse Matrix LU decomposition with full pivoting. Sparse Matrix{" "}
          <code className="language-plaintext highlighter-rouge">A</code> is decomposed in two
          matrices (<code className="language-plaintext highlighter-rouge">L</code>,{" "}
          <code className="language-plaintext highlighter-rouge">U</code>) and two permutation
          vectors (<code className="language-plaintext highlighter-rouge">pinv</code>,{" "}
          <code className="language-plaintext highlighter-rouge">q</code>) where
        </p>

        <p>
          <code className="language-plaintext highlighter-rouge">P * A * Q = L * U</code>
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>slu(A, order, threshold)</code>
        </pre>

        <h3>Parameters</h3>

        <table className={"highlight-table"}>
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">A</code>
              </td>
              <td>SparseMatrix</td>
              <td>A two dimensional sparse matrix for which to get the LU decomposition.</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">order</code>
              </td>
              <td>Number</td>
              <td>
                The Symbolic Ordering and Analysis order: 0 - Natural ordering, no permutation
                vector q is returned 1 - Matrix must be square, symbolic ordering and analisis is
                performed on M = A + A’ 2 - Symbolic ordering and analisis is performed on M = A’ *
                A. Dense columns from A’ are dropped, A recreated from A’. This is appropriatefor LU
                factorization of unsymmetric matrices. 3 - Symbolic ordering and analisis is
                performed on M = A’ * A. This is best used for LU factorization is matrix M has no
                dense rows. A dense row is a row with more than 10*sqr(columns) entries.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">threshold</code>
              </td>
              <td>Number</td>
              <td>Partial pivoting threshold (1 for partial pivoting)</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className={"highlight-table"}>
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Object</td>
              <td>
                The lower triangular matrix, the upper triangular matrix and the permutation
                vectors.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const A = sparse([[4,3], [6, 3]]) <br />
            slu(A, 1, 0.001) <br />
            {"//"} returns: <br />
            {"// {"} <br />
            {"//    "} L: [[1, 0], [1.5, 1]] <br />
            {"//    "} U: [[4, 3], [0, -1.5]] <br />
            {"//    "} p: [0, 1] <br />
            {"//    "} q: [0, 1] <br />
            {"// }"} <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "sylvester",
    text: (
      <div>
        <h1>Function sylvester</h1>

        <p>
          Solves the real-valued Sylvester equation AX+XB=C for X, where A, B and C are matrices of
          appropriate dimensions, being A and B squared. Notice that other equivalent definitions
          for the Sylvester equation exist and this function assumes the one presented in the
          original publication of the the Bartels- Stewart algorithm, which is implemented by this
          function. https://en.wikipedia.org/wiki/Sylvester_equation
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            <span className="nx">math</span>
            <span className="p">.</span>
            <span className="nx">sylvester</span>
            <span className="p">(</span>
            <span className="nx">A</span>
            <span className="p">,</span> <span className="nx">B</span>
            <span className="p">,</span> <span className="nx">C</span>
            <span className="p">)</span>
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">A</code>
              </td>
              <td>Matrix | Array</td>
              <td>Matrix A</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">B</code>
              </td>
              <td>Matrix | Array</td>
              <td>Matrix B</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">C</code>
              </td>
              <td>Matrix | Array</td>
              <td>Matrix C</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Matrix | Array</td>
              <td>Matrix X, solving the Sylvester equation</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const A = [[-1, -2], [1, 1]] <br />
            const B = [[2, -1], [1, -2]] <br />
            const C = [[-3, 2], [3, 0]] <br />
            sylvester(A, B, C) <br />
            {"//"} returns DenseMatrix [[-0.25, 0.25], [1.5, -1.25]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "symbolicEqual",
    text: (
      <div>
        <h1>Function symbolicEqual</h1>

        <p>
          Attempts to determine if two expressions are symbolically equal, i.e. one is the result of
          valid algebraic manipulations on the other. Currently, this simply checks if the
          difference of the two expressions simplifies down to 0. So there are two important
          caveats:
        </p>
        <ol>
          <li>
            whether two expressions are symbolically equal depends on the manipulations allowed.
            Therefore, this function takes an optional third argument, which are the options that
            control the behavior as documented for the{" "}
            <code className="language-plaintext highlighter-rouge">simplify()</code> function.
          </li>
          <li>
            it is in general intractable to find the minimal simplification of an arbitrarily
            complicated expression. So while a{" "}
            <code className="language-plaintext highlighter-rouge">true</code> value of{" "}
            <code className="language-plaintext highlighter-rouge">symbolicEqual</code> ensures that
            the two expressions can be manipulated to match each other, a{" "}
            <code className="language-plaintext highlighter-rouge">false</code> value does not
            absolutely rule this out.
          </li>
        </ol>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            symbolicEqual(expr1, expr2) <br />
            symbolicEqual(expr1, expr2, options)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">expr1</code>
              </td>
              <td>Node | string</td>
              <td>The first expression to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">expr2</code>
              </td>
              <td>Node | string</td>
              <td>The second expression to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">options</code>
              </td>
              <td>Object</td>
              <td>Optional option object, passed to simplify</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean</td>
              <td>Returns true if a valid manipulation making the expressions equal is found.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            symbolicEqual(&apos;x*y&apos;, &apos;y*x&apos;) <br />
            {"//"} Returns true <br /> <br />
            symbolicEqual(&apos;x*y&apos;, &apos;y*x&apos;,{" "}
            {"{context: {multiply: {commutative: false}}}"}) <br />
            {"//"} Returns false <br /> <br />
            symbolicEqual(&apos;x/y&apos;, &apos;(y*x^(-1))^(-1)&apos;) <br />
            {"//"} Returns true <br /> <br />
            symbolicEqual(&apos;abs(x)&apos;,&apos;x&apos;) <br />
            {"//"} Returns false <br /> <br />
            symbolicEqual(&apos;abs(x)&apos;,&apos;x&apos;, simplify.positiveContext) <br />
            {"//"} Returns true
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "usolve",
    text: (
      <div>
        <h1>Function usolve</h1>

        <p>
          Finds one solution of a linear equation system by backward substitution. Matrix must be an
          upper triangular matrix. Throws an error if there’s no solution.
        </p>

        <p>
          <code className="language-plaintext highlighter-rouge">U * x = b</code>
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>usolve(U, b)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">U</code>
              </td>
              <td>Matrix, Array</td>
              <td>A N x N matrix or array (U)</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">b</code>
              </td>
              <td>Matrix, Array</td>
              <td>A column vector with the b values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>DenseMatrix | Array</td>
              <td>A column vector with the linear system solution (x)</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const a = [[-2, 3], [2, 1]] <br />
            const b = [11, 9] <br />
            const x = usolve(a, b) <br />
            {"//"} [[8], [9]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "usolveAll",
    text: (
      <div>
        <h1>Function usolveAll</h1>

        <p>
          Finds all solutions of a linear equation system by backward substitution. Matrix must be
          an upper triangular matrix.
        </p>

        <p>
          <code className="language-plaintext highlighter-rouge">U * x = b</code>
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>usolveAll(U, b)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">U</code>
              </td>
              <td>Matrix, Array</td>
              <td>A N x N matrix or array (U)</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">b</code>
              </td>
              <td>Matrix, Array</td>
              <td>A column vector with the b values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>DenseMatrix[] | Array[]</td>
              <td>
                An array of affine-independent column vectors (x) that solve the linear system
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const a = [[-2, 3], [2, 1]] <br />
            const b = [11, 9] <br />
            const x = usolveAll(a, b) <br />
            {"//"} [ [[8], [9]] ]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "abs",
    text: (
      <div>
        <h1>Function abs</h1>

        <p>
          Calculate the absolute value of a number. For matrices, the function is evaluated element
          wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>abs(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix | Unit</td>
              <td>A number or matrix for which to get the absolute value</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix | Unit</td>
              <td>
                Absolute value of <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            abs(3.5) <br />
            {"//"} returns number 3.5 <br /> <br />
            abs(-4.2) <br />
            {"//"} returns number 4.2 <br /> <br />
            abs([3, -5, -1, 0, 2]) <br />
            {"//"} returns Array [3, 5, 1, 0, 2]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "add",
    text: (
      <div>
        <h1>Function add</h1>

        <p>
          Add two or more values,{" "}
          <code className="language-plaintext highlighter-rouge">x + y</code>. For matrices, the
          function is evaluated element wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            add(x, y) <br />
            add(x, y, z, ...)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>First value to add</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>Second value to add</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>
                Sum of <code className="language-plaintext highlighter-rouge">x</code> and{" "}
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            add(2, 3) <br />
            {"//"} returns number 5 <br /> <br />
            add(2, 3, 4) <br />
            {"//"} returns number 9 <br /> <br />
            const a = complex(2, 3) <br />
            const b = complex(-4, 1) <br />
            add(a, b) <br />
            {"//"} returns Complex -2 + 4i <br /> <br />
            add([1, 2, 3], 4) <br />
            {"//"} returns Array [5, 6, 7] <br /> <br />
            const c = unit(&apos;5 cm&apos;) <br />
            const d = unit(&apos;2.1 mm&apos;) <br />
            add(c, d) <br />
            {"//"} returns Unit 52.1 mm <br /> <br />
            add(&quot;2.3&quot;, &quot;4&quot;) <br />
            {"//"} returns number 6.3
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "cbrt",
    text: (
      <div>
        <h1>Function cbrt</h1>

        <p>Calculate the cubic root of a value.</p>

        <p>
          To avoid confusion with the matrix cube root, this function does not apply to matrices.
          For a matrix, to take the cube root elementwise, see the examples.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            cbrt(x) <br />
            cbrt(x, allRoots)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit</td>
              <td>Value for which to calculate the cubic root.</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">allRoots</code>
              </td>
              <td>boolean</td>
              <td>
                Optional, false by default. Only applicable when{" "}
                <code className="language-plaintext highlighter-rouge">x</code> is a number or
                complex number. If true, all complex roots are returned, if false (default) the
                principal root is returned.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex | Unit</td>
              <td>
                Returns the cubic root of{" "}
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            cbrt(27) <br />
            {"//"} returns 3 <br /> <br />
            cube(3) <br />
            {"//"} returns 27 <br /> <br />
            cbrt(-64) <br />
            {"//"} returns -4 <br /> <br />
            cbrt(unit(&apos;27 m^3&apos;)) <br />
            {"//"} returns Unit 3 m <br /> <br />
            map([27, 64, 125], x {"=>"} cbrt(x)) <br />
            {"//"} returns [3, 4, 5] <br /> <br />
            const x = complex(&apos;8i&apos;) <br />
            cbrt(x) <br />
            {"//"} returns Complex 1.7320508075689 + i <br /> <br />
            cbrt(x, true) <br />
            {"//"} returns Matrix {"["} <br />
            {"//"}
            {"    "}1.7320508075689 + i <br />
            {"//"}
            {"    "}-1.7320508075689 + i <br />
            {"//"}
            {"    "}-2i <br />
            {"//"} {"]"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "ceil",
    text: (
      <div>
        <h1>Function ceil</h1>

        <p>
          Round a value towards plus infinity If{" "}
          <code className="language-plaintext highlighter-rouge">x</code> is complex, both real and
          imaginary part are rounded towards plus infinity. For matrices, the function is evaluated
          element wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            ceil(x) <br />
            ceil(x, n)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix</td>
              <td>Number to be rounded</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>number | BigNumber | Array</td>
              <td>Number of decimals Default value: 0.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix</td>
              <td>Rounded value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            ceil(3.2) <br />
            {"//"} returns number 4 <br /> <br />
            ceil(3.8) <br />
            {"//"} returns number 4 <br /> <br />
            ceil(-4.2) <br />
            {"//"} returns number -4 <br /> <br />
            ceil(-4.7) <br />
            {"//"} returns number -4 <br /> <br />
            ceil(3.212, 2) <br />
            {"//"} returns number 3.22 <br /> <br />
            ceil(3.288, 2) <br />
            {"//"} returns number 3.29 <br /> <br />
            ceil(-4.212, 2) <br />
            {"//"} returns number -4.21 <br /> <br />
            ceil(-4.782, 2) <br />
            {"//"} returns number -4.78 <br /> <br />
            const c = complex(3.24, -2.71) <br />
            ceil(c) <br />
            {"//"} returns Complex 4 - 2i <br /> <br />
            ceil(c, 1) <br />
            {"//"} returns Complex 3.3 - 2.7i <br /> <br />
            ceil([3.2, 3.8, -4.7]) <br />
            {"//"} returns Array [4, 4, -4] <br /> <br />
            ceil([3.21, 3.82, -4.71], 1) <br />
            {"//"} returns Array [3.3, 3.9, -4.7]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "cube",
    text: (
      <div>
        <h1>Function cube</h1>

        <p>
          Compute the cube of a value,{" "}
          <code className="language-plaintext highlighter-rouge">x * x * x</code>. To avoid
          confusion with <code className="language-plaintext highlighter-rouge">pow(M,3)</code>,
          this function does not apply to matrices. If you wish to cube every entry of a matrix, see
          the examples.
        </p>

        <h2>Syntax</h2>

        <div className="language-js highlighter-rouge">
          <div className="highlight">
            <pre className="highlight">
              <code>cube(x)</code>
            </pre>
          </div>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit</td>
              <td>Number for which to calculate the cube</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Unit</td>
              <td>Cube of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            cube(2) <br />
            {"//"} returns number 8 <br /> <br />
            pow(2, 3) <br />
            {"//"} returns number 8 <br /> <br />
            cube(4) <br />
            {"//"} returns number 64 <br /> <br />
            4 * 4 * 4 <br />
            {"//"} returns number 64 <br /> <br />
            map([1, 2, 3, 4], cube) <br />
            {"//"} returns Array [1, 8, 27, 64]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "divide",
    text: (
      <div>
        <h1>Function divide</h1>

        <p>
          Divide two values, <code className="language-plaintext highlighter-rouge">x / y</code>. To
          divide matrices, <code className="language-plaintext highlighter-rouge">x</code> is
          multiplied with the inverse of{" "}
          <code className="language-plaintext highlighter-rouge">y</code>:{" "}
          <code className="language-plaintext highlighter-rouge">x * inv(y)</code>.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>divide(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>Numerator</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix</td>
              <td>Denominator</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>
                Quotient, <code className="language-plaintext highlighter-rouge">x / y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            divide(2, 3) <br />
            {"//"} returns number 0.6666666666666666 <br /> <br />
            const a = complex(5, 14) <br />
            const b = complex(4, 1) <br />
            divide(a, b) <br />
            {"//"} returns Complex 2 + 3i <br /> <br />
            const c = [[7, -6], [13, -4]] <br />
            const d = [[1, 2], [4, 3]] <br />
            divide(c, d) <br />
            {"//"} returns Array [[-9, 4], [-11, 6]] <br /> <br />
            const e = unit(&apos;18 km&apos;) <br />
            divide(e, 4.5) <br />
            {"//"} returns Unit 4 km
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "dotDivide",
    text: (
      <div>
        <h1>Function dotDivide</h1>

        <p>
          Divide two matrices element wise. The function accepts both matrices and scalar values.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>dotDivide(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>Numerator</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>Denominator</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>
                Quotient, <code className="language-plaintext highlighter-rouge">x ./ y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            dotDivide(2, 4) <br />
            {"//"} returns 0.5 <br /> <br />
            a = [[9, 5], [6, 1]] <br />
            b = [[3, 2], [5, 2]] <br />
            dotDivide(a, b) <br />
            {"//"} returns [[3, 2.5], [1.2, 0.5]] <br /> <br />
            divide(a, b) <br />
            {"//"} returns [[1.75, 0.75], [-1.75, 2.25]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "dotMultiply",
    text: (
      <div>
        <h1>Function dotMultiply</h1>

        <p>
          Multiply two matrices element wise. The function accepts both matrices and scalar values.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>dotMultiply(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>Left hand value</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>Right hand value</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>
                Multiplication of <code className="language-plaintext highlighter-rouge">x</code>{" "}
                and <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            dotMultiply(2, 4) <br />
            {"//"} returns 8 <br /> <br />
            a = [[9, 5], [6, 1]] <br />
            b = [[3, 2], [5, 2]] <br />
            dotMultiply(a, b) <br />
            {"//"} returns [[27, 10], [30, 2]] <br /> <br />
            multiply(a, b) <br />
            {"//"} returns [[52, 28], [23, 14]] <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "dotPow",
    text: (
      <div>
        <h1>Function dotPow</h1>

        <p>Calculates the power of x to y element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>dotPow(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit | Array | Matrix</td>
              <td>The base</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Complex | Unit | Array | Matrix</td>
              <td>The exponent</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex | Unit | Array | Matrix</td>
              <td>
                The value of <code className="language-plaintext highlighter-rouge">x</code> to the
                power <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            dotPow(2, 3) <br />
            {"//"} returns number 8 <br /> <br />
            const a = [[1, 2], [4, 3]] <br />
            dotPow(a, 2) <br />
            {"//"} returns Array [[1, 4], [16, 9]] <br /> <br />
            pow(a, 2) <br />
            {"//"} returns Array [[9, 8], [16, 17]] <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "exp",
    text: (
      <div>
        <h1>Function exp</h1>

        <p>
          Calculate the exponential of a value. For matrices, if you want the matrix exponential of
          square matrix, use the <code className="language-plaintext highlighter-rouge">expm</code>{" "}
          function; if you want to take the exponential of each element, see the examples.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>exp(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>A number to exponentiate</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>
                Exponential of <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            exp(2) <br />
            {"//"} returns number 7.3890560989306495 <br /> <br />
            pow(e, 2) <br />
            {"//"} returns number 7.3890560989306495 <br /> <br />
            log(exp(2)) <br />
            {"//"} returns number 2 <br /> <br />
            map([1, 2, 3], exp) <br />
            {"//"} returns Array {"["} <br />
            {"//"}
            {"    "}2.718281828459045, <br />
            {"//"}
            {"    "}7.3890560989306495, <br />
            {"//"}
            {"    "}20.085536923187668 <br />
            {"//"} {"]"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "expm1",
    text: (
      <div>
        <h1>Function expm1</h1>

        <p>
          Calculate the value of subtracting 1 from the exponential value. This function is more
          accurate than <code className="language-plaintext highlighter-rouge">exp(x)-1</code> when{" "}
          <code className="language-plaintext highlighter-rouge">x</code> is near 0 To avoid
          ambiguity with the matrix exponential{" "}
          <code className="language-plaintext highlighter-rouge">expm</code>, this function does not
          operate on matrices; if you wish to apply it elementwise, see the examples.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>expm1(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>The number to exponentiate</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>
                Exponential of <code className="language-plaintext highlighter-rouge">x</code>,
                minus one
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            expm1(2) <br />
            {"//"} returns number 6.38905609893065 <br /> <br />
            pow(e, 2) - 1 <br />
            {"//"} returns number 6.3890560989306495 <br /> <br />
            expm1(1e-8) <br />
            {"//"} returns number 1.0000000050000001e-8 <br /> <br />
            exp(1e-8) - 1 <br />
            {"//"} returns number 9.9999999392253e-9 <br /> <br />
            log(expm1(2) + 1) <br />
            {"//"} returns number 2 <br /> <br />
            map([1, 2, 3], expm1) <br />
            {"//"} returns Array {"["} <br />
            {"//"}
            {"    "}1.718281828459045, <br />
            {"//"}
            {"    "}6.3890560989306495, <br />
            {"//"}
            {"    "}19.085536923187668 <br />
            {"//"} {"]"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "fix",
    text: (
      <div>
        <h1>Function fix</h1>

        <p>Round a value towards zero. For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>fix(x) fix(x,n)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix</td>
              <td>Number to be rounded</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>number | BigNumber | Array</td>
              <td>Number of decimals Default value: 0.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix</td>
              <td>Rounded value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            fix(3.2) <br />
            {"//"} returns number 3 <br /> <br />
            fix(3.8) <br />
            {"//"} returns number 3 <br /> <br />
            fix(-4.2) <br />
            {"//"} returns number -4 <br /> <br />
            fix(-4.7) <br />
            {"//"} returns number -4 <br /> <br />
            fix(3.12, 1) <br />
            {"//"} returns number 3.1 <br /> <br />
            fix(3.18, 1) <br />
            {"//"} returns number 3.1 <br /> <br />
            fix(-4.12, 1) <br />
            {"//"} returns number -4.1 <br /> <br />
            fix(-4.17, 1) <br />
            {"//"} returns number -4.1 <br /> <br />
            const c = complex(3.22, -2.78) <br />
            fix(c) <br />
            {"//"} returns Complex 3 - 2i <br /> <br />
            fix(c, 1) <br />
            {"//"} returns Complex 3.2 -2.7i <br /> <br />
            fix([3.2, 3.8, -4.7]) <br />
            {"//"} returns Array [3, 3, -4] <br /> <br />
            fix([3.2, 3.8, -4.7], 1) <br />
            {"//"} returns Array [3.2, 3.8, -4.7] <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "floor",
    text: (
      <div>
        <h1>Function floor</h1>

        <p>
          Round a value towards minus infinity. For matrices, the function is evaluated element
          wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>floor(x) floor(x, n)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix</td>
              <td>Number to be rounded</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>number | BigNumber | Array</td>
              <td>Number of decimals Default value: 0.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix</td>
              <td>Rounded value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            floor(3.2) <br />
            {"//"} returns number 3 <br /> <br />
            floor(3.8) <br />
            {"//"} returns number 3 <br /> <br />
            floor(-4.2) <br />
            {"//"} returns number -5 <br /> <br />
            floor(-4.7) <br />
            {"//"} returns number -5 <br /> <br />
            floor(3.212, 2) <br />
            {"//"} returns number 3.21 <br /> <br />
            floor(3.288, 2) <br />
            {"//"} returns number 3.28 <br /> <br />
            floor(-4.212, 2) <br />
            {"//"} returns number -4.22 <br /> <br />
            floor(-4.782, 2) <br />
            {"//"} returns number -4.79 <br /> <br />
            const c = complex(3.24, -2.71) <br />
            floor(c) <br />
            {"//"} returns Complex 3 - 3i <br /> <br />
            floor(c, 1) <br />
            {"//"} returns Complex 3.2 -2.8i <br /> <br />
            floor([3.2, 3.8, -4.7]) <br />
            {"//"} returns Array [3, 3, -5] <br /> <br />
            floor([3.21, 3.82, -4.71], 1) <br />
            {"//"} returns Array [3.2, 3.8, -4.8] <br /> <br />
            floor(tau, [2, 3]) <br />
            {"//"} returns Array [6.28, 6.283] <br /> <br />
            {"//"} Note that floor(array, array) currently not implemented.
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "gcd",
    text: (
      <div>
        <h1>Function gcd</h1>

        <p>Calculate the greatest common divisor for two or more values or arrays.</p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            gcd(a, b) <br />
            gcd(a, b, c, ...)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… number | BigNumber | Fraction | Array | Matrix</td>
              <td>Two or more integer numbers</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Array | Matrix</td>
              <td>The greatest common divisor</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            gcd(8, 12) <br />
            {"//"} returns 4 <br /> <br />
            gcd(-4, 6) <br />
            {"//"} returns 2 <br /> <br />
            gcd(25, 15, -10) <br />
            {"//"} returns 5 <br /> <br />
            gcd([8, -4], [12, 6]) <br />
            {"//"} returns [4, 2] <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "hypot",
    text: (
      <div>
        <h1>Function hypot</h1>

        <p>Calculate the hypotenusa of a list with values. The hypotenusa is defined as:</p>

        <pre className="highlight">
          <code>hypot(a, b, c, ...) = sqrt(a^2 + b^2 + c^2 + ...)</code>
        </pre>

        <p>For matrix input, the hypotenusa is calculated for all values in the matrix.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            hypot(a, b, ...) <br />
            hypot([a, b, c, ...])
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… number | BigNumber | Array | Matrix</td>
              <td>
                A list with numeric values or an Array or Matrix. Matrix and Array input is
                flattened and returns a single number for the whole matrix.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber</td>
              <td>Returns the hypothenusa of the input values.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            hypot(3, 4) <br />
            {"//"} 5 <br /> <br />
            hypot(3, 4, 5) <br />
            {"//"} 7.0710678118654755 <br /> <br />
            hypot([3, 4, 5]) <br />
            {"//"} 7.0710678118654755 <br /> <br />
            hypot(-2) <br />
            {"//"} 2
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "invmod",
    text: (
      <div>
        <h1>Function invmod</h1>

        <p>
          Calculate the (modular) multiplicative inverse of a modulo b. Solution to the equation{" "}
          <code className="language-plaintext highlighter-rouge">ax ≣ 1 (mod b)</code>
          See https://en.wikipedia.org/wiki/Modular_multiplicative_inverse.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>invmod(a, b)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a</code>
              </td>
              <td>number | BigNumber</td>
              <td>An integer number</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">b</code>
              </td>
              <td>number | BigNumber</td>
              <td>An integer number</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber</td>
              <td>
                Returns an integer number where{" "}
                <code className="language-plaintext highlighter-rouge">
                  invmod(a,b)*a ≣ 1 (mod b)
                </code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            invmod(8, 12) <br />
            {"//"} returns NaN <br /> <br />
            invmod(7, 13) <br />
            {"//"} returns 2 <br /> <br />
            invmod(15151, 15122) <br />
            {"//"} returns 10429 <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "lcm",
    text: (
      <div>
        <h1>Function lcm</h1>

        <p>Calculate the least common multiple for two or more values or arrays.</p>

        <p>lcm is defined as:</p>

        <pre className="highlight">
          <code>lcm(a, b) = abs(a * b) / gcd(a, b)</code>
        </pre>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            lcm(a, b) <br />
            lcm(a, b, c, ...)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… number | BigNumber | Array | Matrix</td>
              <td>Two or more integer numbers</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Array | Matrix</td>
              <td>The least common multiple</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            lcm(4, 6) <br />
            {"//"} returns 12 <br /> <br />
            lcm(6, 21) <br />
            {"//"} returns 42 <br /> <br />
            lcm(6, 21, 5) <br />
            {"//"} returns 210 <br /> <br />
            lcm([4, 6], [6, 21]) <br />
            {"//"} returns [12, 42]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "log",
    text: (
      <div>
        <h1>Function log</h1>

        <p>Calculate the logarithm of a value.</p>

        <p>
          To avoid confusion with the matrix logarithm, this function does not apply to matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>log(x) log(x, base)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Value for which to calculate the logarithm.</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">base</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>
                Optional base for the logarithm. If not provided, the natural logarithm of{" "}
                <code className="language-plaintext highlighter-rouge">x</code> is calculated.
                Default value: e.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>
                Returns the logarithm of{" "}
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            log(3.5) <br />
            {"//"} returns 1.252762968495368 <br /> <br />
            exp(log(2.4)) <br />
            {"//"} returns 2.4 <br /> <br />
            pow(10, 4) <br />
            {"//"} returns 10000 <br /> <br />
            log(10000, 10) <br />
            {"//"} returns 4 <br /> <br />
            log(10000) / log(10) // returns 4 <br /> <br />
            log(1024, 2) <br />
            {"//"} returns 10 <br /> <br />
            pow(2, 10) <br />
            {"//"} returns 1024 <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "log10",
    text: (
      <div>
        <h1>Function log10</h1>

        <p>
          Calculate the 10-base logarithm of a value. This is the same as calculating{" "}
          <code className="language-plaintext highlighter-rouge">log(x, 10)</code>.
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>log10(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>Value for which to calculate the logarithm.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>
                Returns the 10-base logarithm of{" "}
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            log10(0.00001) <br />
            {"//"} returns -5 <br /> <br />
            log10(10000) <br />
            {"//"} returns 4 <br /> <br />
            log(10000) / log(10) <br />
            {"//"} returns 4 <br /> <br />
            pow(10, 4) <br />
            {"//"} returns 10000 <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "log1p",
    text: (
      <div>
        <h1>Function log1p</h1>

        <p>
          Calculate the logarithm of a{" "}
          <code className="language-plaintext highlighter-rouge">value+1</code>.
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            log1p(x) <br />
            log1p(x, base)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>
                Value for which to calculate the logarithm of{" "}
                <code className="language-plaintext highlighter-rouge">x+1</code>.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">base</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>
                Optional base for the logarithm. If not provided, the natural logarithm of{" "}
                <code className="language-plaintext highlighter-rouge">x+1</code> is calculated.
                Default value: e.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>
                Returns the logarithm of{" "}
                <code className="language-plaintext highlighter-rouge">x+1</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            log1p(2.5) <br />
            {"//"} returns 1.252762968495368 <br /> <br />
            exp(log1p(1.4)) <br />
            {"//"} returns 2.4 <br /> <br />
            pow(10, 4) <br />
            {"//"}returns 10000 <br /> <br />
            log1p(9999, 10) <br />
            {"//"} returns 4 <br /> <br />
            log1p(9999) / log(10) <br />
            {"//"} returns 4
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "log2",
    text: (
      <div>
        <h1>Function log2</h1>

        <p>
          Calculate the 2-base of a value. This is the same as calculating{" "}
          <code className="language-plaintext highlighter-rouge">log(x, 2)</code>.
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>log2(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>Value for which to calculate the logarithm.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>
                Returns the 2-base logarithm of{" "}
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            log2(0.03125) <br />
            {"//"} returns -5 <br /> <br />
            log2(16) <br />
            {"//"} returns 4 <br /> <br />
            log2(16) / log2(2) <br />
            {"//"} returns 4 <br /> <br />
            pow(2, 4) <br />
            {"//"} returns 16
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "mod",
    text: (
      <div>
        <h1>Function mod</h1>

        <p>Calculates the modulus, the remainder of an integer division.</p>

        <p>For matrices, the function is evaluated element wise.</p>

        <p>The modulus is defined as:</p>

        <pre className="highlight">
          <code>x - y * floor(x / y)</code>
        </pre>

        <p>See https://en.wikipedia.org/wiki/Modulo_operation.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>mod(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Array | Matrix</td>
              <td>Dividend</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | Array | Matrix</td>
              <td>Divisor</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Array | Matrix</td>
              <td>
                Returns the remainder of{" "}
                <code className="language-plaintext highlighter-rouge">x</code> divided by{" "}
                <code className="language-plaintext highlighter-rouge">y</code>.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            mod(8, 3) <br />
            {"//"} returns 2 <br /> <br />
            mod(11, 2) <br />
            {"//"} returns 1 <br /> <br />
            function isOdd(x) {"{"} <br />
            {"    "}
            {"return mod(x, 2) != 0"} <br />
            {"}"} <br /> <br />
            isOdd(2) <br />
            {"//"} returns false <br /> <br />
            isOdd(3) <br />
            {"//"} returns true
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "multiply",
    text: (
      <div>
        <h1>Function multiply</h1>

        <p>
          Multiply two or more values,{" "}
          <code className="language-plaintext highlighter-rouge">x * y</code>. For matrices, the
          matrix product is calculated.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>multiply(x, y) multiply(x, y, z, ...)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>First value to multiply</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>Second value to multiply</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>
                Multiplication of <code className="language-plaintext highlighter-rouge">x</code>{" "}
                and <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            multiply(4, 5.2) <br />
            {"//"} returns number 20.8 <br /> <br />
            multiply(2, 3, 4) <br />
            {"//"} returns number 24 <br /> <br />
            const a = complex(2, 3) <br />
            const b = complex(4, 1) <br />
            multiply(a, b) <br />
            {"//"} returns Complex 5 + 14i <br /> <br />
            const c = [[1, 2], [4, 3]] <br />
            const d = [[1, 2, 3], [3, -4, 7]] <br />
            multiply(c, d) <br />
            {"//"} returns Array [[7, -6, 17], [13, -4, 33]] <br /> <br />
            const e = unit(&apos;2.1 km&apos;) <br />
            multiply(3, e) <br />
            {"//"} returns Unit 6.3 km <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "norm",
    text: (
      <div>
        <h1>Function norm</h1>

        <p>Calculate the norm of a number, vector or matrix.</p>

        <p>The second parameter p is optional. If not provided, it defaults to 2.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>norm(x) norm(x, p)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>Value for which to calculate the norm</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">p</code>
              </td>
              <td>number | BigNumber | string</td>
              <td>
                Vector space. Supported numbers include Infinity and -Infinity. Supported strings
                are: ‘inf’, ‘-inf’, and ‘fro’ (The Frobenius norm) Default value: 2.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber</td>
              <td>the p-norm</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            abs(-3.5) <br />
            {"//"} returns 3.5 <br /> <br />
            norm(-3.5) <br />
            {"//"} returns 3.5 <br /> <br />
            norm(complex(3, -4)) <br />
            {"//"} returns 5 <br /> <br />
            norm([1, 2, -3], Infinity) <br />
            {"//"} returns 3 <br /> <br />
            norm([1, 2, -3], -Infinity) <br />
            {"//"} returns 1 <br /> <br />
            norm([3, 4], 2) <br />
            {"//"} returns 5 <br /> <br />
            norm([[1, 2], [3, 4]], 1) <br />
            {"//"} returns 6 <br /> <br />
            norm([[1, 2], [3, 4]], &apos;inf&apos;) <br />
            {"//"} returns 7 <br /> <br />
            norm([[1, 2], [3, 4]], &apos;fro&apos;) <br />
            {"//"} returns 5.477225575051661
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "nthRoot",
    text: (
      <div>
        <h1>Function nthRoot</h1>

        <p>
          Calculate the nth root of a value. The principal nth root of a positive real number A, is
          the positive real solution of the equation
        </p>

        <pre className="highlight">
          <code>x^root = A</code>
        </pre>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>nthRoot(a) nthRoot(a, root)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a</code>
              </td>
              <td>number | BigNumber | Array | Matrix | Complex</td>
              <td>Value for which to calculate the nth root</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">root</code>
              </td>
              <td>number | BigNumber</td>
              <td>The root. Default value: 2.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Complex | Array | Matrix</td>
              <td>
                Returns the nth root of{" "}
                <code className="language-plaintext highlighter-rouge">a</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            nthRoot(9, 2) <br />
            {"//"} returns 3 (since 3^2 == 9) <br /> <br />
            sqrt(9) <br />
            {"//"} returns 3 (since 3^2 == 9) <br /> <br />
            nthRoot(64, 3) <br />
            {"//"} returns 4 (since 4^3 == 64)
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "nthRoots",
    text: (
      <div>
        <h1>Function nthRoots</h1>

        <p>
          Calculate the nth roots of a value. An nth root of a positive real number A, is a positive
          real solution of the equation “x^root = A”. This function returns an array of complex
          values.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>nthRoots(x) nthRoots(x, root)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex</td>
              <td>Number to be rounded</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">root</code>
              </td>
              <td>number</td>
              <td>Optional root, default value is 2 Default value: 2.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex</td>
              <td>Returns the nth roots</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            nthRoots(1) <br />
            {"//"}returns {"["} <br />
            {"//"}
            {"    "}
            {"{re: 1, im: 0}"}, <br />
            {"//"}
            {"    "}
            {"{re: -1, im: 0}"} <br />
            {"//"} {"]"} <br /> <br />
            nthRoots(1, 3) <br />
            {"//"} returns [ <br />
            {"//"}
            {""}
            {"    "}
            {"{ re: 1, im: 0 },"} <br />
            {"//"}
            {""}
            {"    "}
            {"{ re: -0.4999999999999998, im: 0.8660254037844387 },"} <br />
            {"//"}
            {""}
            {"    "}
            {"{ re: -0.5000000000000004, im: -0.8660254037844385 }"} <br />
            {"//"} ]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "pow",
    text: (
      <div>
        <h1>Function pow</h1>

        <p>
          Calculates the power of x to y,{" "}
          <code className="language-plaintext highlighter-rouge">x ^ y</code>.
        </p>

        <p>
          Matrix exponentiation is supported for square matrices{" "}
          <code className="language-plaintext highlighter-rouge">x</code> and integers{" "}
          <code className="language-plaintext highlighter-rouge">y</code>: when{" "}
          <code className="language-plaintext highlighter-rouge">y</code> is nonnegative,{" "}
          <code className="language-plaintext highlighter-rouge">x</code> may be any square matrix;
          and when <code className="language-plaintext highlighter-rouge">y</code> is negative,{" "}
          <code className="language-plaintext highlighter-rouge">x</code> must be invertible, and
          then this function returns inv(x)^(-y).
        </p>

        <p>
          For cubic roots of negative numbers, the function returns the principal root by default.
          In order to let the function return the real root, js can be configured with{" "}
          <code className="language-plaintext highlighter-rouge">
            config({"{predictable: true}"})
          </code>
          . To retrieve all cubic roots of a value, use{" "}
          <code className="language-plaintext highlighter-rouge">cbrt(x, true)</code>.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>pow(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit | Array | Matrix</td>
              <td>The base</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>The exponent</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>
                The value of <code className="language-plaintext highlighter-rouge">x</code> to the
                power <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            pow(2, 3) <br />
            {"//"} returns number 8 <br /> <br />
            const a = complex(2, 3) <br />
            pow(a, 2) <br />
            {"//"} returns Complex -5 + 12i <br /> <br />
            const b = [[1, 2], [4, 3]] <br />
            pow(b, 2) <br />
            {"//"} returns Array [[9, 8], [16, 17]] <br /> <br />
            const c = [[1, 2], [4, 3]] <br />
            pow(c, -1) <br />
            {"//"} returns Array [[-0.6, 0.4], [0.8, -0.2]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "round",
    text: (
      <div>
        <h1>Function round</h1>

        <p>
          Round a value towards the nearest rounded value. For matrices, the function is evaluated
          element wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>round(x) round(x, n)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix</td>
              <td>Number to be rounded</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>number | BigNumber | Array</td>
              <td>Number of decimals Default value: 0.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix</td>
              <td>Rounded value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            round(3.22) <br />
            {"//"} returns number 3 <br /> <br />
            round(3.82) <br />
            {"//"} returns number 4 <br /> <br />
            round(-4.2) <br />
            {"//"} returns number -4 <br /> <br />
            round(-4.7) <br />
            {"//"} returns number -5 <br /> <br />
            round(3.22, 1) <br />
            {"//"} returns number 3.2 <br /> <br />
            round(3.88, 1) <br />
            {"//"} returns number 3.9 <br /> <br />
            round(-4.21, 1) <br />
            {"//"} returns number -4.2 <br /> <br />
            round(-4.71, 1) <br />
            {"//"} returns number -4.7 <br /> <br />
            round(pi, 3) <br />
            {"//"} returns number 3.142 <br /> <br />
            round(123.45678, 2) <br />
            {"//"} returns number 123.46 <br /> <br />
            const c = complex(3.2, -2.7) <br />
            round(c) <br />
            {"//"} returns Complex 3 - 3i <br /> <br />
            round([3.2, 3.8, -4.7]) <br />
            {"//"} returns Array [3, 4, -5]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "sign",
    text: (
      <div>
        <h1>Function sign</h1>

        <p>Compute the sign of a value. The sign of a value x is:</p>

        <ul>
          <li>1 when x &gt; 0</li>
          <li>-1 when x &lt; 0</li>
          <li>0 when x == 0</li>
        </ul>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>sign(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix | Unit</td>
              <td>The number for which to determine the sign</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Array | Matrix | Unit</td>
              <td>
                The sign of <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            sign(3.5) <br />
            {"//"} returns 1 <br /> <br />
            sign(-4.2) <br />
            {"//"} returns -1 <br /> <br />
            sign(0) <br />
            {"//"} returns 0 <br /> <br />
            sign([3, 5, -2, 0, 2]) <br />
            {"//"} returns [1, 1, -1, 0, 1]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "sqrt",
    text: (
      <div>
        <h1>Function sqrt</h1>

        <p>Calculate the square root of a value.</p>

        <p>
          For matrices, if you want the matrix square root of a square matrix, use the{" "}
          <code className="language-plaintext highlighter-rouge">sqrtm</code> function. If you wish
          to apply <code className="language-plaintext highlighter-rouge">sqrt</code> elementwise to
          a matrix M, use <code className="language-plaintext highlighter-rouge">map(M, sqrt)</code>
          .
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>sqrt(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit</td>
              <td>Value for which to calculate the square root.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex | Unit</td>
              <td>
                Returns the square root of{" "}
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            sqrt(25) <br />
            {"//"} returns 5 <br /> <br />
            square(5) <br />
            {"//"} returns 25 <br /> <br />
            sqrt(-4) <br />
            {"//"} returns Complex 2i
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "square",
    text: (
      <div>
        <h1>Function square</h1>

        <p>
          Compute the square of a value,{" "}
          <code className="language-plaintext highlighter-rouge">x * x</code>. To avoid confusion
          with multiplying a square matrix by itself, this function does not apply to matrices. If
          you wish to square every element of a matrix, see the examples.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>square(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit</td>
              <td>Number for which to calculate the square</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Unit</td>
              <td>Squared value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            square(2) <br />
            {"//"} returns number 4 <br /> <br />
            square(3) <br />
            {"//"} returns number 9 <br /> <br />
            pow(3, 2) <br />
            {"//"} returns number 9 <br /> <br />
            multiply(3, 3) <br />
            {"//"} returns number 9 <br /> <br />
            map([1, 2, 3, 4], square) <br />
            {"//"} returns Array [1, 4, 9, 16]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "subtract",
    text: (
      <div>
        <h1>Function subtract</h1>

        <p>
          Subtract two values, <code className="language-plaintext highlighter-rouge">x - y</code>.
          For matrices, the function is evaluated element wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>subtract(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>Initial value</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>
                Value to subtract from{" "}
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>
                Subtraction of <code className="language-plaintext highlighter-rouge">x</code> and{" "}
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            subtract(5.3, 2) <br />
            {"//"} returns number 3.3 <br /> <br />
            const a = complex(2, 3) <br />
            const b = complex(4, 1) <br />
            subtract(a, b) <br />
            {"//"} returns Complex -2 + 2i <br /> <br />
            subtract([5, 7, 4], 4) <br />
            {"//"} returns Array [1, 3, 0] <br /> <br />
            const c = unit(&apos;2.1 km&apos;) <br />
            const d = unit(&apos;500m&apos;) <br />
            subtract(c, d) <br />
            {"//"} returns Unit 1.6 km
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "unaryMinus",
    text: (
      <div>
        <h1>Function unaryMinus</h1>

        <p>Inverse the sign of a value, apply a unary minus operation.</p>

        <p>
          For matrices, the function is evaluated element wise. Boolean values and strings will be
          converted to a number. For complex numbers, both real and complex value are inverted.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>unaryMinus(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>Number to be inverted.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>Returns the value with inverted sign.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            unaryMinus(3.5) <br />
            {"//"} returns -3.5 <br /> <br />
            unaryMinus(-4.2) <br />
            {"//"} returns 4.2
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "unaryPlus",
    text: (
      <div>
        <h1>Function unaryPlus</h1>

        <p>
          Unary plus operation. Boolean values and strings will be converted to a number, numeric
          values will be returned as is.
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>unaryPlus(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | string | Complex | Unit | Array | Matrix</td>
              <td>Input value</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>
                Returns the input value when numeric, converts to a number when input is
                non-numeric.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            unaryPlus(3.5) <br />
            {"//"} returns 3.5 <br /> <br />
            unaryPlus(1) <br />
            {"//"} returns 1
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "xgcd",
    text: (
      <div>
        <h1>Function xgcd</h1>

        <p>
          Calculate the extended greatest common divisor for two values. See
          https://en.wikipedia.org/wiki/Extended_Euclidean_algorithm.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>xgcd(a, b)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a</code>
              </td>
              <td>number | BigNumber</td>
              <td>An integer number</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">b</code>
              </td>
              <td>number | BigNumber</td>
              <td>An integer number</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array</td>
              <td>
                Returns an array containing 3 integers{" "}
                <code className="language-plaintext highlighter-rouge">[div, m, n]</code> where{" "}
                <code className="language-plaintext highlighter-rouge">div = gcd(a, b)</code> and{" "}
                <code className="language-plaintext highlighter-rouge">a*m + b*n = div</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            xgcd(8, 12) <br />
            {"//"} returns [4, -1, 1] <br /> <br />
            gcd(8, 12) <br />
            {"//"} returns 4 <br /> <br />
            xgcd(36163, 21199) <br />
            {"//"} returns [1247, -7, 12]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "bitAnd",
    text: (
      <div>
        <h1>Function bitAnd</h1>

        <p>
          Bitwise AND two values,{" "}
          <code className="language-plaintext highlighter-rouge">x &amp; y</code>. For matrices, the
          function is evaluated element wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>bitAnd(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Array | Matrix</td>
              <td>First value to and</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Array | Matrix</td>
              <td>Second value to and</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Array | Matrix</td>
              <td>
                AND of <code className="language-plaintext highlighter-rouge">x</code> and{" "}
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            bitAnd(53, 131) <br />
            {"//"} returns number 1 <br /> <br />
            bitAnd([1, 12, 31], 42) <br />
            {"//"} returns Array [0, 8, 10]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "bitNot",
    text: (
      <div>
        <code>
          <h1>Function bitNot</h1>

          <p>
            Bitwise NOT value, <code className="language-plaintext highlighter-rouge">~x</code>. For
            matrices, the function is evaluated element wise. For units, the function is evaluated
            on the best prefix base.
          </p>

          <h2>Syntax</h2>

          <pre className="highlight">
            <code>bitNot(x)</code>
          </pre>

          <h3>Parameters</h3>

          <table className="highlight-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code className="language-plaintext highlighter-rouge">x</code>
                </td>
                <td>number | BigNumber | Array | Matrix</td>
                <td>Value to not</td>
              </tr>
            </tbody>
          </table>

          <h3>Returns</h3>

          <table className="highlight-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>number | BigNumber | Array | Matrix</td>
                <td>
                  NOT of <code className="language-plaintext highlighter-rouge">x</code>
                </td>
              </tr>
            </tbody>
          </table>

          <h3>Throws</h3>

          <p>Type | Description —- | ———–</p>

          <h2>Examples</h2>

          <pre className="highlight">
            <code>
              bitNot(1) <br />
              {"//"} returns number -2 <br /> <br />
              bitNot([2, -3, 4]) <br />
              {"//"} returns Array [-3, 2, -5]
            </code>
          </pre>
        </code>
      </div>
    ),
  },
  {
    math: "bitOr",
    text: (
      <div>
        <h1>Function bitOr</h1>

        <p>
          Bitwise OR two values, <code className="language-plaintext highlighter-rouge">x | y</code>
          . For matrices, the function is evaluated element wise. For units, the function is
          evaluated on the lowest print base.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>bitOr(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Array | Matrix</td>
              <td>First value to or</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Array | Matrix</td>
              <td>Second value to or</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Array | Matrix</td>
              <td>
                OR of <code className="language-plaintext highlighter-rouge">x</code> and{" "}
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            bitOr(1, 2) <br />
            {"//"} returns number 3 <br /> <br />
            bitOr([1, 2, 3], 4) <br />
            {"//"} returns Array [5, 6, 7]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "bitXor",
    text: (
      <div>
        <h1>Function bitXor</h1>

        <p>
          Bitwise XOR two values,{" "}
          <code className="language-plaintext highlighter-rouge">x ^ y</code>. For matrices, the
          function is evaluated element wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>bitXor(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Array | Matrix</td>
              <td>First value to xor</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Array | Matrix</td>
              <td>Second value to xor</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Array | Matrix</td>
              <td>
                XOR of <code className="language-plaintext highlighter-rouge">x</code> and{" "}
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            bitXor(1, 2) <br />
            {"//"} returns number 3 <br /> <br />
            bitXor([2, 3, 4], 4) <br />
            {"//"} returns Array [6, 7, 0]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "leftShift",
    text: (
      <div>
        <h1>Function leftShift</h1>

        <p>
          Bitwise left logical shift of a value x by y number of bits,{" "}
          <code className="language-plaintext highlighter-rouge">x &lt;&lt; y</code>. For matrices,
          the function is evaluated element wise. For units, the function is evaluated on the best
          prefix base.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>leftShift(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Array | Matrix</td>
              <td>Value to be shifted</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber</td>
              <td>Amount of shifts</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Array | Matrix</td>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code> shifted left{" "}
                <code className="language-plaintext highlighter-rouge">y</code> times
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            leftShift(1, 2) <br />
            {"//"} returns number 4 <br /> <br />
            leftShift([1, 2, 4], 4) <br />
            {"//"} returns Array [16, 32, 64]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "rightArithShift",
    text: (
      <div>
        <h1>Function rightArithShift</h1>

        <p>
          Bitwise right arithmetic shift of a value x by y number of bits,{" "}
          <code className="language-plaintext highlighter-rouge">x &gt;&gt; y</code>. For matrices,
          the function is evaluated element wise. For units, the function is evaluated on the best
          prefix base.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>rightArithShift(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Array | Matrix</td>
              <td>Value to be shifted</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber</td>
              <td>Amount of shifts</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Array | Matrix</td>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code> zero-filled shifted
                right <code className="language-plaintext highlighter-rouge">y</code> times
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            rightArithShift(4, 2) <br />
            {"//"} returns number 1 <br /> <br />
            rightArithShift([16, -32, 64], 4) <br />
            {"//"} returns Array [1, -2, 4]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "rightLogShift",
    text: (
      <div>
        <h1>Function rightLogShift</h1>

        <p>
          Bitwise right logical shift of value x by y number of bits,{" "}
          <code className="language-plaintext highlighter-rouge">x &gt;&gt;&gt; y</code>. For
          matrices, the function is evaluated element wise. For units, the function is evaluated on
          the best prefix base.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>rightLogShift(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | Array | Matrix</td>
              <td>Value to be shifted</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number</td>
              <td>Amount of shifts</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Array | Matrix</td>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code> zero-filled shifted
                right <code className="language-plaintext highlighter-rouge">y</code> times
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            rightLogShift(4, 2) <br />
            {"//"} returns number 1 <br /> <br />
            rightLogShift([16, 32, 64], 4) <br />
            {"//"} returns Array [1, 2, 4]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "bellNumbers",
    text: (
      <div>
        <h1>Function bellNumbers</h1>

        <p>
          The Bell Numbers count the number of partitions of a set. A partition is a pairwise
          disjoint subset of S whose union is S. bellNumbers only takes integer arguments. The
          following condition must be enforced: n &gt;= 0
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>bellNumbers(n)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>Number | BigNumber</td>
              <td>Total number of objects in the set</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Number | BigNumber</td>
              <td>B(n)</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          bellNumbers(3) <br />
          {"//"} returns 5 <br /> <br />
          bellNumbers(8) <br />
          {"//"} returns 4140
        </pre>
      </div>
    ),
  },
  {
    math: "catalan",
    text: (
      <div>
        <h1>Function catalan</h1>

        <p>
          The Catalan Numbers enumerate combinatorial structures of many different types. catalan
          only takes integer arguments. The following condition must be enforced: n &gt;= 0
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>catalan(n)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>Number | BigNumber</td>
              <td>nth Catalan number</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Number | BigNumber</td>
              <td>Cn(n)</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            catalan(3) <br />
            {"//"} returns 5 <br /> <br />
            catalan(8) <br />
            {"//"} returns 1430
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "composition",
    text: (
      <div>
        <h1>Function composition</h1>

        <p>The composition counts of n into k parts.</p>

        <p>
          composition only takes integer arguments. The following condition must be enforced: k
          &lt;= n.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>composition(n, k)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>Number | BigNumber</td>
              <td>Total number of objects in the set</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">k</code>
              </td>
              <td>Number | BigNumber</td>
              <td>Number of objects in the subset</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Number | BigNumber</td>
              <td>Returns the composition counts of n into k parts.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            composition(5, 3) <br />
            {"//"} returns 6
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "stirlingS2",
    text: (
      <div>
        <h1>Function stirlingS2</h1>

        <p>
          The Stirling numbers of the second kind, counts the number of ways to partition a set of n
          labelled objects into k nonempty unlabelled subsets. stirlingS2 only takes integer
          arguments. The following condition must be enforced: k &lt;= n.
        </p>

        <p>If n = k or k = 1 &lt;= n, then s(n,k) = 1 If k = 0 &lt; n, then s(n,k) = 0</p>

        <p>Note that if either n or k is supplied as a BigNumber, the result will be as well.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>stirlingS2(n, k)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>Number | BigNumber</td>
              <td>Total number of objects in the set</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">k</code>
              </td>
              <td>Number | BigNumber</td>
              <td>Number of objects in the subset</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Number | BigNumber</td>
              <td>S(n,k)</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            stirlingS2(5, 3) <br />
            {"//"}returns 25
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "arg",
    text: (
      <div>
        <h1>Function arg</h1>

        <p>
          Compute the argument of a complex value. For a complex number{" "}
          <code className="language-plaintext highlighter-rouge">a + bi</code>, the argument is
          computed as <code className="language-plaintext highlighter-rouge">atan2(b, a)</code>.
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <div className="language-js highlighter-rouge">
          <div className="highlight">
            <pre className="highlight">
              <code>arg(x)</code>
            </pre>
          </div>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>A complex number or array with complex numbers</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Array | Matrix</td>
              <td>The argument of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const a = complex(2, 2) <br />
            arg(a) / pi <br />
            {"//"} returns number 0.25 <br /> <br />
            const b = complex(&apos;2 + 3i&apos;) <br />
            arg(b) <br />
            {"//"} returns number 0.982793723247329 <br /> <br />
            atan2(3, 2) <br />
            {"//"} returns number 0.98279372324732
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "conj",
    text: (
      <div>
        <h1>Function conj</h1>

        <p>
          Compute the complex conjugate of a complex value. If{" "}
          <code className="language-plaintext highlighter-rouge">x = a+bi</code>, the complex
          conjugate of <code className="language-plaintext highlighter-rouge">x</code> is{" "}
          <code className="language-plaintext highlighter-rouge">a - bi</code>.
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>conj(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>A complex number or array with complex numbers</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>The complex conjugate of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            conj(complex(&apos;2 + 3i&apos;)) <br />
            {"//"} returns Complex 2 - 3i <br /> <br />
            conj(complex(&apos;2 - 3i&apos;)) <br />
            {"//"} returns Complex 2 + 3i <br /> <br />
            conj(complex(&apos;-5.2i&apos;)) <br />
            {"//"} returns Complex 5.2i
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "im",
    text: (
      <div>
        <h1>Function im</h1>

        <p>
          Get the imaginary part of a complex number. For a complex number{" "}
          <code className="language-plaintext highlighter-rouge">a + bi</code>, the function returns{" "}
          <code className="language-plaintext highlighter-rouge">b</code>.
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>im(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>A complex number or array with complex numbers</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Array | Matrix</td>
              <td>The imaginary part of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const a = complex(2, 3) <br />
            re(a) <br />
            {"//"} returns number 2 <br /> <br />
            im(a) <br />
            {"//"} returns number 3 <br /> <br />
            re(complex(&apos;-5.2i&apos;)) <br />
            {"//"} returns number -5.2 <br /> <br />
            re(complex(2.4)) <br />
            {"//"} returns number 0
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "re",
    text: (
      <div>
        <h1>Function re</h1>

        <p>
          Get the real part of a complex number. For a complex number{" "}
          <code className="language-plaintext highlighter-rouge">a + bi</code>, the function returns{" "}
          <code className="language-plaintext highlighter-rouge">a</code>.
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>re(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Array | Matrix</td>
              <td>A complex number or array with complex numbers</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Array | Matrix</td>
              <td>The real part of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const a = complex(2, 3) <br />
            re(a) <br />
            {"//"} returns number 2 <br /> <br />
            im(a) <br />
            {"//"} returns number 3 <br /> <br />
            re(complex(&apos;-5.2i&apos;)) <br />
            {"//"} returns number 0 <br /> <br />
            re(complex(2.4)) <br />
            {"//"} returns number 2.4
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "now",
    text: (
      <div>
        <h1>Function now</h1>

        <p>
          Returns the serial number of the current date and time. The NOW function is useful when
          you need to display the current date and time or calculate a value based on the current
          date and time
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"now()"} <br />
            {"The now function syntax has no arguments."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>
        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="3">
                <i>This function takes no parameters.</i>
              </td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            now() <br />
            {"//"} Returns the current date and time. <br />
            {"//"} 2024-12-10 17:30:00 <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "today",
    text: (
      <div>
        <h1>Function today</h1>

        <p>
          Returns the current date with the time set to the start of the day (00:00:00). The today
          function is useful for applications that require a reference to the current day without
          time specificity.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"today()"} <br />
            {"The today function syntax has no arguments."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>
        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="3">
                <i>This function takes no parameters.</i>
              </td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            today() <br />
            {"//"} Returns the current date with time as 00:00:00. <br />
            {"//"} For example, if today is &quot;2024-12-16&quot;, the function will return <br />
            {"//"} &quot;2024-12-16 00:00:00&quot;. <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "weekday",
    text: (
      <div>
        <h1>Function weekday</h1>

        <p>
          Returns the day of the week as a number for a given date. The <b>weekday</b> function maps
          days of the week to numbers, where Sunday is 0, Monday is 1, Tuesday is 2, and so on
          through Saturday (6).
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"weekday(date)"} <br />
            {"// date: The date for which to determine the day of the week."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>String | Date</td>
              <td>
                The date for which to calculate the day of the week. Can be a JavaScript{" "}
                <code>Date</code> object or a valid date string.
              </td>
            </tr>
          </tbody>
        </table>

        <h2>Return Value</h2>

        <p>
          Returns a number between <b>0</b> and <b>6</b>, where:
          <ul>
            <li>
              <b>0</b>: Sunday
            </li>
            <li>
              <b>1</b>: Monday
            </li>
            <li>
              <b>2</b>: Tuesday
            </li>
            <li>
              <b>3</b>: Wednesday
            </li>
            <li>
              <b>4</b>: Thursday
            </li>
            <li>
              <b>5</b>: Friday
            </li>
            <li>
              <b>6</b>: Saturday
            </li>
          </ul>
        </p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"weekday('2024-12-15')"} <br />
            {"//"} Returns 0 (Sunday). <br /> <br />
            {"weekday('2024-12-16')"} <br />
            {"//"} Returns 1 (Monday). <br /> <br />
            {"weekday('2024-12-17')"} <br />
            {"//"} Returns 2 (Tuesday). <br /> <br />
            {"weekday('2024-12-21')"} <br />
            {"//"} Returns 6 (Saturday). <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "minute",
    text: (
      <div>
        <h1>Function minute</h1>

        <p>
          Retrieves the minutes component from a given date. The <b>minute</b> function extracts and
          returns the minutes portion of the specified date.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"minute(date)"} <br />
            {"// date: The date from which to retrieve the minutes component."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>String | Date</td>
              <td>The date object or date string from which to extract the minutes component.</td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"minute('2024-12-16 14:30:00')"} <br />
            {"//"} Returns 30 (minutes portion of the given date). <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "hour",
    text: (
      <div>
        <h1>Function hour</h1>

        <p>
          Retrieves the hour component from a given date. The <b>hour</b> function extracts and
          returns the hour portion of the specified date.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"hour(date)"} <br />
            {"// date: The date from which to retrieve the hour component."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>String | Date</td>
              <td>The date object or date string from which to extract the hour component.</td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"hour('2024-12-16 14:30:00')"} <br />
            {"//"} Returns 14 (hour portion of the given date). <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "day",
    text: (
      <div>
        <h1>Function day</h1>

        <p>
          Retrieves the day of the month from a given date. The <b>day</b> function extracts and
          returns the day of the month from the specified date.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"day(date)"} <br />
            {"// date: The date from which to retrieve the day of the month."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>String | Date</td>
              <td>The date object or date string from which to extract the day of the month.</td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"day('2024-12-16 14:30:00')"} <br />
            {"//"} Returns 16 (day of the month portion of the given date). <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "month",
    text: (
      <div>
        <h1>Function month</h1>

        <p>
          Retrieves the month from a given date. The <b>month</b> function extracts and returns the
          month from the specified date.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"month(date)"} <br />
            {"// date: The date from which to retrieve the month."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>String | Date</td>
              <td>The date object or date string from which to extract the month.</td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"month('2024-12-16 14:30:00')"} <br />
            {"//"} Returns 12 (month portion of the given date). <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "addMonths",
    text: (
      <div>
        <h1>Function addMonths</h1>

        <p>
          Adds a specified number of months to a given date. The <b>addMonths</b> function is useful
          for performing time calculations, such as determining a future or past time relative to a
          given date and time.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"addMonths(date, months)"} <br />
            {"// date: The base date from which months will be added or subtracted."} <br />
            {"// months: The number of months to add (positive) or subtract (negative)."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>String | Date</td>
              <td>
                The base date to which months will be added or subtracted. It can be a JavaScript
                <code>Date</code> object or a valid date string.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">months</code>
              </td>
              <td>Number</td>
              <td>
                The number of months to add (positive) or subtract (negative) to the given date.
              </td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"addMonths('2025-01-01 12:00:00', 4)"} <br />
            {"//"} Returns a date and time 4 months after the given date. <br />
            {"//"} 2024-05-01 12:00:00 <br /> <br />
            {"addMonths('2025-01-01 12:00:00', -2)"} <br />
            {"//"} Returns a date and time 2 months before the given date. <br />
            {"//"} 2024-11-01 12:00:00 <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "addDays",
    text: (
      <div>
        <h1>Function addDays</h1>

        <p>
          Adds a specified number of days to a given date. The <b>addDays</b> function is useful for
          performing date calculations, such as determining a future or past date relative to a
          given date.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"addDays(date, days)"} <br />
            {"// date: The base date from which days will be added or subtracted."} <br />
            {"// days: The number of days to add (positive) or subtract (negative)."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>String | Date</td>
              <td>
                The base date to which days will be added or subtracted. It can be a JavaScript
                <code>Date</code> object or a valid date string.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">days</code>
              </td>
              <td>Number</td>
              <td>
                The number of days to add (positive) or subtract (negative) to the given date.
              </td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"addDays('2024-12-16 00:00:00', 5)"} <br />
            {"//"} Returns a date 5 days after the given date. <br />
            {"//"} 2024-12-21 00:00:00 <br /> <br />
            {"addDays('2024-12-16 00:00:00', -10)"} <br />
            {"//"} Returns a date 10 days before the given date. <br />
            {"//"} 2024-12-06 00:00:00 <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "addHours",
    text: (
      <div>
        <h1>Function addHours</h1>

        <p>
          Adds a specified number of hours to a given date. The <b>addHours</b> function is useful
          for performing time calculations, such as determining a future or past time relative to a
          given date and time.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"addHours(date, hours)"} <br />
            {"// date: The base date from which hours will be added or subtracted."} <br />
            {"// hours: The number of hours to add (positive) or subtract (negative)."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>String | Date</td>
              <td>
                The base date to which hours will be added or subtracted. It can be a JavaScript
                <code>Date</code> object or a valid date string.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">hours</code>
              </td>
              <td>Number</td>
              <td>
                The number of hours to add (positive) or subtract (negative) to the given date.
              </td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"addHours('2024-12-16 12:00:00', 5)"} <br />
            {"//"} Returns a date and time 5 hours after the given date. <br />
            {"//"} 2024-12-16 17:00:00 <br /> <br />
            {"addHours('2024-12-16 12:00:00', -3)"} <br />
            {"//"} Returns a date and time 3 hours before the given date. <br />
            {"//"} 2024-12-16 09:00:00 <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "addMinutes",
    text: (
      <div>
        <h1>Function addMinutes</h1>

        <p>
          Adds a specified number of minutes to a given date. The <b>addMinutes</b> function is
          useful for performing time calculations, such as determining a future or past time
          relative to a given date and time.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"addMinutes(date, minutes)"} <br />
            {"// date: The base date from which minutes will be added or subtracted."} <br />
            {"// minutes: The number of minutes to add (positive) or subtract (negative)."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>String | Date</td>
              <td>
                The base date to which hours will be added or subtracted. It can be a JavaScript
                <code>Date</code> object or a valid date string.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">minutes</code>
              </td>
              <td>Number</td>
              <td>
                The number of minutes to add (positive) or subtract (negative) to the given date.
              </td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"addMinutes('2024-12-16 12:00:00', 30)"} <br />
            {"//"} Returns a date and time 30 minutes after the given date. <br />
            {"//"} 2024-12-16 12:30:00 <br /> <br />
            {"addMinutes('2024-12-16 12:00:00', -20)"} <br />
            {"//"} Returns a date and time 20 minutes before the given date. <br />
            {"//"} 2024-12-16 11:40:00 <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "roundToNextHour",
    text: (
      <div>
        <h1>Function roundToNextHour</h1>

        <p>
          Rounds the given date and time to the next full hour. This function is useful when you
          need to standardize time inputs or calculate deadlines that align with the next hour.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"roundToNextHour(date)"} <br />
            {"The roundToNextHour function syntax requires one argument."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>
        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>Date</td>
              <td>The date and time to be rounded to the next full hour.</td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"roundToNextHour('2024-12-16 10:20:00')"} <br />
            {"//"} Returns &quot;2024-12-16 11:00:00&quot;. <br /> <br />
            {"roundToNextHour('2024-12-16 23:59:59')"} <br />
            {"//"} Returns &quot;2024-12-17 00:00:00&quot;. <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "roundToPreviousHour",
    text: (
      <div>
        <h1>Function roundToNextHour</h1>

        <p>
          Rounds the given date and time to the previous full hour. This function is useful when you
          need to standardize time inputs.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"roundToPreviousHour(date)"} <br />
            {"The roundToPreviousHour function syntax requires one argument."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>
        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>Date</td>
              <td>The date and time to be rounded to the previous full hour.</td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"roundToPreviousHour('2024-12-16 10:20:00')"} <br />
            {"//"} Returns &quot;2024-12-16 10:00:00&quot;. <br /> <br />
            {"roundToPreviousHour('2024-12-16 23:59:59')"} <br />
            {"//"} Returns &quot;2024-12-16 23:00:00&quot;. <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "dateDif",
    text: (
      <div>
        <h1>Function dateDif</h1>

        <p>
          Calculates the difference between two dates in a specified unit. The <b>dateDif</b>{" "}
          function is versatile, allowing you to determine the interval in years, months, days,
          hours, or minutes between two specific dates. If no unit is specified, the default is
          days.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"dateDif(date1, date2, unit)"} <br />
            {"// date1: The first date."} <br />
            {"// date2: The second date."} <br />
            {"// unit: The unit of measurement for the difference (optional)."} <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date1</code>
              </td>
              <td>String | Date</td>
              <td>
                The starting date for the calculation. Can be a JavaScript <code>Date</code> object
                or a valid date string.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date2</code>
              </td>
              <td>String | Date</td>
              <td>
                The ending date for the calculation. Can be a JavaScript <code>Date</code> object or
                a valid date string.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">unit</code>
              </td>
              <td>String (optional)</td>
              <td>
                The unit of measurement for the difference. Options include:
                <ul>
                  <li>
                    <b>Y</b>: Years (complete years).
                  </li>
                  <li>
                    <b>M</b>: Months (complete months).
                  </li>
                  <li>
                    <b>D</b>: Days (complete days, default).
                  </li>
                  <li>
                    <b>H</b>: Hours (complete hours).
                  </li>
                  <li>
                    <b>m</b>: Minutes (complete minutes).
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"dateDif('2024-12-16', '2025-12-16', 'Y')"} <br />
            {"//"} Returns 1 (difference in complete years). <br /> <br />
            {"dateDif('2024-12-16', '2025-02-16', 'M')"} <br />
            {"//"} Returns 2 (difference in complete months). <br /> <br />
            {"dateDif('2024-12-16', '2024-12-20', 'D')"} <br />
            {"//"} Returns 4 (difference in days). <br /> <br />
            {"dateDif('2024-12-16T00:00:00', '2024-12-16 12:00:00', 'H')"} <br />
            {"//"} Returns 12 (difference in complete hours). <br /> <br />
            {"dateDif('2024-12-16T00:00:00', '2024-12-16 00:30:00', 'm')"} <br />
            {"//"} Returns 30 (difference in complete minutes). <br /> <br />
            {"dateDif('2024-12-16', '2024-12-20')"} <br />
            {"//"} Returns 4 (default unit, difference in days). <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "EOMonth",
    text: (
      <div>
        <h1>Function EOMonth</h1>

        <p>
          Returns the last day of the month for a given date, optionally adjusted by a specified
          number of months. The <b>EOMonth</b> function is useful for determining the end date of a
          specific month.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"EOMonth(date, months)"} <br />
            {"// date: The base date to calculate the end of the month."} <br />
            {
              "// months: The number of months to add (positive) or subtract (negative) from the base date before determining the end of the month."
            }{" "}
            <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">date</code>
              </td>
              <td>String | Date</td>
              <td>
                The base date from which to calculate the end of the month. Can be a JavaScript{" "}
                <code>Date</code> object or a valid date string.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">months</code>
              </td>
              <td>Number</td>
              <td>
                The number of months to add (positive) or subtract (negative) before determining the
                end of the month. Defaults to <code>0</code>.
              </td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"EOMonth('2024-12-16', 0)"} <br />
            {"//"} Returns the last day of the same month (2024-12-31). <br /> <br />
            {"EOMonth('2024-12-16', 2)"} <br />
            {"//"} Returns the last day two months later (2025-02-28). <br /> <br />
            {"EOMonth('2024-12-16', -1)"} <br />
            {"//"} Returns the last day of the previous month (2024-11-30). <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },

  {
    math: "distance",
    text: (
      <div>
        <h1>Function distance</h1>

        <p>
          Calculates: The eucledian distance between two points in N-dimensional spaces. Distance
          between point and a line in 2 and 3 dimensional spaces. Pairwise distance between a set of
          2D or 3D points NOTE: When substituting coefficients of a line(a, b and c), use ax + by +
          c = 0 instead of ax + by = c For parametric equation of a 3D line, x0, y0, z0, a, b, c are
          from: (x−x0, y−y0, z−z0) = t(a, b, c)
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            {"distance([x1,y1], [x2,y2])"} <br />
            {"distance({(pointOneX, pointOneY)}, {(pointTwoX, pointTwoY)})"} <br />
            {"distance([x1,y1,z1], [x2,y2,z2])"} <br />
            {"distance({(pointOneX, pointOneY, pointOneZ)},"} <br />
            {"    "}
            {"{(pointTwoX, pointTwoY, pointTwoZ)})"} <br />
            {"distance([x1,y1,z1,a1], [x2,y2,z2,a2])"} <br />
            {"distance([[x1,y1], [x2,y2], [x3,y3]])"} <br />
            {"distance([[x1,y1,z1], [x2,y2,z2], [x3,y3,z3]])"} <br />
            {"distance([pointX,pointY], [a,b,c])"} <br />
            {"distance([pointX,pointY], [lineOnePtX,lineOnePtY],"} <br />
            {"    "}
            {"[lineTwoPtX,lineTwoPtY])"} <br />
            {"distance({(pointX, pointY)}, {(lineOnePtX, lineOnePtY)},"} <br />
            {"    "}
            {"{(lineTwoPtX, lineTwoPtY)})"} <br />
            {"distance([pointX,pointY,pointZ], [x0, y0, z0, a, b, c])"} <br />
            {"distance({(pointX, pointY, pointZ)}, {(x0, y0, z0, a, b, c)})"}
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix | Object</td>
              <td>Co-ordinates of first point</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>Array | Matrix | Object</td>
              <td>Co-ordinates of second point</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Number | BigNumber</td>
              <td>Returns the distance from two/three points</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            distance([0,0], [4,4]) <br />
            {"//"} Returns 5.656854249492381 <br /> <br />
            distance{"("} <br />
            {"{pointOneX: 0, pointOneY: 0},"} <br />
            {"{pointTwoX: 10, pointTwoY: 10})"} <br />
            {"//"} Returns 14.142135623730951 <br /> <br />
            distance([1, 0, 1], [4, -2, 2]) <br />
            {"//"} Returns 3.7416573867739413 <br /> <br />
            distance{"("} <br />
            {"{pointOneX: 4, pointOneY: 5, pointOneZ: 8},"} <br />
            {"{pointTwoX: 2, pointTwoY: 7, pointTwoZ: 9})"} <br />
            {"//"} Returns 3 <br /> <br />
            distance([1, 0, 1, 0], [0, -1, 0, -1]) <br />
            {"//"} Returns 2 <br /> <br />
            distance([[1, 2], [1, 2], [1, 3]]) <br />
            {"//"} Returns [0, 1, 1] <br /> <br />
            distance([[1,2,4], [1,2,6], [8,1,3]]) <br />
            {"//"} Returns [2, 7.14142842854285, 7.681145747868608] <br /> <br />
            distance([10, 10], [8, 1, 3]) <br />
            {"//"} Returns 11.535230316796387 <br /> <br />
            distance([0, 0], [3, 0], [0, 4]) <br />
            {"//"} Returns 2.4 <br /> <br />
            distance{"("} <br />
            {"{pointX: 0, pointY: 0},"} <br />
            {"{lineOnePtX: 3, lineOnePtY: 0},"} <br />
            {"{lineTwoPtX: 0, lineTwoPtY: 4})"} <br />
            {"//"} Returns 2.4 <br /> <br />
            distance([2, 3, 1], [1, 1, 2, 5, 0, 1]) <br />
            {"//"} Returns 2.3204774044612857 <br /> <br />
            distance{"("} <br />
            {"{pointX: 2, pointY: 3, pointZ: 1},"} <br />
            {"{x0: 1, y0: 1, z0: 2, a: 5, b: 0, c: 1})"} <br />
            {"//"} Returns 2.3204774044612857
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "intersect",
    text: (
      <div>
        <h1>Function intersect</h1>

        <p>
          Calculates the point of intersection of two lines in two or three dimensions and of a line
          and a plane in three dimensions. The inputs are in the form of arrays or 1 dimensional
          matrices. The line intersection functions return null if the lines do not meet.
        </p>

        <p>
          Note: Fill the plane coefficients as{" "}
          <code className="language-plaintext highlighter-rouge">x + y + z = c</code> and not as{" "}
          <code className="language-plaintext highlighter-rouge">x + y + z + c = 0</code>.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            intersect(endPoint1Line1, endPoint2Line1, <br />
            {"    "}endPoint1Line2, endPoint2Line2) <br />
            intersect(endPoint1, endPoint2, planeCoefficients)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">w</code>
              </td>
              <td>Array | Matrix</td>
              <td>Co-ordinates of first end-point of first line</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix</td>
              <td>Co-ordinates of second end-point of first line</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>Array | Matrix</td>
              <td>
                Co-ordinates of first end-point of second line OR Co-efficients of the plane’s
                equation
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">z</code>
              </td>
              <td>Array | Matrix</td>
              <td>
                Co-ordinates of second end-point of second line OR undefined if the calculation is
                for line and plane
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array</td>
              <td>Returns the point of intersection of lines/lines-planes</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            intersect([0, 0], [10, 10], [10, 0], [0, 10]) <br />
            {"//"} Returns [5, 5] <br /> <br />
            intersect([0, 0, 0], [10, 10, 0], [10, 0, 0], [0, 10, 0]) <br />
            {"//"} Returns [5, 5, 0] <br /> <br />
            intersect([1, 0, 1], [4, -2, 2], [1, 1, 1, 6]) <br />
            {"//"} Returns [7, -4, 3]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "and",
    text: (
      <div>
        <h1>Function and</h1>

        <p>
          Logical <code className="language-plaintext highlighter-rouge">and</code>. Test whether
          two values are both defined with a nonzero/nonempty value. For matrices, the function is
          evaluated element wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>and(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit | Array | Matrix</td>
              <td>First value to check</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Complex | Unit | Array | Matrix</td>
              <td>Second value to check</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean | Array | Matrix</td>
              <td>Returns true when both inputs are defined with a nonzero/nonempty value.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            and(2, 4) <br />
            {"//"} returns true <br /> <br />
            a = [2, 0, 0] <br />
            b = [3, 7, 0] <br />
            c = 0 <br /> <br />
            and(a, b) <br />
            {"//"} returns [true, false, false] <br /> <br />
            and(a, c) <br />
            {"//"} returns [false, false, false]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "not",
    text: (
      <div>
        <h1>Function not</h1>

        <p>
          Logical <code className="language-plaintext highlighter-rouge">not</code>. Flips boolean
          value of a given parameter. For matrices, the function is evaluated element wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>not(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit | Array | Matrix</td>
              <td>First value to check</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean | Array | Matrix</td>
              <td>Returns true when input is a zero or empty value.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            not(2) <br />
            {"//"} returns false <br /> <br />
            not(0) <br />
            {"//"} returns true <br /> <br />
            not(true) <br />
            {"//"} returns false <br /> <br />
            a = [2, -7, 0] <br />
            not(a) <br />
            {"//"} returns [false, false, true]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "or",
    text: (
      <div>
        <h1>Function or</h1>

        <p>
          Logical <code className="language-plaintext highlighter-rouge">or</code>. Test if at least
          one value is defined with a nonzero/nonempty value. For matrices, the function is
          evaluated element wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>or(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit | Array | Matrix</td>
              <td>First value to check</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Complex | Unit | Array | Matrix</td>
              <td>Second value to check</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean | Array | Matrix</td>
              <td>Returns true when one of the inputs is defined with a nonzero/nonempty value.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            or(2, 4) <br />
            {"//"} returns true <br /> <br />
            a = [2, 5, 0] <br />
            b = [0, 22, 0] <br />
            c = 0 <br /> <br />
            or(a, b) <br />
            {"//"} returns [true, true, false] <br /> <br />
            or(b, c) <br />
            {"//"} returns [false, true, false]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "xor",
    text: (
      <div>
        <h1>Function xor</h1>

        <p>
          Logical <code className="language-plaintext highlighter-rouge">xor</code>. Test whether
          one and only one value is defined with a nonzero/nonempty value. For matrices, the
          function is evaluated element wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>xor(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit | Array | Matrix</td>
              <td>First value to check</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Complex | Unit | Array | Matrix</td>
              <td>Second value to check</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean | Array | Matrix</td>
              <td>
                Returns true when one and only one input is defined with a nonzero/nonempty value.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            xor(2, 4) <br />
            {"//"} returns false <br /> <br />
            a = [2, 0, 0] <br />
            b = [2, 7, 0] <br />
            c = 0 <br /> <br />
            xor(a, b) <br />
            {"//"} returns [false, true, false] <br /> <br />
            xor(a, c) <br />
            {"//"} returns [true, false, false]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "erf",
    text: (
      <div>
        <h1>Function erf</h1>

        <p>
          Compute the erf function of a value using a rational Chebyshev approximations for
          different intervals of x.
        </p>

        <p>
          This is a translation of W. J. Cody’s Fortran implementation from 1987 (
          https://www.netlib.org/specfun/erf ). See the AMS publication “Rational Chebyshev
          Approximations for the Error Function” by W. J. Cody for an explanation of this process.
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>erf(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | Array | Matrix</td>
              <td>A real number</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Array | Matrix</td>
              <td>
                The erf of <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            erf(0.2) <br />
            {"//"} returns 0.22270258921047847 <br /> <br />
            erf(-0.5) <br />
            {"//"} returns -0.5204998778130465 <br /> <br />
            erf(4) <br />
            {"//"} returns 0.9999999845827421
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "cumsum",
    text: (
      <div>
        <h1>Function cumsum</h1>

        <p>
          Compute the cumulative sum of a matrix or a list with values. In case of a (multi
          dimensional) array or matrix, the cumulative sums along a specified dimension (defaulting
          to the first) will be calculated.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            cumsum(a, b, c, ...) <br /> cumsum(A)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… *</td>
              <td>A single matrix or or multiple scalar values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>The cumulative sum of all values</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            cumsum(2, 1, 4, 3) <br />
            {"//"} returns [2, 3, 7, 10] <br /> <br />
            cumsum([2, 1, 4, 3]) <br />
            {"//"} returns [2, 3, 7, 10] <br /> <br />
            cumsum([[1, 2], [3, 4]]) <br />
            {"//"} returns [[1, 2], [4, 6]] <br /> <br />
            cumsum([[1, 2], [3, 4]], 0) <br />
            {"//"} returns [[1, 2], [4, 6]] <br /> <br />
            cumsum([[1, 2], [3, 4]], 1) <br />
            {"//"} returns [[1, 3], [3, 7]] <br /> <br />
            cumsum([[2, 5], [4, 3], [1, 7]]) <br />
            {"//"} returns [[2, 5], [6, 8], [7, 15]] <br /> <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "mad",
    text: (
      <div>
        <h1>Function mad</h1>

        <p>
          Compute the median absolute deviation of a matrix or a list with values. The median
          absolute deviation is defined as the median of the absolute deviations from the median.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            mad(a, b, c, ...) <br />
            mad(A)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">array</code>
              </td>
              <td>Array | Matrix</td>
              <td>A single matrix or multiple scalar values.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>The median absolute deviation.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            mad(10, 20, 30) <br />
            {"//"} returns 10 <br /> <br />
            mad([1, 2, 3]) <br />
            {"//"} returns 1 <br /> <br />
            mad([[1, 2, 3], [4, 5, 6]]) <br />
            {"//"} returns 1.5
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "max",
    text: (
      <div>
        <h1>Function max</h1>

        <p>
          Compute the maximum value of a matrix or a list with values. In case of a multidimensional
          array, the maximum of the flattened array will be calculated. When{" "}
          <code className="language-plaintext highlighter-rouge">dim</code> is provided, the maximum
          over the selected dimension will be calculated. Parameter{" "}
          <code className="language-plaintext highlighter-rouge">dim</code> is zero-based.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            max(a, b, c, ...) <br />
            max(A) <br />
            max(A, dimension)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… *</td>
              <td>A single matrix or or multiple scalar values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>The maximum value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            max(2, 1, 4, 3) <br />
            {"//"} returns 4 <br /> <br />
            max([2, 1, 4, 3]) <br />
            {"//"} returns 4 <br /> <br />
            {"//"} maximum over a specified dimension (zero-based) <br />
            max([[2, 5], [4, 3], [1, 7]], 0) <br />
            {"//"} returns [4, 7] <br /> <br />
            max([[2, 5], [4, 3], [1, 7]], 1) <br />
            {"//"} returns [5, 4, 7] <br /> <br />
            max(2.7, 7.1, -4.5, 2.0, 4.1) <br />
            {"//"} returns 7.1 <br /> <br />
            min(2.7, 7.1, -4.5, 2.0, 4.1) <br />
            {"//"} returns -4.5
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "mean",
    text: (
      <div>
        <h1>Function mean</h1>

        <p>
          Compute the mean value of matrix or a list with values. In case of a multidimensional
          array, the mean of the flattened array will be calculated. When{" "}
          <code className="language-plaintext highlighter-rouge">dim</code> is provided, the maximum
          over the selected dimension will be calculated. Parameter{" "}
          <code className="language-plaintext highlighter-rouge">dim</code> is zero-based.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            mean(a, b, c, ...) <br />
            mean(A) <br />
            mean(A, dimension)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… *</td>
              <td>A single matrix or or multiple scalar values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>The mean of all values</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            mean(2, 1, 4, 3) <br />
            {"//"} returns 2.5 <br /> <br />
            mean([1, 2.7, 3.2, 4]) <br />
            {"//"} returns 2.725 <br /> <br />
            mean([[2, 5], [6, 3], [1, 7]], 0) <br />
            {"//"} returns [3, 5] <br /> <br />
            mean([[2, 5], [6, 3], [1, 7]], 1) <br />
            {"//"} returns [3.5, 4.5, 4]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "median",
    text: (
      <div>
        <h1>Function median</h1>

        <p>
          Compute the median of a matrix or a list with values. The values are sorted and the middle
          value is returned. In case of an even number of values, the average of the two middle
          values is returned. Supported types of values are: Number, BigNumber, Unit
        </p>

        <p>
          In case of a (multi dimensional) array or matrix, the median of all elements will be
          calculated.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            median(a, b, c, ...) <br />
            median(A)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… *</td>
              <td>A single matrix or or multiple scalar values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>The median</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            median(5, 2, 7) <br />
            {"//"} returns 5 <br /> <br />
            median([3, -1, 5, 7]) <br />
            {"//"} returns 4
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "min",
    text: (
      <div>
        <h1>Function min</h1>

        <p>
          Compute the minimum value of a matrix or a list of values. In case of a multidimensional
          array, the minimum of the flattened array will be calculated. When{" "}
          <code className="language-plaintext highlighter-rouge">dim</code> is provided, the minimum
          over the selected dimension will be calculated. Parameter{" "}
          <code className="language-plaintext highlighter-rouge">dim</code> is zero-based.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            min(a, b, c, ...) <br />
            min(A) <br />
            min(A, dimension)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… *</td>
              <td>A single matrix or or multiple scalar values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>The minimum value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            min(2, 1, 4, 3) <br />
            {"//"} returns 1 <br /> <br />
            min([2, 1, 4, 3]) <br />
            {"//"} returns 1 <br /> <br />
            {"//"} minimum over a specified dimension (zero-based) <br />
            min([[2, 5], [4, 3], [1, 7]], 0) <br />
            {"//"} returns [1, 3] <br /> <br />
            min([[2, 5], [4, 3], [1, 7]], 1) <br />
            {"//"} returns [2, 3, 1] <br /> <br />
            max(2.7, 7.1, -4.5, 2.0, 4.1) <br />
            {"//"} returns 7.1 <br /> <br />
            min(2.7, 7.1, -4.5, 2.0, 4.1) <br />
            {"//"} returns -4.5
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "mode",
    text: (
      <div>
        <h1>Function mode</h1>

        <p>
          Computes the mode of a set of numbers or a list with values(numbers or characters). If
          there are multiple modes, it returns a list of those values.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            mode(a, b, c, ...) <br />
            mode(A)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… *</td>
              <td>A single matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>The mode of all values</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            mode(2, 1, 4, 3, 1) <br />
            {"//"} returns [1] <br /> <br />
            mode([1, 2.7, 3.2, 4, 2.7]) <br />
            {"//"} returns [2.7] <br /> <br />
            mode(1, 4, 6, 1, 6) <br />
            {"//"} returns [1, 6] <br /> <br />
            mode(&apos;a&apos;,&apos;a&apos;,&apos;b&apos;,&apos;c&apos;) <br />
            {"//"} returns [&quot;a&quot;] <br /> <br />
            mode(1, 1.5, &apos;abc&apos;) <br />
            {"//"} returns [1, 1.5, &quot;abc&quot;]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "prod",
    text: (
      <div>
        <h1>Function prod</h1>

        <p>
          Compute the product of a matrix or a list with values. In case of a multidimensional array
          or matrix, the sum of all elements will be calculated.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            prod(a, b, c, ...) <br />
            prod(A)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… *</td>
              <td>A single matrix or or multiple scalar values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>The product of all values</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            multiply(2, 3) <br />
            {"//"} returns 6 <br /> <br />
            prod(2, 3) <br />
            {"//"} returns 6 <br /> <br />
            prod(2, 3, 4) <br />
            {"//"} returns 24 <br /> <br />
            prod([2, 3, 4]) <br />
            {"//"} returns 24 <br /> <br />
            prod([[2, 5], [4, 3]]) <br />
            {"//"} returns 120
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "quantileSeq",
    text: (
      <div>
        <h1>Function quantileSeq</h1>

        <p>
          Compute the prob order quantile of a matrix or a list with values. The sequence is sorted
          and the middle value is returned. Supported types of sequence values are: Number,
          BigNumber, Unit Supported types of probability are: Number, BigNumber
        </p>

        <p>
          In case of a multidimensional array or matrix, the prob order quantile of all elements
          will be calculated.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            quantileSeq(A, prob[, sorted]) <br />
            quantileSeq(A, [prob1, prob2, ...][, sorted]) <br />
            quantileSeq(A, N[, sorted])
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">data</code>
              </td>
              <td>Array, Matrix</td>
              <td>A single matrix or Array</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">probOrN</code>
              </td>
              <td>Number, BigNumber, Array</td>
              <td>
                prob is the order of the quantile, while N is the amount of evenly distributed steps
                of probabilities; only one of these options can be provided
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">sorted</code>
              </td>
              <td>Boolean</td>
              <td>=false is data sorted in ascending order</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Number, BigNumber, Unit, Array</td>
              <td>Quantile(s)</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            quantileSeq([3, -1, 5, 7], 0.5) <br />
            {"//"} returns 4 <br /> <br />
            quantileSeq([3, -1, 5, 7], [1/3, 2/3]) <br />
            {"//"} returns [3, 5] <br /> <br />
            quantileSeq([3, -1, 5, 7], 2) <br />
            {"//"} returns [3, 5] <br /> <br />
            quantileSeq([-1, 3, 5, 7], 0.5, true) <br />
            {"//"} returns 4
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "std",
    text: (
      <div>
        <h1>Function std</h1>

        <p>
          Compute the standard deviation of a matrix or a list with values. The standard deviations
          is defined as the square root of the variance:
          <code className="language-plaintext highlighter-rouge">std(A) = sqrt(variance(A))</code>.
          In case of a (multi dimensional) array or matrix, the standard deviation over all elements
          will be calculated by default, unless an axis is specified in which case the standard
          deviation will be computed along that axis.
        </p>

        <p>
          Additionally, it is possible to compute the standard deviation along the rows or columns
          of a matrix by specifying the dimension as the second argument.
        </p>

        <p>
          Optionally, the type of normalization can be specified as the final parameter. The
          parameter <code className="language-plaintext highlighter-rouge">normalization</code> can
          be one of the following values:
        </p>

        <ul>
          <li>‘unbiased’ (default) The sum of squared errors is divided by (n - 1)</li>
          <li>‘uncorrected’ The sum of squared errors is divided by n</li>
          <li>‘biased’ The sum of squared errors is divided by (n + 1)</li>
        </ul>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            std(a, b, c, ...) <br />
            std(A) <br />
            std(A, normalization) <br />
            std(A, dimension) <br />
            std(A, dimension, normalization)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">array</code>
              </td>
              <td>Array | Matrix</td>
              <td>A single matrix or or multiple scalar values</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">normalization</code>
              </td>
              <td>string</td>
              <td>
                Determines how to normalize the variance. Choose ‘unbiased’ (default),
                ‘uncorrected’, or ‘biased’. Default value: ‘unbiased’.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>The standard deviation</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            std(2, 4, 6) <br />
            {"//"} returns 2 <br /> <br />
            std([2, 4, 6, 8]) <br />
            {"//"} returns 2.581988897471611 <br /> <br />
            std([2, 4, 6, 8], &apos;uncorrected&apos;) <br />
            {"//"} returns 2.23606797749979 <br /> <br />
            std([2, 4, 6, 8], &apos;biased&apos;) <br />
            {"//"} returns 2 <br /> <br />
            std([[1, 2, 3], [4, 5, 6]]) <br />
            {"//"} returns 1.8708286933869707 <br /> <br />
            std([[1, 2, 3], [4, 6, 8]], 1) <br />
            {"//"} returns [1, 2] <br /> <br />
            std([[1, 2, 3], [4, 6, 8]], 1, &apos;biased&apos;) <br />
            {"//"} returns [0.7071067811865476, 1.4142135623730951]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "sum",
    text: (
      <div>
        <h1>Function sum</h1>

        <p>
          Compute the sum of a matrix or a list with values. In case of a multidimensional array or
          matrix, the sum of all elements will be calculated.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            sum(a, b, c, ...) <br />
            sum(A) <br />
            sum(A, dimension)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… *</td>
              <td>A single matrix or multiple scalar values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>The sum of all values</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            sum(2, 1, 4, 3) <br />
            {"//"} returns 10 <br /> <br />
            sum([2, 1, 4, 3]) <br />
            {"//"} returns 10 <br /> <br />
            sum([[2, 5], [4, 3], [1, 7]]) <br />
            {"//"} returns 22
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "variance",
    text: (
      <div>
        <h1>Function variance</h1>

        <p>
          Compute the variance of a matrix or a list with values. In case of a multidimensional
          array or matrix, the variance over all elements will be calculated.
        </p>

        <p>
          Additionally, it is possible to compute the variance along the rows or columns of a matrix
          by specifying the dimension as the second argument.
        </p>

        <p>
          Optionally, the type of normalization can be specified as the final parameter. The
          parameter <code className="language-plaintext highlighter-rouge">normalization</code> can
          be one of the following values:
        </p>

        <ul>
          <li>‘unbiased’ (default) The sum of squared errors is divided by (n - 1)</li>
          <li>‘uncorrected’ The sum of squared errors is divided by n</li>
          <li>‘biased’ The sum of squared errors is divided by (n + 1)</li>
        </ul>

        <p>
          Note that older browser may not like the variable name{" "}
          <code className="language-plaintext highlighter-rouge">var</code>. In that case, the
          function can be called as{" "}
          <code className="language-plaintext highlighter-rouge">math[&apos;var&apos;](...)</code>{" "}
          instead of
          <code className="language-plaintext highlighter-rouge">var(...)</code>.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            variance(a, b, c, ...) <br />
            variance(A) <br />
            variance(A, normalization) <br />
            variance(A, dimension) <br />
            variance(A, dimension, normalization)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">array</code>
              </td>
              <td>Array | Matrix</td>
              <td>A single matrix or or multiple scalar values</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">normalization</code>
              </td>
              <td>string</td>
              <td>
                Determines how to normalize the variance. Choose ‘unbiased’ (default),
                ‘uncorrected’, or ‘biased’. Default value: ‘unbiased’.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>The variance</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            variance(2, 4, 6) <br />
            {"//"} returns 4 <br /> <br />
            variance([2, 4, 6, 8]) <br />
            {"//"} returns 6.666666666666667 <br /> <br />
            variance([2, 4, 6, 8], &apos;uncorrected&apos;) <br />
            {"//"} returns 5 <br /> <br />
            variance([2, 4, 6, 8], &apos;biased&apos;) <br />
            {"//"} returns 4 <br /> <br />
            variance([[1, 2, 3], [4, 5, 6]]) <br />
            {"//"} returns 3.5 <br /> <br />
            variance([[1, 2, 3], [4, 6, 8]], 0) <br />
            {"//"} returns [4.5, 8, 12.5] <br /> <br />
            variance([[1, 2, 3], [4, 6, 8]], 1) <br />
            {"//"} returns [1, 4] <br /> <br />
            variance([[1, 2, 3], [4, 6, 8]], 1, &apos;biased&apos;) <br />
            {"//"} returns [0.5, 2]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "bin",
    text: (
      <div>
        <h1>Function bin</h1>

        <p>Format a number as binary.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>bin(value)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">value</code>
              </td>
              <td>number</td>
              <td>Value to be stringified</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">wordSize</code>
              </td>
              <td>number</td>
              <td>
                Optional word size (see{" "}
                <code className="language-plaintext highlighter-rouge">format</code>)
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>string</td>
              <td>The formatted value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            bin(2) <br />
            {"//"} returns &quot;0b10&quot;
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "format",
    text: (
      <div>
        <h1>Function format</h1>

        <p>Format a value of any type into a string.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            format(value) <br />
            format(value, options) <br />
            format(value, precision) <br />
            format(value, callback)
          </code>
        </pre>

        <h3>Where</h3>

        <ul>
          <li>
            <code className="language-plaintext highlighter-rouge">value: *</code>
            The value to be formatted
          </li>
          <li>
            <code className="language-plaintext highlighter-rouge">options: Object</code>
            An object with formatting options. Available options:
            <ul>
              <li>
                <code className="language-plaintext highlighter-rouge">notation: string</code>
                Number notation. Choose from:
                <ul>
                  <li>
                    <code className="language-plaintext highlighter-rouge">&apos;fixed&apos;</code>
                    Always use regular number notation. For example{" "}
                    <code className="language-plaintext highlighter-rouge">
                      &apos;123.40&apos;
                    </code>{" "}
                    and{" "}
                    <code className="language-plaintext highlighter-rouge">
                      &apos;14000000&apos;
                    </code>
                  </li>
                  <li>
                    <code className="language-plaintext highlighter-rouge">
                      &apos;exponential&apos;
                    </code>
                    Always use exponential notation. For example{" "}
                    <code className="language-plaintext highlighter-rouge">
                      &apos;1.234e+2&apos;
                    </code>{" "}
                    and{" "}
                    <code className="language-plaintext highlighter-rouge">&apos;1.4e+7&apos;</code>
                  </li>
                  <li>
                    <code className="language-plaintext highlighter-rouge">
                      &apos;engineering&apos;
                    </code>
                    Always use engineering notation: always have exponential notation, and select
                    the exponent to be a multiple of{" "}
                    <code className="language-plaintext highlighter-rouge">3</code>. For example{" "}
                    <code className="language-plaintext highlighter-rouge">
                      &apos;123.4e+0&apos;
                    </code>{" "}
                    and{" "}
                    <code className="language-plaintext highlighter-rouge">
                      &apos;14.0e+6&apos;
                    </code>
                  </li>
                  <li>
                    <code className="language-plaintext highlighter-rouge">&apos;auto&apos;</code>{" "}
                    (default) Regular number notation for numbers having an absolute value between
                    <code className="language-plaintext highlighter-rouge">lower</code> and{" "}
                    <code className="language-plaintext highlighter-rouge">upper</code> bounds, and
                    uses exponential notation elsewhere. Lower bound is included, upper bound is
                    excluded. For example{" "}
                    <code className="language-plaintext highlighter-rouge">&apos;123.4&apos;</code>{" "}
                    and{" "}
                    <code className="language-plaintext highlighter-rouge">&apos;1.4e7&apos;</code>.
                  </li>
                  <li>
                    <code className="language-plaintext highlighter-rouge">&apos;bin&apos;</code>,{" "}
                    <code className="language-plaintext highlighter-rouge">&apos;oct&apos;</code>,
                    or <code className="language-plaintext highlighter-rouge">&apos;hex&apos;</code>
                    Format the number using binary, octal, or hexadecimal notation. For example{" "}
                    <code className="language-plaintext highlighter-rouge">
                      &apos;0b1101&apos;
                    </code>{" "}
                    and{" "}
                    <code className="language-plaintext highlighter-rouge">&apos;0x10fe&apos;</code>
                    .
                  </li>
                </ul>
              </li>
              <li>
                <code className="language-plaintext highlighter-rouge">wordSize: number</code>
                The word size in bits to use for formatting in binary, octal, or hexadecimal
                notation. To be used only with{" "}
                <code className="language-plaintext highlighter-rouge">&apos;bin&apos;</code>,{" "}
                <code className="language-plaintext highlighter-rouge">&apos;oct&apos;</code>, or{" "}
                <code className="language-plaintext highlighter-rouge">&apos;hex&apos;</code>
                values for <code className="language-plaintext highlighter-rouge">
                  notation
                </code>{" "}
                option. When this option is defined the value is formatted as a signed twos
                complement integer of the given word size and the size suffix is appended to the
                output. For example{" "}
                <code className="language-plaintext highlighter-rouge">
                  format(-1, {"{notation: 'hex', wordSize: 8}"}) === &apos;0xffi8&apos;
                </code>
                . Default value is undefined.
              </li>
              <li>
                <code className="language-plaintext highlighter-rouge">precision: number</code>
                Limit the number of digits of the formatted value. For regular numbers, must be a
                number between <code className="language-plaintext highlighter-rouge">
                  0
                </code> and <code className="language-plaintext highlighter-rouge">16</code>. For
                bignumbers, the maximum depends on the configured precision, see function{" "}
                <code className="language-plaintext highlighter-rouge">config()</code>. In case of
                notations{" "}
                <code className="language-plaintext highlighter-rouge">
                  &apos;exponential&apos;
                </code>
                ,{" "}
                <code className="language-plaintext highlighter-rouge">
                  &apos;engineering&apos;
                </code>
                , and <code className="language-plaintext highlighter-rouge">&apos;auto&apos;</code>
                ,<code className="language-plaintext highlighter-rouge">precision</code> defines the
                total number of significant digits returned. In case of notation{" "}
                <code className="language-plaintext highlighter-rouge">&apos;fixed&apos;</code>,{" "}
                <code className="language-plaintext highlighter-rouge">precision</code> defines the
                number of significant digits after the decimal point.
                <code className="language-plaintext highlighter-rouge">precision</code> is undefined
                by default.
              </li>
              <li>
                <code className="language-plaintext highlighter-rouge">lowerExp: number</code>
                Exponent determining the lower boundary for formatting a value with an exponent when{" "}
                <code className="language-plaintext highlighter-rouge">
                  notation=&apos;auto&apos;
                </code>
                . Default value is <code className="language-plaintext highlighter-rouge">-3</code>.
              </li>
              <li>
                <code className="language-plaintext highlighter-rouge">upperExp: number</code>
                Exponent determining the upper boundary for formatting a value with an exponent when{" "}
                <code className="language-plaintext highlighter-rouge">
                  notation=&apos;auto&apos;
                </code>
                . Default value is <code className="language-plaintext highlighter-rouge">5</code>.
              </li>
              <li>
                <code className="language-plaintext highlighter-rouge">fraction: string</code>.
                Available values:{" "}
                <code className="language-plaintext highlighter-rouge">&apos;ratio&apos;</code>{" "}
                (default) or{" "}
                <code className="language-plaintext highlighter-rouge">&apos;decimal&apos;</code>.
                For example{" "}
                <code className="language-plaintext highlighter-rouge">format(fraction(1, 3))</code>{" "}
                will output{" "}
                <code className="language-plaintext highlighter-rouge">&apos;1/3&apos;</code> when{" "}
                <code className="language-plaintext highlighter-rouge">&apos;ratio&apos;</code>
                is configured, and will output{" "}
                <code className="language-plaintext highlighter-rouge">
                  &apos;0.(3)&apos;
                </code> when{" "}
                <code className="language-plaintext highlighter-rouge">&apos;decimal&apos;</code> is
                configured.
              </li>
              <li>
                <code className="language-plaintext highlighter-rouge">truncate: number</code>.
                Specifies the maximum allowed length of the returned string. If it had been longer,
                the excess characters are deleted and replaced with{" "}
                <code className="language-plaintext highlighter-rouge">&apos;...&apos;</code>.
              </li>
            </ul>
          </li>
          <li>
            <code className="language-plaintext highlighter-rouge">callback: function</code>A custom
            formatting function, invoked for all numeric elements in{" "}
            <code className="language-plaintext highlighter-rouge">value</code>, for example all
            elements of a matrix, or the real and imaginary parts of a complex number. This callback
            can be used to override the built-in numeric notation with any type of formatting.
            Function <code className="language-plaintext highlighter-rouge">callback</code>
            is called with <code className="language-plaintext highlighter-rouge">value</code> as
            parameter and must return a string.
          </li>
        </ul>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">value</code>
              </td>
              <td>*</td>
              <td>Value to be stringified</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">options</code>
              </td>
              <td>Object | Function | number</td>
              <td>Formatting options</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>string</td>
              <td>The formatted value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            format(6.4) <br />
            {"//"} returns &apos;6.4&apos; <br /> <br />
            format(1240000) <br />
            {"//"} returns &apos;1.24e+6&apos; <br /> <br />
            format(1/3) <br />
            {"//"} returns &apos;0.3333333333333333&apos; <br /> <br />
            format(1/3, 3) <br />
            {"//"} returns &apos;0.333&apos; <br /> <br />
            format(21385, 2) <br />
            {"//"} returns &apos;21000&apos; <br /> <br />
            format(12e8, {"{notation: 'fixed'}"}) <br />
            {"//"} returns &apos;1200000000&apos; <br /> <br />
            format(2.3, {"{notation: 'fixed', precision: 4}"}) <br />
            {"//"} returns &apos;2.3000&apos; <br /> <br />
            format(52.8, {"{notation: 'exponential'}"}) <br />
            {"//"} returns &apos;5.28e+1&apos; <br /> <br />
            format(12400, {"{notation: 'engineering'}"}) <br />
            {"//"} returns &apos;12.4e+3&apos; <br /> <br />
            format(2000, {"{lowerExp: -2, upperExp: 2}"}) <br />
            {"//"} returns &apos;2e+3&apos; <br /> <br />
            function formatCurrency(value) {"{"} <br />
            {"    "}
            {"//"}return currency notation with two digits: <br />
            {"    "}return &apos;$&apos; + value.toFixed(2) <br />
            {"    "}
            {"//"} you could also use format inside the callback: <br />
            {"    "}
            {"//"} return &apos;$&apos; + format(value, <br />
            {"        "}
            {"{notation: 'fixed', precision: 2}"}) <br />
            {"}"} <br /> <br />
            format([2.1, 3, 0.016], formatCurrency) <br />
            {"//"} returns &apos;[$2.10, $3.00, $0.02]&apos;
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "hex",
    text: (
      <div>
        <h1>Function hex</h1>

        <p>Format a number as hexadecimal.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>hex(value)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">value</code>
              </td>
              <td>number</td>
              <td>Value to be stringified</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">wordSize</code>
              </td>
              <td>number</td>
              <td>
                Optional word size (see{" "}
                <code className="language-plaintext highlighter-rouge">format</code>)
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>string</td>
              <td>The formatted value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            hex(240) <br />
            {"//"} returns &quot;0xF0&quot;
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "oct",
    text: (
      <div>
        <h1>Function oct</h1>

        <p>Format a number as octal.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>oct(value)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">value</code>
              </td>
              <td>number</td>
              <td>Value to be stringified</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">wordSize</code>
              </td>
              <td>number</td>
              <td>
                Optional word size (see{" "}
                <code className="language-plaintext highlighter-rouge">format</code>)
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>string</td>
              <td>The formatted value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            oct(56) <br />
            {"//"} returns &quot;0o70&quot;
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "print",
    text: (
      <div>
        <h1>Function print</h1>

        <p>Interpolate values into a string template.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            print(template, values) <br />
            print(template, values, precision) <br />
            print(template, values, options)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">template</code>
              </td>
              <td>string</td>
              <td>A string containing variable placeholders.</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">values</code>
              </td>
              <td>Object | Array | Matrix</td>
              <td>
                An object or array containing variables which will be filled in in the template.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">options</code>
              </td>
              <td>number | Object</td>
              <td>
                Formatting options, or the number of digits to format numbers. See function format
                for a description of all options.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>string</td>
              <td>Interpolated string</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"//"} the following outputs: &apos;Lucy is 5 years old&apos; <br />
            print{"("}&apos;Lucy is $age years old&apos;, {"{age: 5}"}
            {")"} <br /> <br />
            {"//"} the following outputs: &apos;The value of pi is 3.141592654&apos; <br />
            print{"("}&apos;The value of pi is $pi&apos;, {"{pi: pi}"}, 10{")"} <br /> <br />
            {"//"} the following outputs: <br />
            {"//"} &apos;hello Mary! The date is 2013-03-23&apos; <br />
            print(&apos;Hello $user.name! The date is $date&apos;, {"{"} <br />
            {"    "}
            {"user:"} {"{"} <br />
            {"        "}name: &apos;Mary&apos;, <br />
            {"    "}
            {"}"}, <br />
            {"    "}date: new Date(2013, 2, 23).toISOString() <br />
            {"        "}.substring(0, 10) <br />
            {"}"}) <br /> <br />
            {"//"} the following outputs: &apos;My favorite fruits are apples and <br />
            {"//"} bananas !&apos; <br />
            print{"("}&apos;My favorite fruits are $0 and $1 !&apos;, {"["} <br />
            {"    "}&apos;apples&apos;, <br />
            {"    "}&apos;bananas&apos; <br />
            {"])"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "to",
    text: (
      <div>
        <h1>Function to</h1>

        <p>Change the unit of a value.</p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>to(x, unit)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Unit | Array | Matrix</td>
              <td>The unit to be converted.</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">unit</code>
              </td>
              <td>Unit | Array | Matrix</td>
              <td>New unit. Can be a string like “cm” or a unit without value.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Unit | Array | Matrix</td>
              <td>value with changed, fixed unit.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            to(unit(&apos;2 inch&apos;), &apos;cm&apos;) <br />
            {"//"} returns Unit 5.08 cm <br /> <br />
            to(unit(&apos;2 inch&apos;), unit(&apos;cm&apos;)) <br />
            {"//"} returns Unit 5.08 cm <br /> <br />
            to(unit(16, &apos;bytes&apos;), &apos;bits&apos;) <br />
            {"//"} returns Unit 128 bits
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "clone",
    text: (
      <div>
        <h1>Function clone</h1>

        <p>Clone an object. Will make a deep copy of the data.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>clone(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>*</td>
              <td>Object to be cloned</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>A clone of object x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            clone(3.5) <br />
            {"//"} returns number 3.5 <br /> <br />
            clone(complex(&apos;2-4i&apos;)) <br />
            {"//"} returns Complex 2 - 4i <br /> <br />
            clone(unit(45, &apos;deg&apos;)) <br />
            {"//"} returns Unit 45 deg <br /> <br />
            clone([[1, 2], [3, 4]]) <br />
            {"//"} returns Array [[1, 2], [3, 4]] <br /> <br />
            clone(&quot;hello world&quot;) <br />
            {"//"} returns string &quot;hello world&quot;
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "hasNumericValue",
    text: (
      <div>
        <h1>Function hasNumericValue</h1>

        <p>Test whether a value is an numeric value.</p>

        <p>In case of a string, true is returned if the string contains a numeric value.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>hasNumericValue(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>*</td>
              <td>Value to be tested</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean</td>
              <td>
                Returns true when <code className="language-plaintext highlighter-rouge">x</code> is
                a <code className="language-plaintext highlighter-rouge">number</code>,{" "}
                <code className="language-plaintext highlighter-rouge">BigNumber</code>,{" "}
                <code className="language-plaintext highlighter-rouge">Fraction</code>,{" "}
                <code className="language-plaintext highlighter-rouge">Boolean</code>, or a{" "}
                <code className="language-plaintext highlighter-rouge">String</code> containing
                number. Returns false for other types. Throws an error in case of unknown types.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            hasNumericValue(2) <br />
            {"//"} returns true <br /> <br />
            hasNumericValue(&apos;2&apos;) <br />
            {"//"} returns true <br /> <br />
            isNumeric(&apos;2&apos;) <br />
            {"//"} returns false <br /> <br />
            hasNumericValue(0) <br />
            {"//"} returns true <br /> <br />
            hasNumericValue(bignumber(500)) <br />
            {"//"} returns true <br /> <br />
            hasNumericValue(fraction(4)) <br />
            {"//"} returns true <br /> <br />
            hasNumericValue(complex(&apos;2-4i&apos;)) <br />
            {"//"} returns false <br /> <br />
            hasNumericValue(false) <br />
            {"//"} returns true <br /> <br />
            hasNumericValue([2.3, &apos;foo&apos;, false]) <br />
            {"//"} returns [true, false, true]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "isInteger",
    text: (
      <div>
        <h1>Function isInteger</h1>

        <p>
          Test whether a value is an integer number. The function supports{" "}
          <code className="language-plaintext highlighter-rouge">number</code>,{" "}
          <code className="language-plaintext highlighter-rouge">BigNumber</code>, and{" "}
          <code className="language-plaintext highlighter-rouge">Fraction</code>.
        </p>

        <p>The function is evaluated element-wise in case of Array or Matrix input.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>isInteger(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Array | Matrix</td>
              <td>Value to be tested</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean</td>
              <td>
                Returns true when <code className="language-plaintext highlighter-rouge">x</code>{" "}
                contains a numeric, integer value. Throws an error in case of an unknown data type.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <div>
            isInteger(2) <br />
            {"//"} returns true <br /> <br />
            isInteger(0) <br />
            {"//"} returns true <br /> <br />
            isInteger(0.5) <br />
            {"//"} returns false <br /> <br />
            isInteger(bignumber(500)) <br />
            {"//"} returns true <br /> <br />
            isInteger(fraction(4)) <br />
            {"//"} returns true <br /> <br />
            isInteger(&apos;3&apos;) <br />
            {"//"} returns true <br /> <br />
            isInteger([3, 0.5, -2]) <br />
            {"//"} returns [true, false, true] <br /> <br />
            isInteger(complex(&apos;2-4i&apos;)) <br />
            {"//"} throws an error
          </div>
        </pre>
      </div>
    ),
  },
  {
    math: "isNaN",
    text: (
      <div>
        <h1>Function isNaN</h1>

        <p>
          Test whether a value is NaN (not a number). The function supports types{" "}
          <code className="language-plaintext highlighter-rouge">number</code>,{" "}
          <code className="language-plaintext highlighter-rouge">BigNumber</code>,{" "}
          <code className="language-plaintext highlighter-rouge">Fraction</code>,{" "}
          <code className="language-plaintext highlighter-rouge">Unit</code> and{" "}
          <code className="language-plaintext highlighter-rouge">Complex</code>.
        </p>

        <p>The function is evaluated element-wise in case of Array or Matrix input.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>isNaN(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table>
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Unit | Array | Matrix</td>
              <td>Value to be tested</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean</td>
              <td>
                Returns true when <code className="language-plaintext highlighter-rouge">x</code> is
                NaN. Throws an error in case of an unknown data type.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            isNaN(3) <br />
            {"//"} returns false <br /> <br />
            isNaN(NaN) <br />
            {"//"} returns true <br /> <br />
            isNaN(0) <br />
            {"//"} returns false <br /> <br />
            isNaN(bignumber(NaN)) <br />
            {"//"} returns true <br /> <br />
            isNaN(bignumber(0)) <br />
            {"//"} returns false <br /> <br />
            isNaN(fraction(-2, 5)) <br />
            {"//"} returns false <br /> <br />
            isNaN(&apos;-2&apos;) <br />
            {"//"} returns false <br /> <br />
            isNaN([2, 0, -3, NaN]) <br />
            {"//"} returns [false, false, false, true]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "isNegative",
    text: (
      <div>
        <h1>Function isNegative</h1>

        <p>
          Test whether a value is negative: smaller than zero. The function supports types{" "}
          <code className="language-plaintext highlighter-rouge">number</code>,{" "}
          <code className="language-plaintext highlighter-rouge">BigNumber</code>,{" "}
          <code className="language-plaintext highlighter-rouge">Fraction</code>, and{" "}
          <code className="language-plaintext highlighter-rouge">Unit</code>.
        </p>

        <p>The function is evaluated element-wise in case of Array or Matrix input.</p>

        <h2>Syntax</h2>

        <div className="language-js highlighter-rouge">
          <div className="highlight">
            <pre className="highlight">
              <code>isNegative(x)</code>
            </pre>
          </div>
        </div>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Unit | Array | Matrix</td>
              <td>Value to be tested</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean</td>
              <td>
                Returns true when <code className="language-plaintext highlighter-rouge">x</code> is
                larger than zero. Throws an error in case of an unknown data type.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            isNegative(3) <br />
            {"//"} returns false <br /> <br />
            isNegative(-2) <br />
            {"//"} returns true <br /> <br />
            isNegative(0) <br />
            {"//"} returns false <br /> <br />
            isNegative(-0) <br />
            {"//"} returns false <br /> <br />
            isNegative(bignumber(2)) <br />
            {"//"} returns false <br /> <br />
            isNegative(fraction(-2, 5)) <br />
            {"//"} returns true <br /> <br />
            isNegative(&apos;-2&apos;) <br />
            {"//"} returns true <br /> <br />
            isNegative([2, 0, -3]) <br />
            {"//"} returns [false, false, true]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "isNumeric",
    text: (
      <div>
        <h1>Function isNumeric</h1>

        <p>Test whether a value is an numeric value.</p>

        <p>The function is evaluated element-wise in case of Array or Matrix input.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>isNumeric(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>*</td>
              <td>Value to be tested</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean</td>
              <td>
                Returns true when <code className="language-plaintext highlighter-rouge">x</code> is
                a <code className="language-plaintext highlighter-rouge">number</code>,{" "}
                <code className="language-plaintext highlighter-rouge">BigNumber</code>,{" "}
                <code className="language-plaintext highlighter-rouge">Fraction</code>, or{" "}
                <code className="language-plaintext highlighter-rouge">boolean</code>. Returns false
                for other types. Throws an error in case of unknown types.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            isNumeric(2) <br />
            {"//"} returns true <br /> <br />
            isNumeric(&apos;2&apos;) <br />
            {"//"} returns false <br /> <br />
            hasNumericValue(&apos;2&apos;) <br />
            {"//"} returns true <br /> <br />
            isNumeric(0) <br />
            {"//"} returns true <br /> <br />
            isNumeric(bignumber(500)) <br />
            {"//"} returns true <br /> <br />
            isNumeric(fraction(4)) <br />
            {"//"} returns true <br /> <br />
            isNumeric(complex(&apos;2-4i&apos;)) <br />
            {"//"} returns false <br /> <br />
            isNumeric([2.3, &apos;foo&apos;, false]) <br />
            {"//"} returns [true, false, true]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "isPositive",
    text: (
      <div>
        <h1>Function isPositive</h1>

        <p>
          Test whether a value is positive: larger than zero. The function supports types{" "}
          <code className="language-plaintext highlighter-rouge">number</code>,{" "}
          <code className="language-plaintext highlighter-rouge">BigNumber</code>,{" "}
          <code className="language-plaintext highlighter-rouge">Fraction</code>, and{" "}
          <code className="language-plaintext highlighter-rouge">Unit</code>.
        </p>

        <p>The function is evaluated element-wise in case of Array or Matrix input.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>isPositive(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Unit | Array | Matrix</td>
              <td>Value to be tested</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean</td>
              <td>
                Returns true when <code className="language-plaintext highlighter-rouge">x</code> is
                larger than zero. Throws an error in case of an unknown data type.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            isPositive(3) <br />
            {"//"} returns true <br /> <br />
            isPositive(-2) <br />
            {"//"} returns false <br /> <br />
            isPositive(0) <br />
            {"//"} returns false <br /> <br />
            isPositive(-0) <br />
            {"//"} returns false <br /> <br />
            isPositive(0.5) <br />
            {"//"} returns true <br /> <br />
            isPositive(bignumber(2)) <br />
            {"//"} returns true <br /> <br />
            isPositive(fraction(-2, 5)) <br />
            {"//"} returns false <br /> <br />
            isPositive(fraction(1, 3)) <br />
            {"//"} returns true <br /> <br />
            isPositive(&apos;2&apos;) <br />
            {"//"} returns true <br /> <br />
            isPositive([2, 0, -3]) <br />
            {"//"} returns [true, false, false]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "isPrime",
    text: (
      <div>
        <h1>Function isPrime</h1>

        <p>
          Test whether a value is prime: has no divisors other than itself and one. The function
          supports type <code className="language-plaintext highlighter-rouge">number</code>,{" "}
          <code className="language-plaintext highlighter-rouge">bignumber</code>.
        </p>

        <p>The function is evaluated element-wise in case of Array or Matrix input.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>isPrime(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Array | Matrix</td>
              <td>Value to be tested</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean</td>
              <td>
                Returns true when <code className="language-plaintext highlighter-rouge">x</code> is
                larger than zero. Throws an error in case of an unknown data type.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            isPrime(3) <br />
            {"//"} returns true <br /> <br />
            isPrime(-2) <br />
            {"//"} returns false <br /> <br />
            isPrime(0) <br />
            {"//"} returns false <br /> <br />
            isPrime(-0) <br />
            {"//"} returns false <br /> <br />
            isPrime(0.5) <br />
            {"//"} returns false <br /> <br />
            isPrime(&apos;2&apos;) <br />
            {"//"} returns true <br /> <br />
            isPrime([2, 17, 100]) <br />
            {"//"} returns [true, true, false]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "isZero",
    text: (
      <div>
        <h1>Function isZero</h1>

        <p>
          Test whether a value is zero. The function can check for zero for types{" "}
          <code className="language-plaintext highlighter-rouge">number</code>,{" "}
          <code className="language-plaintext highlighter-rouge">BigNumber</code>,{" "}
          <code className="language-plaintext highlighter-rouge">Fraction</code>,
          <code className="language-plaintext highlighter-rouge">Complex</code>, and{" "}
          <code className="language-plaintext highlighter-rouge">Unit</code>.
        </p>

        <p>The function is evaluated element-wise in case of Array or Matrix input.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>isZero(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Fraction | Unit | Array | Matrix</td>
              <td>Value to be tested</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean</td>
              <td>
                Returns true when <code className="language-plaintext highlighter-rouge">x</code> is
                zero. Throws an error in case of an unknown data type.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            isZero(0) <br />
            {"//"} returns true <br /> <br />
            isZero(2) <br />
            {"//"} returns false <br /> <br />
            isZero(0.5) <br />
            {"//"} returns false <br /> <br />
            isZero(bignumber(0)) <br />
            {"//"} returns true <br /> <br />
            isZero(fraction(0)) <br />
            {"//"} returns true <br /> <br />
            isZero(fraction(1,3)) <br />
            {"//"} returns false <br /> <br />
            isZero(complex(&apos;2 - 4i&apos;)) <br />
            {"//"} returns false <br /> <br />
            isZero(complex(&apos;0i&apos;)) <br />
            {"//"} returns true <br /> <br />
            isZero(&apos;0&apos;) <br />
            {"//"} returns true <br /> <br />
            isZero(&apos;2&apos;) <br />
            {"//"} returns false <br /> <br />
            isZero([2, 0, -3]) <br />
            {"//"} returns [false, true, false]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "numeric",
    text: (
      <div>
        <h1>Function numeric</h1>

        <p>Convert a numeric input to a specific numeric type: number, BigNumber, or Fraction.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>numeric(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">value</code>
              </td>
              <td>string | number | BigNumber | Fraction</td>
              <td>A numeric value or a string containing a numeric value</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">outputType</code>
              </td>
              <td>string</td>
              <td>
                Desired numeric output type. Available values: ‘number’, ‘BigNumber’, or ‘Fraction’
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction</td>
              <td>Returns an instance of the numeric in the requested type</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            numeric(&apos;4&apos;) <br />
            {"//"} returns 4 <br /> <br />
            numeric(&apos;4&apos;, &apos;number&apos;) <br />
            {"//"} returns 4 <br /> <br />
            numeric(&apos;4&apos;, &apos;BigNumber&apos;) <br />
            {"//"} returns BigNumber 4 <br /> <br />
            numeric(&apos;4&apos;, &apos;Fraction&apos;) <br />
            {"//"} returns Fraction 4 <br /> <br />
            numeric(4, &apos;Fraction&apos;) <br />
            {"//"} returns Fraction 4 <br /> <br />
            numeric(fraction(2, 5), &apos;number&apos;) <br />
            {"//"} returns 0.4
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "typeOf",
    text: (
      <div>
        <h1>Function typeOf</h1>

        <p>Determine the type of an entity.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>typeOf(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>*</td>
              <td>The variable for which to test the type.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>string</td>
              <td>
                Returns the name of the type. Primitive types are lower case, non-primitive types
                are upper-camel-case. For example ‘number’, ‘string’, ‘Array’, ‘Date’.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"//"} This list is intended to include all <br />
            {"//"} relevant types, for testing purposes: <br />
            typeOf(3.5) <br />
            {"//"} returns &apos;number&apos; <br /> <br />
            typeOf(complex(&apos;2-4i&apos;)) <br />
            {"//"} returns &apos;Complex&apos; <br /> <br />
            typeOf(unit(&apos;45 deg&apos;)) <br />
            {"//"} returns &apos;Unit&apos; <br /> <br />
            typeOf(&apos;hello world&apos;) <br />
            {"//"} returns &apos;string&apos; <br /> <br />
            typeOf(null) <br />
            {"//"} returns &apos;null&apos; <br /> <br />
            typeOf(true) <br />
            {"//"} returns &apos;boolean&apos; <br /> <br />
            typeOf([1, 2, 3]) <br />
            {"//"} returns &apos;Array&apos; <br /> <br />
            typeOf(new Date()) <br />
            {"//"} returns &apos;Date&apos; <br /> <br />
            typeOf(function () {}) <br />
            {"//"} returns &apos;function&apos; <br /> <br />
            {"typeOf({a: 2, b: 3})"} <br />
            {"//"} returns &apos;Object&apos; <br /> <br />
            typeOf(/a regexp/) <br />
            {"//"} returns &apos;RegExp&apos; <br /> <br />
            typeOf(undefined) <br />
            {"//"} returns &apos;undefined&apos; <br /> <br />
            typeOf(bignumber(&apos;23e99&apos;)) <br />
            {"//"} returns &apos;BigNumber&apos; <br /> <br />
            typeOf(chain(2)) <br />
            {"//"} returns &apos;Chain&apos; <br /> <br />
            typeOf(fraction(1, 3)) <br />
            {"//"} returns &apos;Fraction&apos; <br /> <br />
            typeOf(help(&apos;sqrt&apos;)) <br />
            {"//"} returns &apos;Help&apos; <br /> <br />
            typeOf(index(1, 3)) <br />
            {"//"} returns &apos;Index&apos; <br /> <br />
            typeOf(matrix([[1],[3]])) <br />
            {"//"} returns &apos;DenseMatrix&apos; <br /> <br />
            typeOf(matrix([],&apos;sparse&apos;)) <br />
            {"//"} returns &apos;SparseMatrix&apos; <br /> <br />
            typeOf(new Range(0, 10)) <br />
            {"//"} returns &apos;Range&apos; <br /> <br />
            typeOf(evaluate(&apos;a=2\na&apos;)) <br />
            {"//"} returns &apos;ResultSet&apos; <br /> <br />
            typeOf(parse(&apos;A[2]&apos;)) <br />
            {"//"} returns &apos;AccessorNode&apos; <br /> <br />
            typeOf(parse(&apos;[1,2,3]&apos;)) <br />
            {"//"} returns &apos;ArrayNode&apos; <br /> <br />
            typeOf(parse(&apos;x=2&apos;)) <br />
            {"//"} returns &apos;AssignmentNode&apos; <br /> <br />
            typeOf(parse(&apos;a=2; b=3&apos;)) <br />
            {"//"} returns &apos;BlockNode&apos; <br /> <br />
            {"typeOf(parse('x<0?-1:1'))"} <br />
            {"//"} returns &apos;ConditionalNode&apos; <br /> <br />
            typeOf(parse(&apos;2.3&apos;)) <br />
            {"//"} returns &apos;ConstantNode&apos; <br /> <br />
            typeOf(parse(&apos;f(x)=x^2&apos;)) <br />
            {"//"} returns &apos;FunctionAssignmentNode&apos; <br /> <br />
            typeOf(parse(&apos;sqrt(4)&apos;)) <br />
            {"//"} returns &apos;FunctionNode&apos; <br /> <br />
            typeOf(parse(&apos;A[2]&apos;).index) <br />
            {"//"} returns &apos;IndexNode&apos; <br /> <br />
            {"typeOf(parse('{a:2}'))"} <br />
            {"//"} returns &apos;ObjectNode&apos; <br /> <br />
            typeOf(parse(&apos;(2+3)&apos;)) <br />
            {"//"} returns &apos;ParenthesisNode&apos; <br /> <br />
            typeOf(parse(&apos;1:10&apos;)) <br />
            {"//"} returns &apos;RangeNode&apos; <br /> <br />
            {"typeOf(parse('a<b<c'))"} <br />
            {"//"} returns &apos;RelationalNode&apos; <br /> <br />
            typeOf(parse(&apos;x&apos;)) <br />
            {"//"} returns &apos;SymbolNode&apos;
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "acos",
    text: (
      <div>
        <h1>Function acos</h1>

        <p>Calculate the inverse cosine of a value.</p>

        <p>
          To avoid confusion with the matrix arccosine, this function does not apply to matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>acos(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>The arc cosine of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            acos(0.5) <br />
            {"//"} returns number 1.0471975511965979 <br /> <br />
            acos(cos(1.5)) <br />
            {"//"} returns number 1.5 <br /> <br />
            acos(2) <br />
            {"//"} returns Complex 0 + 1.3169578969248166 i
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "acosh",
    text: (
      <div>
        <h1>Function acosh</h1>

        <p>
          Calculate the hyperbolic arccos of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">
            acosh(x) = ln(sqrt(x^2 - 1) + x)
          </code>
          .
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>acosh(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic arccosine of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            acosh(1.5) <br />
            {"//"} returns 0.9624236501192069
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "acot",
    text: (
      <div>
        <h1>Function acot </h1>

        <p>
          Calculate the inverse cotangent of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">acot(x) = atan(1/x)</code>.
        </p>

        <p>
          To avoid confusion with the matrix arccotanget, this function does not apply to matrices.
        </p>

        <h2 id="syntax">Syntax</h2>

        <pre className="highlight">
          <code>acot(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>The arc cotangent of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            acot(0.5) <br />
            {"//"} returns number 1.1071487177940904 <br /> <br />
            acot(2) <br />
            {"//"} returns number 0.4636476090008061 <br /> <br />
            acot(cot(1.5)) <br />
            {"//"} returns number 1.5
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "acoth",
    text: (
      <div>
        <h1>Function acoth</h1>

        <p>
          Calculate the hyperbolic arccotangent of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">
            acoth(x) = atanh(1/x) = (ln((x+1)/x) + ln(x/(x-1))) / 2
          </code>
          .
        </p>

        <p>
          To avoid confusion with the matrix hyperbolic arccotangent, this function does not apply
          to matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>acoth(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic arccotangent of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            acoth(0.5) <br />
            {"//"} returns 0.8047189562170503
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "acsc",
    text: (
      <div>
        <h1>Function acsc</h1>

        <p>
          Calculate the inverse cosecant of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">acsc(x) = asin(1/x)</code>.
        </p>

        <p>
          To avoid confusion with the matrix arccosecant, this function does not apply to matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>acsc(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>The arc cosecant of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            acsc(2) <br />
            {"//"} returns 0.5235987755982989 <br /> <br />
            acsc(0.5) <br />
            {"//"} returns Complex 1.5707963267948966 -1.3169578969248166i <br /> <br />
            acsc(csc(1.5)) <br />
            {"//"} returns number ~1.5
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "acsch",
    text: (
      <div>
        <h1>Function acsch</h1>

        <p>
          Calculate the hyperbolic arccosecant of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">
            acsch(x) = asinh(1/x) = ln(1/x + sqrt(1/x^2 + 1))
          </code>
          .
        </p>

        <p>
          To avoid confusion with the matrix hyperbolic arccosecant, this function does not apply to
          matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>acsch(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic arccosecant of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            acsch(0.5) <br />
            {"//"} returns 1.4436354751788103
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "asec",
    text: (
      <div>
        <h1>Function asec</h1>

        <p>
          Calculate the inverse secant of a value. Defined as{" "}
          <code className="language-plaintext highlighter-rouge">asec(x) = acos(1/x)</code>.
        </p>

        <p>
          To avoid confusion with the matrix arcsecant, this function does not apply to matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>asec(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>The arc secant of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            asec(2) <br />
            {"//"} returns 1.0471975511965979 <br /> <br />
            asec(sec(1.5)) <br />
            {"//"} returns 1.5 <br /> <br />
            asec(0.5) <br />
            {"//"} returns Complex 0 + 1.3169578969248166i
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "asech",
    text: (
      <div>
        <h1>Function asech</h1>

        <p>
          Calculate the hyperbolic arcsecant of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">
            asech(x) = acosh(1/x) = ln(sqrt(1/x^2 - 1) + 1/x)
          </code>
          .
        </p>

        <p>
          To avoid confusion with the matrix hyperbolic arcsecant, this function does not apply to
          matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>asech(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic arcsecant of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            asech(0.5) <br />
            {"//"} returns 1.3169578969248166
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "asin",
    text: (
      <div>
        <h1>Function asin</h1>

        <p>Calculate the inverse sine of a value.</p>

        <p>To avoid confusion with the matric arcsine, this function does not apply to matrices.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>asin(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>The arc sine of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            asin(0.5) <br />
            {"//"} returns number 0.5235987755982989 <br /> <br />
            asin(sin(1.5)) <br />
            {"//"} returns number ~1.5 <br /> <br />
            asin(2) <br />
            {"//"} returns Complex 1.5707963267948966 -1.3169578969248166i
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "asinh",
    text: (
      <div>
        <h1>Function asinh</h1>

        <p>
          Calculate the hyperbolic arcsine of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">
            asinh(x) = ln(x + sqrt(x^2 + 1))
          </code>
          .
        </p>

        <p>
          To avoid confusion with the matrix hyperbolic arcsine, this function does not apply to
          matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>asinh(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic arcsine of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            asinh(0.5) <br />
            {"//"} returns 0.48121182505960347
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "atan",
    text: (
      <div>
        <h1>Function atan</h1>

        <p>Calculate the inverse tangent of a value.</p>

        <p>To avoid confusion with matrix arctangent, this function does not apply to matrices.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>atan(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>The arc tangent of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            atan(0.5) <br />
            {"//"} returns number 0.4636476090008061 <br /> <br />
            atan(2) <br />
            {"//"} returns number 1.1071487177940904 <br /> <br />
            atan(tan(1.5)) <br />
            {"//"} returns number 1.5
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "atan2",
    text: (
      <div>
        <h1>Function atan2</h1>

        <p>
          Calculate the inverse tangent function with two arguments, y/x. By providing two
          arguments, the right quadrant of the computed angle can be determined.
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>atan2(y, x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | Array | Matrix</td>
              <td>Second dimension</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | Array | Matrix</td>
              <td>First dimension</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Array | Matrix</td>
              <td>Four-quadrant inverse tangent</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            atan2(2, 2) / pi <br />
            {"//"} returns number 0.25 <br /> <br />
            const angle = unit(60, &apos;deg&apos;) <br />
            {"//"} returns Unit 60 deg <br />
            const x = cos(angle) <br />
            const y = sin(angle) <br /> <br />
            atan(2) <br />
            {"//"} returns number 1.1071487177940904
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "atanh",
    text: (
      <div>
        <h1>Function atanh</h1>

        <p>
          Calculate the hyperbolic arctangent of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">
            atanh(x) = ln((1 + x)/(1 - x)) / 2
          </code>
          .
        </p>

        <p>
          To avoid confusion with the matrix hyperbolic arctangent, this function does not apply to
          matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>atanh(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic arctangent of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            atanh(0.5) <br />
            {"//"} returns 0.5493061443340549
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "cos",
    text: (
      <div>
        <h1>Function cos</h1>

        <p>Calculate the cosine of a value.</p>

        <p>To avoid confusion with the matrix cosine, this function does not apply to matrices.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>cos(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Cosine of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            cos(2) <br />
            {"//"} returns number -0.4161468365471422 <br /> <br />
            cos(pi / 4) <br />
            {"//"} returns number 0.7071067811865475 <br /> <br />
            cos(unit(180, &apos;deg&apos;)) <br />
            {"//"} returns number -1 <br /> <br />
            cos(unit(60, &apos;deg&apos;)) <br />
            {"//"} returns number 0.5 <br /> <br />
            const angle = 0.2 <br />
            pow(sin(angle), 2) + pow(cos(angle), 2) <br />
            {"//"} returns number ~1
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "cosh",
    text: (
      <div>
        <h1>Function cosh</h1>

        <p>
          Calculate the hyperbolic cosine of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">
            cosh(x) = 1/2 * (exp(x) + exp(-x))
          </code>
          .
        </p>

        <p>
          To avoid confusion with the matrix hyperbolic cosine, this function does not apply to
          matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>cosh(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic cosine of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            cosh(0.5) <br />
            {"//"} returns number 1.1276259652063807
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "cot",
    text: (
      <div>
        <h1>Function cot</h1>

        <p>
          Calculate the cotangent of a value. Defined as{" "}
          <code className="language-plaintext highlighter-rouge">cot(x) = 1 / tan(x)</code>.
        </p>

        <p>
          To avoid confusion with the matrix cotangent, this function does not apply to matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>cot(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | Complex | Unit | Array | Matrix</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Complex | Array | Matrix</td>
              <td>Cotangent of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            cot(2) <br />
            {"//"} returns number -0.45765755436028577 <br /> <br />
            1 / tan(2) <br />
            {"//"} returns number -0.45765755436028577
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "coth",
    text: (
      <div>
        <h1>Function coth</h1>

        <p>
          Calculate the hyperbolic cotangent of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">coth(x) = 1 / tanh(x)</code>.
        </p>

        <p>
          To avoid confusion with the matrix hyperbolic cotangent, this function does not apply to
          matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>coth(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic cotangent of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"//"} coth(x) = 1 / tanh(x) <br />
            coth(2) <br />
            {"//"} returns 1.0373147207275482 <br /> <br />
            1 / tanh(2) <br />
            {"//"} returns 1.0373147207275482
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "csc",
    text: (
      <div>
        <h1>Function csc</h1>

        <p>
          Calculate the cosecant of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">csc(x) = 1/sin(x)</code>.
        </p>

        <p>
          To avoid confusion with the matrix cosecant, this function does not apply to matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>csc(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Cosecant of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            csc(2) <br />
            {"//"} returns number 1.099750170294617 <br /> <br />
            1 / sin(2) <br />
            {"//"} returns number 1.099750170294617
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "csch",
    text: (
      <div>
        <h1>Function csch</h1>

        <p>
          Calculate the hyperbolic cosecant of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">csch(x) = 1 / sinh(x)</code>.
        </p>

        <p>
          To avoid confusion with the matrix hyperbolic cosecant, this function does not apply to
          matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>csch(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic cosecant of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"//"} csch(x) = 1/ sinh(x) <br />
            csch(0.5) <br />
            {"//"} returns 1.9190347513349437 <br /> <br />
            1 / sinh(0.5) <br />
            {"//"} returns 1.9190347513349437
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "sec",
    text: (
      <div>
        <h1>Function sec</h1>

        <p>
          Calculate the secant of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">sec(x) = 1/cos(x)</code>.
        </p>

        <p>To avoid confusion with the matrix secant, this function does not apply to matrices.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>sec(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Secant of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            sec(2) <br />
            {"//"} returns number -2.4029979617223822 <br /> <br />
            1 / cos(2) <br />
            {"//"} returns number -2.4029979617223822
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "sech",
    text: (
      <div>
        <h1>Function sech</h1>

        <p>
          Calculate the hyperbolic secant of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">sech(x) = 1 / cosh(x)</code>.
        </p>

        <p>
          To avoid confusion with the matrix hyperbolic secant, this function does not apply to
          matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>sech(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic secant of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"//"} sech(x) = 1/ cosh(x) <br />
            sech(0.5) <br />
            {"//"} returns 0.886818883970074 <br /> <br />
            1 / cosh(0.5) <br />
            {"//"} returns 0.886818883970074
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "sin",
    text: (
      <div>
        <h1>Function sin</h1>

        <p>Calculate the sine of a value.</p>

        <p>To avoid confusion with the matrix sine, this function does not apply to matrices.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>sin(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Sine of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            sin(2) <br />
            {"//"} returns number 0.9092974268256813 <br /> <br />
            sin(pi / 4) <br />
            {"//"} returns number 0.7071067811865475 <br /> <br />
            sin(unit(90, &apos;deg&apos;)) <br />
            {"//"} returns number 1 <br /> <br />
            sin(unit(30, &apos;deg&apos;)) <br />
            {"//"} returns number 0.5 <br /> <br />
            const angle = 0.2 <br />
            pow(sin(angle), 2) + pow(cos(angle), 2) <br />
            {"//"} returns number ~1 <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "sinh",
    text: (
      <div>
        <h1>Function sinh</h1>

        <p>
          Calculate the hyperbolic sine of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">
            sinh(x) = 1/2 * (exp(x) - exp(-x))
          </code>
          .
        </p>

        <p>
          To avoid confusion with the matrix hyperbolic sine, this function does not apply to
          matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>sinh(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic sine of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            sinh(0.5) <br />
            {"//"} returns number 0.5210953054937474
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "tan",
    text: (
      <div>
        <h1>Function tan</h1>

        <p>
          Calculate the tangent of a value.{" "}
          <code className="language-plaintext highlighter-rouge">tan(x)</code> is equal to{" "}
          <code className="language-plaintext highlighter-rouge">sin(x) / cos(x)</code>.
        </p>

        <p>To avoid confusion with the matrix tangent, this function does not apply to matrices.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>tan(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex | Unit</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Tangent of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            tan(0.5) <br />
            {"//"} returns number 0.5463024898437905 <br /> <br />
            sin(0.5) / cos(0.5) <br />
            {"//"} returns number 0.5463024898437905 <br /> <br />
            tan(pi / 4) <br />
            {"//"} returns number 1 <br /> <br />
            tan(unit(45, &apos;deg&apos;)) <br />
            {"//"} returns number 1
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "tanh",
    text: (
      <div>
        <h1>Function tanh</h1>

        <p>
          Calculate the hyperbolic tangent of a value, defined as{" "}
          <code className="language-plaintext highlighter-rouge">
            tanh(x) = (exp(2 * x) - 1) / (exp(2 * x) + 1)
          </code>
          .
        </p>

        <p>
          To avoid confusion with matrix hyperbolic tangent, this function does not apply to
          matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>tanh(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>Function input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>Hyperbolic tangent of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"//"} tanh(x) = sinh(x) / cosh(x) = 1 / coth(x) <br />
            tanh(0.5) <br />
            {"//"} returns 0.46211715726000974 <br /> <br />
            sinh(0.5) / cosh(0.5) <br />
            {"//"} returns 0.46211715726000974 <br /> <br />
            1 / coth(0.5) <br />
            {"//"} returns 0.46211715726000974
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "setCartesian",
    text: (
      <div>
        <h1>Function setCartesian</h1>

        <p>
          Create the cartesian product of two (multi)sets. Multi-dimension arrays will be converted
          to single-dimension arrays and the values will be sorted in ascending order before the
          operation.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>setCartesian(set1, set2)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a1</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a2</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>The cartesian product of two (multi)sets</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            setCartesian([1, 2], [3, 4]) <br />
            {"//"} returns [[1, 3], [1, 4], [2, 3], [2, 4]] <br /> <br />
            setCartesian([4, 3], [2, 1]) <br />
            {"//"} returns [[3, 1], [3, 2], [4, 1], [4, 2]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "setDifference",
    text: (
      <div>
        <h1>Function setDifference</h1>

        <p>
          Create the difference of two (multi)sets: every element of set1, that is not the element
          of set2. Multi-dimension arrays will be converted to single-dimension arrays before the
          operation.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>setDifference(set1, set2)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a1</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a2</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>The difference of two (multi)sets</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            setDifference([1, 2, 3, 4], [3, 4, 5, 6]) <br />
            {"//"} returns [1, 2] <br /> <br />
            setDifference([[1, 2], [3, 4]], [[3, 4], [5, 6]]) <br />
            {"//"} returns [1, 2]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "setDistinct",
    text: (
      <div>
        <h1>Function setDistinct</h1>

        <p>
          Collect the distinct elements of a multiset. A multi-dimension array will be converted to
          a single-dimension array before the operation.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>setDistinct(set)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a</code>
              </td>
              <td>Array | Matrix</td>
              <td>A multiset</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>A set containing the distinc elements of the multiset</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            setDistinct([1, 1, 1, 2, 2, 3]) <br />
            {"//"} returns [1, 2, 3]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "setIntersect",
    text: (
      <div>
        <h1>Function setIntersect</h1>

        <p>
          Create the intersection of two (multi)sets. Multi-dimension arrays will be converted to
          single-dimension arrays before the operation.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>setIntersect(set1, set2)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a1</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a2</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>The intersection of two (multi)sets</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            setIntersect([1, 2, 3, 4], [3, 4, 5, 6]) <br />
            {"//"} returns [3, 4] <br /> <br />
            setIntersect([[1, 2], [3, 4]], [[3, 4], [5, 6]]) <br />
            {"//"} returns [3, 4]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "setIsSubset",
    text: (
      <div>
        <h1>Function setIsSubset</h1>

        <p>
          Check whether a (multi)set is a subset of another (multi)set. (Every element of set1 is
          the element of set2.) Multi-dimension arrays will be converted to single-dimension arrays
          before the operation.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>setIsSubset(set1, set2)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a1</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a2</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean</td>
              <td>Returns true when a1 is a subset of a2, returns false otherwise</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            setIsSubset([1, 2], [3, 4, 5, 6]) <br />
            {"//"} returns false <br /> <br />
            setIsSubset([3, 4], [3, 4, 5, 6]) <br />
            {"//"} returns true
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "setMultiplicity",
    text: (
      <div>
        <h1>Function setMultiplicity</h1>

        <p>
          Count the multiplicity of an element in a multiset. A multi-dimension array will be
          converted to a single-dimension array before the operation.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>setMultiplicity(element, set)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">e</code>
              </td>
              <td>number | BigNumber | Fraction | Complex</td>
              <td>An element in the multiset</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a</code>
              </td>
              <td>Array | Matrix</td>
              <td>A multiset</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number</td>
              <td>The number of how many times the multiset contains the element</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            setMultiplicity(1, [1, 2, 2, 4]) <br />
            {"//"} returns 1 <br /> <br />
            setMultiplicity(2, [1, 2, 2, 4]) <br />
            {"//"} returns 2
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "setPowerset",
    text: (
      <div>
        <h1>Function setPowerset</h1>

        <p>
          Create the powerset of a (multi)set. (The powerset contains very possible subsets of a
          (multi)set.) A multi-dimension array will be converted to a single-dimension array before
          the operation.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>setPowerset(set)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array</td>
              <td>The powerset of the (multi)set</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            setPowerset([1, 2, 3]) <br />
            {"//"} returns [[], [1], [2], [3], [1, 2], <br />
            {"//"} [1, 3], [2, 3], [1, 2, 3]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "setSize",
    text: (
      <div>
        <h1>Function setSize</h1>

        <p>
          Count the number of elements of a (multi)set. When a second parameter is ‘true’, count
          only the unique values. A multi-dimension array will be converted to a single-dimension
          array before the operation.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            setSize(set) <br />
            setSize(set, unique)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a</code>
              </td>
              <td>Array | Matrix</td>
              <td>A multiset</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">unique</code>
              </td>
              <td>boolean</td>
              <td>If true, only the unique values are counted. False by default</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number</td>
              <td>The number of elements of the (multi)set</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            setSize([1, 2, 2, 4]) <br />
            {"//"} returns 4 <br /> <br />
            setSize([1, 2, 2, 4], true) <br />
            {"//"} returns 3
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "setSymDifference",
    text: (
      <div>
        <h1>Function setSymDifference</h1>

        <p>
          Create the symmetric difference of two (multi)sets. Multi-dimension arrays will be
          converted to single-dimension arrays before the operation.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>setSymDifference(set1, set2)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a1</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a2</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>The symmetric difference of two (multi)sets</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            setSymDifference([1, 2, 3, 4], [3, 4, 5, 6]) <br />
            {"//"} returns [1, 2, 5, 6] <br /> <br />
            setSymDifference([[1, 2], [3, 4]], [[3, 4], [5, 6]]) <br />
            {"//"} returns [1, 2, 5, 6]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "setUnion",
    text: (
      <div>
        <h1>Function setUnion</h1>

        <p>
          Create the union of two (multi)sets. Multi-dimension arrays will be converted to
          single-dimension arrays before the operation.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>setUnion(set1, set2)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a1</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a2</code>
              </td>
              <td>Array | Matrix</td>
              <td>A (multi)set</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>The union of two (multi)sets</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            setUnion([1, 2, 3, 4], [3, 4, 5, 6]) <br />
            {"//"} returns [1, 2, 3, 4, 5, 6] <br /> <br />
            setUnion([[1, 2], [3, 4]], [[3, 4], [5, 6]]) <br />
            {"//"} returns [1, 2, 3, 4, 5, 6]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "compare",
    text: (
      <div>
        <h1>Function compare</h1>

        <p>Compare two values. Returns 1 when x &gt; y, -1 when x &lt; y, and 0 when x == y.</p>

        <p>
          x and y are considered equal when the relative difference between x and y is smaller than
          the configured epsilon. The function cannot be used to compare values smaller than
          approximately 2.22e-16.
        </p>

        <p>
          For matrices, the function is evaluated element wise. Strings are compared by their
          numerical value.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>compare(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Unit | string | Array | Matrix</td>
              <td>First value to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | Unit | string | Array | Matrix</td>
              <td>Second value to compare</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Array | Matrix</td>
              <td>
                Returns the result of the comparison: 1 when x &gt; y, -1 when x &lt; y, and 0 when
                x == y.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            compare(6, 1) <br />
            {"//"} returns 1 <br /> <br />
            compare(2, 3) <br />
            {"//"} returns -1 <br /> <br />
            compare(7, 7) <br />
            {"//"} returns 0 <br /> <br />
            compare(&apos;10&apos;, &apos;2&apos;) <br />
            {"//"} returns 1 <br /> <br />
            compare(&apos;1000&apos;, &apos;1e3&apos;) <br />
            {"//"} returns 0 <br /> <br />
            const a = unit(&apos;5 cm&apos;) <br />
            const b = unit(&apos;40 mm&apos;) <br />
            compare(a, b) <br />
            {"//"} returns 1 <br /> <br />
            compare(2, [1, 2, 3]) <br />
            {"//"} returns [1, 0, -1]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "compareNatural",
    text: (
      <div>
        <h1>Function compareNatural</h1>

        <p>Compare two values of any type in a deterministic, natural way.</p>

        <p>
          For numeric values, the function works the same as{" "}
          <code className="language-plaintext highlighter-rouge">compare</code>. For types of values
          that can’t be compared mathematically, the function compares in a natural way.
        </p>

        <p>
          For numeric values, x and y are considered equal when the relative difference between x
          and y is smaller than the configured epsilon. The function cannot be used to compare
          values smaller than approximately 2.22e-16.
        </p>

        <p>
          For Complex numbers, first the real parts are compared. If equal, the imaginary parts are
          compared.
        </p>

        <p>
          Strings are compared with a natural sorting algorithm, which orders strings in a “logic”
          way following some heuristics. This differs from the function{" "}
          <code className="language-plaintext highlighter-rouge">compare</code>, which converts the
          string into a numeric value and compares that. The function{" "}
          <code className="language-plaintext highlighter-rouge">compareText</code>
          on the other hand compares text lexically.
        </p>

        <p>
          Arrays and Matrices are compared value by value until there is an unequal pair of values
          encountered. Objects are compared by sorted keys until the keys or their values are
          unequal.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>compareNatural(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>*</td>
              <td>First value to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>*</td>
              <td>Second value to compare</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number</td>
              <td>
                Returns the result of the comparison: 1 when x &gt; y, -1 when x &lt; y, and 0 when
                x == y.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            compareNatural(6, 1) <br />
            {"//"} returns 1 <br /> <br />
            compareNatural(2, 3) <br />
            {"//"} returns -1 <br /> <br />
            compareNatural(7, 7) <br />
            {"//"} returns 0 <br /> <br />
            compareNatural(&apos;10&apos;, &apos;2&apos;) <br />
            {"//"} returns 1 <br /> <br />
            compareText(&apos;10&apos;, &apos;2&apos;) <br />
            {"//"} returns -1 <br /> <br />
            compare(&apos;10&apos;, &apos;2&apos;) <br />
            {"//"} returns 1 <br /> <br />
            compareNatural(&apos;Answer: 10&apos;, &apos;Answer: 2&apos;) <br />
            {"//"} returns 1 <br /> <br />
            compareText(&apos;Answer: 10&apos;, &apos;Answer: 2&apos;) <br />
            {"//"} returns -1 <br /> <br />
            compare(&apos;Answer: 10&apos;, &apos;Answer: 2&apos;) <br />
            {"//"} Error: Cannot convert &quot;Answer: 10&quot; to a number <br /> <br />
            const a = unit(&apos;5 cm&apos;) <br />
            const b = unit(&apos;40 mm&apos;) <br />
            compareNatural(a, b) <br />
            {"//"} returns 1 <br /> <br />
            const c = complex(&apos;2 + 3i&apos;) <br />
            const d = complex(&apos;2 + 4i&apos;) <br />
            compareNatural(c, d) <br />
            {"//"} returns -1 <br /> <br />
            compareNatural([1, 2, 4], [1, 2, 3]) <br />
            {"//"} returns 1 <br /> <br />
            compareNatural([1, 2, 3], [1, 2]) <br />
            {"//"} returns 1 <br /> <br />
            compareNatural([1, 5], [1, 2, 3]) <br />
            {"//"} returns 1 <br /> <br />
            compareNatural([1, 2], [1, 2]) <br />
            {"//"} returns 0 <br /> <br />
            {"compareNatural({a: 2}, {a: 4})"} <br />
            {"//"} returns -1
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "compareText",
    text: (
      <div>
        <h1>Function compareText</h1>

        <p>
          Compare two strings lexically. Comparison is case sensitive. Returns 1 when x &gt; y, -1
          when x &lt; y, and 0 when x == y.
        </p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>compareText(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>string | Array | DenseMatrix</td>
              <td>First string to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>string | Array | DenseMatrix</td>
              <td>Second string to compare</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Array | DenseMatrix</td>
              <td>
                Returns the result of the comparison: 1 when x &gt; y, -1 when x &lt; y, and 0 when
                x == y.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            compareText(&apos;B&apos;, &apos;A&apos;) <br />
            {"//"} returns 1 <br /> <br />
            compareText(&apos;2&apos;, &apos;10&apos;) <br />
            {"//"} returns 1 <br /> <br />
            compare(&apos;2&apos;, &apos;10&apos;) <br />
            {"//"} returns -1 <br /> <br />
            compareNatural(&apos;2&apos;, &apos;10&apos;) <br />
            {"//"} returns -1 <br /> <br />
            compareText(&apos;B&apos;, [&apos;A&apos;, &apos;B&apos;, &apos;C&apos;]) <br />
            {"//"} returns [1, 0, -1]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "deepEqual",
    text: (
      <div>
        <h1>Function deepEqual</h1>

        <p>
          Test element wise whether two matrices are equal. The function accepts both matrices and
          scalar values.
        </p>

        <p>Strings are compared by their numerical value.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>deepEqual(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>First matrix to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>Second matrix to compare</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Fraction | Complex | Unit | Array | Matrix</td>
              <td>
                Returns true when the input matrices have the same size and each of their elements
                is equal.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            deepEqual(2, 4) <br />
            {"//"} returns false <br /> <br />
            a = [2, 5, 1] <br />
            b = [2, 7, 1] <br />
            deepEqual(a, b) <br />
            {"//"} returns false <br /> <br />
            equal(a, b) <br />
            {"//"} returns [true, false, true]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "equal",
    text: (
      <div>
        <h1>Function equal</h1>

        <p>Test whether two values are equal.</p>

        <p>
          The function tests whether the relative difference between x and y is smaller than the
          configured epsilon. The function cannot be used to compare values smaller than
          approximately 2.22e-16.
        </p>

        <p>
          For matrices, the function is evaluated element wise. In case of complex numbers, x.re
          must equal y.re, and x.im must equal y.im.
        </p>

        <p>
          Values <code className="language-plaintext highlighter-rouge">null</code> and{" "}
          <code className="language-plaintext highlighter-rouge">undefined</code> are compared
          strictly, thus <code className="language-plaintext highlighter-rouge">null</code> is only
          equal to <code className="language-plaintext highlighter-rouge">null</code> and nothing
          else, and <code className="language-plaintext highlighter-rouge">undefined</code> is only
          equal to
          <code className="language-plaintext highlighter-rouge">undefined</code> and nothing else.
          Strings are compared by their numerical value.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>equal(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | boolean | Complex | Unit | string | Array | Matrix</td>
              <td>First value to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | boolean | Complex | Unit | string | Array | Matrix</td>
              <td>Second value to compare</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean | Array | Matrix</td>
              <td>Returns true when the compared values are equal, else returns false</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            equal(2 + 2, 3) <br />
            {"//"} returns false <br /> <br />
            equal(2 + 2, 4) <br />
            {"//"} returns true <br /> <br />
            const a = unit(&apos;50 cm&apos;) <br />
            const b = unit(&apos;5 m&apos;) <br />
            equal(a, b) <br />
            {"//"} returns true <br /> <br />
            const c = [2, 5, 1] <br />
            const d = [2, 7, 1] <br />
            equal(c, d) <br />
            {"//"} returns [true, false, true] <br /> <br />
            deepEqual(c, d) <br />
            {"//"} returns false <br /> <br />
            equal(&quot;1000&quot;, &quot;1e3&quot;) <br />
            {"//"} returns true <br /> <br />
            equal(0, null) <br />
            {"//"} returns false
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "equalText",
    text: (
      <div>
        <h1>Function equalText</h1>

        <p>Check equality of two strings. Comparison is case sensitive.</p>

        <p>For matrices, the function is evaluated element wise.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>equalText(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>string | Array | DenseMatrix</td>
              <td>First string to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>string | Array | DenseMatrix</td>
              <td>Second string to compare</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Array | DenseMatrix</td>
              <td>Returns true if the values are equal, and false if not.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            equalText(&apos;Hello&apos;, &apos;Hello&apos;) <br />
            {"//"} returns true <br /> <br />
            equalText(&apos;a&apos;, &apos;A&apos;) <br />
            {"//"} returns false <br /> <br />
            equal(&apos;2e3&apos;, &apos;2000&apos;) <br />
            {"//"} returns true <br /> <br />
            equalText(&apos;2e3&apos;, &apos;2000&apos;) <br />
            {"//"} returns false <br /> <br />
            equalText(&apos;B&apos;, [&apos;A&apos;, &apos;B&apos;, &apos;C&apos;]) <br />
            {"//"} returns [false, true, false]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "larger",
    text: (
      <div>
        <h1>Function larger</h1>

        <p>Test whether value x is larger than y.</p>

        <p>
          The function returns true when x is larger than y and the relative difference between x
          and y is larger than the configured epsilon. The function cannot be used to compare values
          smaller than approximately 2.22e-16.
        </p>

        <p>
          For matrices, the function is evaluated element wise. Strings are compared by their
          numerical value.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>larger(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | boolean | Unit | string | Array | Matrix</td>
              <td>First value to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | boolean | Unit | string | Array | Matrix</td>
              <td>Second value to compare</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean | Array | Matrix</td>
              <td>Returns true when the x is larger than y, else returns false</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            larger(2, 3) <br />
            {"//"} returns false <br /> <br />
            larger(5, 2 + 2) <br />
            {"//"} returns true <br /> <br />
            const a = unit(&apos;5 cm&apos;) <br />
            const b = unit(&apos;2 inch&apos;) <br />
            larger(a, b) <br />
            {"//"} returns false
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "largerEq",
    text: (
      <div>
        <h1>Function largerEq</h1>

        <p>Test whether value x is larger or equal to y.</p>

        <p>
          The function returns true when x is larger than y or the relative difference between x and
          y is smaller than the configured epsilon. The function cannot be used to compare values
          smaller than approximately 2.22e-16.
        </p>

        <p>
          For matrices, the function is evaluated element wise. Strings are compared by their
          numerical value.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>largerEq(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | boolean | Unit | string | Array | Matrix</td>
              <td>First value to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | boolean | Unit | string | Array | Matrix</td>
              <td>Second value to compare</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean | Array | Matrix</td>
              <td>Returns true when the x is larger or equal to y, else returns false</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            larger(2, 1 + 1) <br />
            {"//"} returns false <br /> <br />
            largerEq(2, 1 + 1) <br />
            {"//"} returns true
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "smaller",
    text: (
      <div>
        <h1>Function smaller</h1>

        <p>Test whether value x is smaller than y.</p>

        <p>
          The function returns true when x is smaller than y and the relative difference between x
          and y is smaller than the configured epsilon. The function cannot be used to compare
          values smaller than approximately 2.22e-16.
        </p>

        <p>
          For matrices, the function is evaluated element wise. Strings are compared by their
          numerical value.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>smaller(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | boolean | Unit | string | Array | Matrix</td>
              <td>First value to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | boolean | Unit | string | Array | Matrix</td>
              <td>Second value to compare</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean | Array | Matrix</td>
              <td>Returns true when the x is smaller than y, else returns false</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            smaller(2, 3) <br />
            {"//"} returns true <br /> <br />
            smaller(5, 2 * 2) <br />
            {"//"} returns false <br /> <br />
            const a = unit(&apos;5 cm&apos;) <br />
            const b = unit(&apos;2 inch&apos;) <br />
            smaller(a, b) <br />
            {"//"} returns true
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "smallerEq",
    text: (
      <div>
        <h1>Function smallerEq</h1>

        <p>Test whether value x is smaller or equal to y.</p>

        <p>
          The function returns true when x is smaller than y or the relative difference between x
          and y is smaller than the configured epsilon. The function cannot be used to compare
          values smaller than approximately 2.22e-16.
        </p>

        <p>
          For matrices, the function is evaluated element wise. Strings are compared by their
          numerical value.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>smallerEq(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | BigNumber | Fraction | boolean | Unit | string | Array | Matrix</td>
              <td>First value to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>number | BigNumber | Fraction | boolean | Unit | string | Array | Matrix</td>
              <td>Second value to compare</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean | Array | Matrix</td>
              <td>Returns true when the x is smaller than y, else returns false</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            smaller(1 + 2, 3) <br />
            {"//"} returns false <br /> <br />
            smallerEq(1 + 2, 3) <br />
            {"//"} returns true
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "unequal",
    text: (
      <div>
        <h1>Function unequal</h1>

        <p>Test whether two values are unequal.</p>

        <p>
          The function tests whether the relative difference between x and y is larger than the
          configured epsilon. The function cannot be used to compare values smaller than
          approximately 2.22e-16.
        </p>

        <p>
          For matrices, the function is evaluated element wise. In case of complex numbers, x.re
          must unequal y.re, or x.im must unequal y.im. Strings are compared by their numerical
          value.
        </p>

        <p>
          Values <code className="language-plaintext highlighter-rouge">null</code> and{" "}
          <code className="language-plaintext highlighter-rouge">undefined</code> are compared
          strictly, thus <code className="language-plaintext highlighter-rouge">null</code> is
          unequal with everything except{" "}
          <code className="language-plaintext highlighter-rouge">null</code>, and{" "}
          <code className="language-plaintext highlighter-rouge">undefined</code> is unequal with
          everything except <code className="language-plaintext highlighter-rouge">undefined</code>.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>unequal(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>
                number | BigNumber | Fraction | boolean | Complex | Unit | string | Array | Matrix |
                undefined
              </td>
              <td>First value to compare</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>
                number | BigNumber | Fraction | boolean | Complex | Unit | string | Array | Matrix |
                undefined
              </td>
              <td>Second value to compare</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boolean | Array | Matrix</td>
              <td>Returns true when the compared values are unequal, else returns false</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            unequal(2 + 2, 3) <br />
            {"//"} returns true <br /> <br />
            unequal(2 + 2, 4) <br />
            {"//"} returns false <br /> <br />
            const a = unit(&apos;50 cm&apos;) <br />
            const b = unit(&apos;5 m&apos;) <br />
            unequal(a, b) <br />
            {"//"} returns false <br /> <br />
            const c = [2, 5, 1] <br />
            const d = [2, 7, 1] <br />
            unequal(c, d) <br />
            {"//"} returns [false, true, false] <br /> <br />
            deepEqual(c, d) <br />
            {"//"} returns false <br /> <br />
            unequal(0, null) <br />
            {"//"} returns true
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "combinations",
    text: (
      <div>
        <h1>Function combinations</h1>

        <p>
          Compute the number of ways of picking{" "}
          <code className="language-plaintext highlighter-rouge">k</code> unordered outcomes from{" "}
          <code className="language-plaintext highlighter-rouge">n</code>
          possibilities.
        </p>

        <p>
          Combinations only takes integer arguments. The following condition must be enforced: k
          &lt;= n.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>combinations(n, k)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>number | BigNumber</td>
              <td>Total number of objects in the set</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">k</code>
              </td>
              <td>number | BigNumber</td>
              <td>Number of objects in the subset</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber</td>
              <td>Number of possible combinations.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            combinations(7, 5) <br />
            {"//"} returns 21
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "combinationsWithRep",
    text: (
      <div>
        <h1>Function combinationsWithRep</h1>

        <p>
          Compute the number of ways of picking{" "}
          <code className="language-plaintext highlighter-rouge">k</code> unordered outcomes from{" "}
          <code className="language-plaintext highlighter-rouge">n</code>
          possibilities, allowing individual outcomes to be repeated more than once.
        </p>

        <p>
          CombinationsWithRep only takes integer arguments. The following condition must be
          enforced: k &lt;= n + k -1.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>combinationsWithRep(n, k)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>number | BigNumber</td>
              <td>Total number of objects in the set</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">k</code>
              </td>
              <td>number | BigNumber</td>
              <td>Number of objects in the subset</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber</td>
              <td>Number of possible combinations with replacement.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            combinationsWithRep(7, 5) <br />
            {"//"} returns 462
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "factorial",
    text: (
      <div>
        <h1>Function factorial</h1>

        <p>Compute the factorial of a value</p>

        <p>
          Factorial only supports an integer value as argument. For matrices, the function is
          evaluated element wise.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>factorial(n)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>number | BigNumber | Array | Matrix</td>
              <td>An integer number</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Array | Matrix</td>
              <td>
                The factorial of <code className="language-plaintext highlighter-rouge">n</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            factorial(5) <br />
            {"//"} returns 120 <br /> <br />
            factorial(3) <br />
            {"//"} returns 6
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "gamma",
    text: (
      <div>
        <h1>Function gamma</h1>

        <p>
          Compute the gamma function of a value using Lanczos approximation for small values, and an
          extended Stirling approximation for large values.
        </p>

        <p>
          To avoid confusion with the matrix Gamma function, this function does not apply to
          matrices.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>gamma(n)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>number | BigNumber | Complex</td>
              <td>A real or complex number</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber | Complex</td>
              <td>
                The gamma of <code className="language-plaintext highlighter-rouge">n</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            gamma(5) <br />
            {"//"} returns 24 <br /> <br />
            gamma(-0.5) <br />
            {"//"} returns -3.5449077018110335 <br /> <br />
            gamma(i) <br />
            {"//"} returns -0.15494982830180973 - 0.49801566811835596i
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "kldivergence",
    text: (
      <div>
        <h1>Function kldivergence</h1>

        <p>Calculate the Kullback-Leibler (KL) divergence between two distributions</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>kldivergence(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">q</code>
              </td>
              <td>Array | Matrix</td>
              <td>First vector</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">p</code>
              </td>
              <td>Array | Matrix</td>
              <td>Second vector</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number</td>
              <td>Returns distance between q and p</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            kldivergence([0.7,0.5,0.4], [0.2,0.9,0.5]) <br />
            {"//"} returns 0.24376698773121153
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "lgamma",
    text: (
      <div>
        <h1>Function lgamma</h1>

        <p>
          Logarithm of the gamma function for real, positive numbers and complex numbers, using
          Lanczos approximation for numbers and Stirling series for complex numbers.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>lgamma(n)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>number | Complex</td>
              <td>A real or complex number</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Complex</td>
              <td>
                The log gamma of <code className="language-plaintext highlighter-rouge">n</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            lgamma(5) <br />
            {"//"} returns 3.178053830347945 <br /> <br />
            lgamma(0) <br />
            {"//"} returns Infinity <br /> <br />
            lgamma(-0.5) <br />
            {"//"} returns NaN <br /> <br />
            lgamma(i) <br />
            {"//"} returns -0.6509231993018536 - 1.8724366472624294i
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "multinomial",
    text: (
      <div>
        <h1>Function multinomial</h1>

        <p>
          Multinomial Coefficients compute the number of ways of picking a1, a2, …, ai unordered
          outcomes from <code className="language-plaintext highlighter-rouge">n</code>{" "}
          possibilities.
        </p>

        <p>
          multinomial takes one array of integers as an argument. The following condition must be
          enforced: every ai &lt;= 0
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            multinomial(a) <br />
            {"//"} a is an array type
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">a</code>
              </td>
              <td>number[] | BigNumber[]</td>
              <td>Integer numbers of objects in the subset</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Number | BigNumber</td>
              <td>Multinomial coefficient.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            multinomial([1,2,1]) <br />
            {"//"} returns 12
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "permutations",
    text: (
      <div>
        <h1>Function permutations</h1>

        <p>
          Compute the number of ways of obtaining an ordered subset of{" "}
          <code className="language-plaintext highlighter-rouge">k</code> elements from a set of{" "}
          <code className="language-plaintext highlighter-rouge">n</code> elements.
        </p>

        <p>
          Permutations only takes integer arguments. The following condition must be enforced: k
          &lt;= n.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            permutations(n) <br />
            permutations(n, k)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">n</code>
              </td>
              <td>number | BigNumber</td>
              <td>The number of objects in total</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">k</code>
              </td>
              <td>number | BigNumber</td>
              <td>The number of objects in the subset</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | BigNumber</td>
              <td>The number of permutations</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            permutations(5) <br />
            {"//"} 120 <br /> <br />
            permutations(5, 3) <br />
            {"//"} 60
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "pickRandom",
    text: (
      <div>
        <h1>Function pickRandom</h1>

        <p>
          Random pick one or more values from a one dimensional array. Array elements are picked
          using a random function with uniform or weighted distribution.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            pickRandom(array) <br />
            pickRandom(array, number) <br />
            pickRandom(array, weights) <br />
            pickRandom(array, number, weights) <br />
            pickRandom(array, weights, number) <br />
            pickRandom(array, {"{ weights, number, elementWise })"}
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">array</code>
              </td>
              <td>Array | Matrix</td>
              <td>A one dimensional array</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">number</code>
              </td>
              <td>Int</td>
              <td>An int or float</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">weights</code>
              </td>
              <td>Array | Matrix</td>
              <td>An array of ints or floats</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Array</td>
              <td>
                Returns a single random value from array when number is 1 or undefined. Returns an
                array with the configured number of elements when number is &gt; 1.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            pickRandom([3, 6, 12, 2]) <br />
            {"//"} returns one of the values in the array <br /> <br />
            pickRandom([3, 6, 12, 2], 2) <br />
            {"//"} returns an array of two of the values in the array <br /> <br />
            pickRandom([3, 6, 12, 2], {"{ number: 2 })"} <br />
            {"//"} returns an array of two of the values in the array <br /> <br />
            pickRandom([3, 6, 12, 2], [1, 3, 2, 1]) <br />
            {"//"} returns one of the values in <br />
            {"//"} the array with weighteddistribution <br /> <br />
            pickRandom([3, 6, 12, 2], 2, [1, 3, 2, 1]) <br />
            {"//"} returns an array of two of the values in <br />
            {"//"} the array with weighted distribution <br /> <br />
            pickRandom([3, 6, 12, 2], [1, 3, 2, 1], 2) <br />
            {"//"} returns an array of two of the values in <br />
            {"//"} the array with weighted distribution <br /> <br />
            pickRandom{"([{x: 1.0, y: 2.0}, {x: 1.1, y: 2.0}],"} <br />
            {"    "}
            {"{ elementWise: false })"}
            <br />
            {"//"} returns one of the items in the array
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "random",
    text: (
      <div>
        <h1>Function random</h1>

        <p>
          Return a random number larger or equal to{" "}
          <code className="language-plaintext highlighter-rouge">min</code> and smaller than{" "}
          <code className="language-plaintext highlighter-rouge">max</code>
          using a uniform distribution.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            random() <br />
            {"//"} generate a random number between 0 and 1 <br /> <br />
            random(max) <br />
            {"//"} generate a random number between 0 and max <br /> <br />
            random(min, max) <br />
            {"//"} generate a random number between min and max <br /> <br />
            random(size) <br />
            {"//"} generate a matrix with random numbers between 0 and 1 <br /> <br />
            random(size, max) <br />
            {"//"} generate a matrix with random numbers between 0 and max <br /> <br />
            random(size, min, max) <br />
            {"//"} generate a matrix with random numbers between min and max
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">size</code>
              </td>
              <td>Array | Matrix</td>
              <td>
                If provided, an array or matrix with given size and filled with random values is
                returned
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">min</code>
              </td>
              <td>number</td>
              <td>Minimum boundary for the random value, included</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">max</code>
              </td>
              <td>number</td>
              <td>Maximum boundary for the random value, excluded</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Array | Matrix</td>
              <td>A random number</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            random() <br />
            {"//"} returns a random number between 0 and 1 <br /> <br />
            random(100) <br />
            {"//"} returns a random number between 0 and 100 <br /> <br />
            random(30, 40) <br />
            {"//"} returns a random number between 30 and 40 <br /> <br />
            random([2, 3]) <br />
            {"//"} returns a 2x3 matrix with random numbers between 0 and 1
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "randomInt",
    text: (
      <div>
        <h1>Function randomInt</h1>

        <p>
          Return a random integer number larger or equal to{" "}
          <code className="language-plaintext highlighter-rouge">min</code> and smaller than{" "}
          <code className="language-plaintext highlighter-rouge">max</code>
          using a uniform distribution.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            randomInt() <br />
            {"//"} generate a random integer between 0 and 1 <br /> <br />
            randomInt(max) <br />
            {"//"} generate a random integer between 0 and max <br /> <br />
            randomInt(min, max) <br />
            {"//"} generate a random integer between min and max <br /> <br />
            randomInt(size) <br />
            {"//"} generate a matrix with random integer between 0 and 1 <br />
            <br />
            randomInt(size, max) <br />
            {"//"} generate a matrix with random integer between 0 and max
            <br /> <br />
            randomInt(size, min, max) <br />
            {"//"} generate a matrix with random integer between min and max
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">size</code>
              </td>
              <td>Array | Matrix</td>
              <td>
                If provided, an array or matrix with given size and filled with random values is
                returned
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">min</code>
              </td>
              <td>number</td>
              <td>Minimum boundary for the random value, included</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">max</code>
              </td>
              <td>number</td>
              <td>Maximum boundary for the random value, excluded</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Array | Matrix</td>
              <td>A random integer value</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            randomInt(100) <br />
            {"//"} returns a random integer between 0 and 100 <br /> <br />
            randomInt(30, 40) <br />
            {"//"} returns a random integer between 30 and 40 <br /> <br />
            randomInt([2, 3]) <br />
            {"//"} returns a 2x3 matrix with random integers between 0 and 1
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "apply",
    text: (
      <div>
        <h1>Function apply</h1>

        <p>
          Apply a function that maps an array to a scalar along a given axis of a matrix or array.
          Returns a new matrix or array with one less dimension than the input.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>apply(A, dim, callback)</code>
        </pre>

        <h3>Where</h3>

        <ul>
          <li>
            <code className="language-plaintext highlighter-rouge">dim: number</code> is a
            zero-based dimension over which to concatenate the matrices.
          </li>
        </ul>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">array</code>
              </td>
              <td>Array | Matrix</td>
              <td>The input Matrix</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">dim</code>
              </td>
              <td>number</td>
              <td>The dimension along which the callback is applied</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">callback</code>
              </td>
              <td>Function</td>
              <td>
                The callback function that is applied. This Function should take an array or 1-d
                matrix as an input and return a number.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>res The residual matrix with the function applied over some dimension.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const A = [[1, 2], [3, 4]] <br />
            const sum = sum <br />
            apply(A, 0, sum) <br />
            {"//"} returns [4, 6] <br /> <br />
            apply(A, 1, sum) <br />
            {"//"} returns [3, 7]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "column",
    text: (
      <div>
        <h1>Function column</h1>

        <p>Return a column from a Matrix.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>column(value, index)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">value</code>
              </td>
              <td>Array | Matrix</td>
              <td>An array or matrix</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">column</code>
              </td>
              <td>number</td>
              <td>The index of the column</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>The retrieved column</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"//"} get a column <br />
            const d = [[1, 2], [3, 4]] <br />
            column(d, 1) <br />
            {"//"} returns [[2], [4]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "concat",
    text: (
      <div>
        <h1>Function concat</h1>

        <p>Concatenate two or more matrices.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            concat(A, B, C, ...) <br />
            concat(A, B, C, ..., dim)
          </code>
        </pre>

        <h3>Where</h3>

        <ul>
          <li>
            <code className="language-plaintext highlighter-rouge">dim: number</code> is a
            zero-based dimension over which to concatenate the matrices. By default the last
            dimension of the matrices.
          </li>
        </ul>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>… Array | Matrix</td>
              <td>Two or more matrices</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>Concatenated matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const A = [[1, 2], [5, 6]] <br />
            const B = [[3, 4], [7, 8]] <br />
            concat(A, B) <br />
            {"//"} returns [[1, 2, 3, 4], [5, 6, 7, 8]] <br /> <br />
            concat(A, B, 0) <br />
            {"//"} returns [[1, 2], [5, 6], [3, 4], [7, 8]] <br /> <br />
            concat(&apos;hello&apos;, &apos; &apos;, &apos;world&apos;) <br />
            {"//"} returns &apos;hello world&apos;
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "count",
    text: (
      <div>
        <h1>Function count</h1>

        <p>Count the number of elements of a matrix, array or string.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>count(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>string | Array | Matrix</td>
              <td>A matrix or string</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number</td>
              <td>
                An integer with the elements in{" "}
                <code className="language-plaintext highlighter-rouge">x</code>.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            count(&apos;hello world&apos;) <br />
            {"//"} returns 11 <br /> <br />
            const A = [[1, 2, 3], [4, 5, 6]] <br />
            count(A) <br />
            {"//"} returns 6 <br /> <br />
            count(range(1,6)) <br />
            {"//"} returns 5
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "cross",
    text: (
      <div>
        <h1>Function cross</h1>

        <p>
          Calculate the cross product for two vectors in three dimensional space. The cross product
          of <code className="language-plaintext highlighter-rouge">A = [a1, a2, a3]</code> and{" "}
          <code className="language-plaintext highlighter-rouge">B = [b1, b2, b3]</code> is defined
          as:
        </p>

        <p>cross(A, B) = [ a2 * b3 - a3 * b2, a3 * b1 - a1 * b3, a1 * b2 - a2 * b1 ]</p>

        <p>
          If one of the input vectors has a dimension greater than 1, the output vector will be a
          1x3 (2-dimensional) matrix.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>cross(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix</td>
              <td>First vector</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>Array | Matrix</td>
              <td>Second vector</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>
                Returns the cross product of{" "}
                <code className="language-plaintext highlighter-rouge">x</code> and{" "}
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            cross([1, 1, 0], [0, 1, 1]) <br />
            {"//"} Returns [1, -1, 1] <br /> <br />
            cross([3, -3, 1], [4, 9, 2]) <br />
            {"//"} Returns [-15, -2, 39] <br /> <br />
            cross([2, 3, 4], [5, 6, 7]) <br />
            {"//"} Returns [-3, 6, -3] <br /> <br />
            cross([[1, 2, 3]], [[4], [5], [6]]) <br />
            {"//"} Returns [[-3, 6, -3]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "ctranspose",
    text: (
      <div>
        <h1>Function ctranspose</h1>

        <p>
          Transpose and complex conjugate a matrix. All values of the matrix are reflected over its
          main diagonal and then the complex conjugate is taken. This is equivalent to complex
          conjugation for scalars and vectors.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>ctranspose(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix</td>
              <td>Matrix to be ctransposed</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>The ctransposed matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const A = [[1, 2, 3], [4, 5, complex(6,7)]] <br />
            ctranspose(A) <br />
            {"//"} returns [[1, 4], [2, 5], [3, {"{re:6,im:7}"}]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "det",
    text: (
      <div>
        <h1>Function det</h1>

        <p>Calculate the determinant of a matrix.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>det(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix</td>
              <td>A matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number</td>
              <td>
                The determinant of <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            det([[1, 2], [3, 4]]) <br />
            {"//"} returns -2 <br /> <br />
            const A = [ <br />
            [-2, 2, 3], <br />
            [-1, 1, 3], <br />
            [2, 0, -1] <br />
            ] <br />
            det(A) <br />
            {"//"} returns 6
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "diag",
    text: (
      <div>
        <h1>Function diag</h1>

        <p>Create a diagonal matrix or retrieve the diagonal of a matrix</p>

        <p>
          When <code className="language-plaintext highlighter-rouge">x</code> is a vector, a matrix
          with vector <code className="language-plaintext highlighter-rouge">x</code> on the
          diagonal will be returned. When{" "}
          <code className="language-plaintext highlighter-rouge">x</code> is a two dimensional
          matrix, the matrixes <code className="language-plaintext highlighter-rouge">k</code>th
          diagonal will be returned as vector. When k is positive, the values are placed on the
          super diagonal. When k is negative, the values are placed on the sub diagonal.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            diag(X) <br />
            diag(X, format) <br />
            diag(X, k) <br />
            diag(X, k, format) <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Matrix | Array</td>
              <td>A two dimensional matrix or a vector</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">k</code>
              </td>
              <td>number | BigNumber</td>
              <td>
                The diagonal where the vector will be filled in or retrieved. Default value: 0.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">format</code>
              </td>
              <td>string</td>
              <td>The matrix storage format. Default value: ‘dense’.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Matrix | Array</td>
              <td>Diagonal matrix from input vector, or diagonal from input matrix.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"//"} create a diagonal matrix <br />
            diag([1, 2, 3]) <br />
            {"//"} returns [[1, 0, 0], [0, 2, 0], [0, 0, 3]] <br /> <br />
            diag([1, 2, 3], 1) <br />
            {"//"} returns [[0, 1, 0, 0], [0, 0, 2, 0], [0, 0, 0, 3]] <br /> <br />
            diag([1, 2, 3], -1) <br />
            {"//"} returns [[0, 0, 0], [1, 0, 0], [0, 2, 0], [0, 0, 3]] <br /> <br />
            {"//"} retrieve the diagonal from a matrix <br />
            const a = [[1, 2, 3], [4, 5, 6], [7, 8, 9]] <br />
            diag(a) <br />
            {"//"} returns [1, 5, 9]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "diff",
    text: (
      <div>
        <h1>Function diff</h1>

        <p>
          Create a new matrix or array of the difference between elements of the given array The
          optional dim parameter lets you specify the dimension to evaluate the difference of If no
          dimension parameter is passed it is assumed as dimension 0
        </p>

        <p>
          Dimension is zero-based in javascript and one-based in the parser and can be a number or
          bignumber Arrays must be ‘rectangular’ meaning arrays like [1, 2] If something is passed
          as a matrix it will be returned as a matrix but other than that all matrices are converted
          to arrays
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code></code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">arr</code>
              </td>
              <td>Array | Matrix</td>
              <td>An array or matrix</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">dim</code>
              </td>
              <td>number | BigNumber</td>
              <td>Dimension</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>Difference between array elements in given dimension</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const arr = [1, 2, 4, 7, 0] <br />
            diff(arr) <br />
            {"//"} returns [1, 2, 3, -7] <br />
            {"//"} (no dimension passed so 0 is assumed)
            <br /> <br />
            diff(matrix(arr)) <br />
            {"//"} returns Matrix [1, 2, 3, -7] <br /> <br />
            const arr = [[1, 2, 3, 4, 5], [1, 2, 3, 4, 5], <br />
            {"    "}[9, 8, 7, 6, 4]]
            <br />
            diff(arr) <br />
            {"//"} returns [[0, 0, 0, 0, 0], [8, 6, 4, 2, -1]] <br /> <br />
            diff(arr, 0) <br />
            {"//"} returns [[0, 0, 0, 0, 0], [8, 6, 4, 2, -1]] <br /> <br />
            diff(arr, 1) <br />
            {"//"} returns [[1, 1, 1, 1], [1, 1, 1, 1], [-1, -1, -1, -2]] <br />
            <br />
            diff(arr, bignumber(1)) <br />
            {"//"} returns [[1, 1, 1, 1], [1, 1, 1, 1], [-1, -1, -1, -2]] <br />
            <br />
            diff(arr, 2) <br />
            {"//"} throws RangeError as arr is 2 dimensional not 3 <br /> <br />
            diff(arr, -1) <br />
            {"//"} throws RangeError as negative dimensions are not allowed
            <br /> <br />
            {"//"} These will all produce the same result <br />
            diff([[1, 2], [3, 4]]) <br />
            diff([matrix([1, 2]), matrix([3, 4])]) <br />
            diff([[1, 2], matrix([3, 4])]) <br />
            diff([matrix([1, 2]), [3, 4]]) <br />
            {"//"} They do not produce the same result as <br />
            {"//"} diff(matrix([[1, 2], [3, 4]])) as this returns a matrix
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "dot",
    text: (
      <div>
        <h1>Function dot</h1>

        <p>
          Calculate the dot product of two vectors. The dot product of
          <code className="language-plaintext highlighter-rouge">
            A = [a1, a2, ..., an]
          </code> and{" "}
          <code className="language-plaintext highlighter-rouge">B = [b1, b2, ..., bn]</code> is
          defined as:
        </p>

        <p>dot(A, B) = conj(a1) * b1 + conj(a2) * b2 + … + conj(an) * bn</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>dot(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix</td>
              <td>First vector</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>Array | Matrix</td>
              <td>Second vector</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number</td>
              <td>
                Returns the dot product of{" "}
                <code className="language-plaintext highlighter-rouge">x</code> and{" "}
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            dot([2, 4, 1], [2, 2, 3]) <br />
            {"//"} returns number 15 <br /> <br />
            multiply([2, 4, 1], [2, 2, 3]) <br />
            {"//"} returns number 15
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "eigs",
    text: (
      <div>
        <h1>Function eigs</h1>

        <p>
          Compute eigenvalues and eigenvectors of a matrix. The eigenvalues are sorted by their
          absolute value, ascending. An eigenvalue with multiplicity k will be listed k times. The
          eigenvectors are returned as columns of a matrix – the eigenvector that belongs to the
          j-th eigenvalue in the list (eg.{" "}
          <code className="language-plaintext highlighter-rouge">values[j]</code>) is the j-th
          column (eg.{" "}
          <code className="language-plaintext highlighter-rouge">column(vectors, j)</code>
          ). If the algorithm fails to converge, it will throw an error – in that case, however, you
          may still find useful information in{" "}
          <code className="language-plaintext highlighter-rouge">err.values</code> and{" "}
          <code className="language-plaintext highlighter-rouge">err.vectors</code>.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>eigs(x, [prec])</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix</td>
              <td>Matrix to be diagonalized</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">prec</code>
              </td>
              <td>number | BigNumber</td>
              <td>Precision, default value: 1e-15</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{"{values: Array | Matrix, vectors: Array | Matrix}"}</td>
              <td>
                Object containing an array of eigenvalues and a matrix with eigenvectors as columns.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const {"{ eigs, multiply, column, transpose }"} = math <br />
            const H = [[5, 2.3], [2.3, 1]] <br />
            const ans = eigs(H) <br />
            {"// returns {values: [E1,E2...sorted],"} <br />
            {"// vectors: [v1,v2.... corresponding vectors as columns]}"} <br />
            const E = ans.values <br />
            const U = ans.vectors <br />
            multiply(H, column(U, 0)) <br />
            {"//"} returns multiply(E[0], column(U, 0)) <br />
            const UTxHxU = multiply(transpose(U), H, U) <br />
            {"//"} diagonalizes H <br />
            E[0] == UTxHxU[0][0] <br />
            {"//"} returns true
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "expm",
    text: (
      <div>
        <h1>Function expm</h1>

        <p>
          Compute the matrix exponential, expm(A) = e^A. The matrix must be square. Not to be
          confused with exp(a), which performs element-wise exponentiation.
        </p>

        <p>
          The exponential is calculated using the Padé approximant with scaling and squaring; see
          “Nineteen Dubious Ways to Compute the Exponential of a Matrix,” by Moler and Van Loan.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>expm(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Matrix</td>
              <td>A square Matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Matrix</td>
              <td>The exponential of x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const A = [[0,2],[0,0]] <br />
            expm(A) <br />
            {"//"} returns [[1,2],[0,1]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "fft",
    text: (
      <div>
        <h1>Function fft</h1>

        <p>Calculate N-dimensional fourier transform</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>fft(arr)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">arr</code>
              </td>
              <td>Array | Matrix</td>
              <td>An array or matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>N-dimensional fourier transformation of the array</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            fft([[1, 0], [1, 0]]) <br />
            {"//"} returns {"[[{re:2, im:0}, {re:2, im:0}],"} <br />
            {"//"} {"[{re:0, im:0}, {re:0, im:0}]]"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "filter",
    text: (
      <div>
        <h1>Function filter</h1>

        <p>Filter the items in an array or one dimensional matrix.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>filter(x, test)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Matrix | Array</td>
              <td>A one dimensional matrix or array to filter</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">test</code>
              </td>
              <td>Function | RegExp</td>
              <td>
                A function or regular expression to test items. All entries for which{" "}
                <code className="language-plaintext highlighter-rouge">test</code> returns true are
                returned. When <code className="language-plaintext highlighter-rouge">test</code> is
                a function, it is invoked with three parameters: the value of the element, the index
                of the element, and the matrix/array being traversed. The function must return a
                boolean.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Matrix | Array</td>
              <td>Returns the filtered matrix.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            function isPositive (x) {"{"} <br />
            {"    "}
            {"return x > 0"} <br />
            {"}"} <br />
            filter([6, -2, -1, 4, 3], isPositive) <br />
            {"//"} returns [6, 4, 3] <br /> <br />
            filter([&quot;23&quot;, &quot;foo&quot;, &quot;100&quot;, &quot;55&quot;,
            &quot;bar&quot;], /[0-9]+/) <br />
            {"//"} returns [&quot;23&quot;, &quot;100&quot;, &quot;55&quot;]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "flatten",
    text: (
      <div>
        <h1>Function flatten</h1>

        <p>
          Flatten a multidimensional matrix into a single dimensional matrix. A new matrix is
          returned, the original matrix is left untouched.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>flatten(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Matrix | Array</td>
              <td>Matrix to be flattened</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Matrix | Array</td>
              <td>Returns the flattened matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            flatten([[1,2], [3,4]]) <br />
            {"//"} returns [1, 2, 3, 4]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "forEach",
    text: (
      <div>
        <h1>Function forEach</h1>

        <p>
          Iterate over all elements of a matrix/array, and executes the given callback function.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>forEach(x, callback)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Matrix | Array</td>
              <td>The matrix to iterate on.</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">callback</code>
              </td>
              <td>Function</td>
              <td>
                The callback function is invoked with three parameters: the value of the element,
                the index of the element, and the Matrix/array being traversed.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"forEach([1, 2, 3], function(value) {"} <br />
            {"    "}
            {"console.log(value)"} <br />
            {"})"} <br />
            {"//"} outputs 1, 2, 3
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "getMatrixDataType",
    text: (
      <div>
        <h1>Function getMatrixDataType</h1>

        <p>
          Find the data type of all elements in a matrix or array, for example ‘number’ if all items
          are a number and ‘Complex’ if all values are complex numbers. If a matrix contains more
          than one data type, it will return ‘mixed’.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>getMatrixDataType(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>…Matrix | Array</td>
              <td>The Matrix with values.</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>string</td>
              <td>A string representation of the matrix type</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const x = [ [1, 2, 3], [4, 5, 6] ] <br />
            const mixedX = [ [1, true], [2, 3] ] <br />
            const fractionX = [ [fraction(1, 3)], [fraction(1, 3)] ] <br />
            const unitX = [ [unit(&apos;5cm&apos;)], [unit(&apos;5cm&apos;)] ] <br />
            const bigNumberX = [ [bignumber(1)], [bignumber(0)] ] <br />
            const sparse = sparse(x) <br />
            const dense = matrix(x) <br />
            getMatrixDataType(x) <br />
            {"//"} returns &apos;number&apos; <br />
            getMatrixDataType(sparse) <br />
            {"//"} returns &apos;number&apos; <br />
            getMatrixDataType(dense) <br />
            {"//"} returns &apos;number&apos; <br />
            getMatrixDataType(mixedX) <br />
            {"//"} returns &apos;mixed&apos; <br />
            getMatrixDataType(fractionX) <br />
            {"//"} returns &apos;Fraction&apos; <br />
            getMatrixDataType(unitX) <br />
            {"//"} returns &apos;Unit&apos; <br />
            getMatrixDataType(bigNumberX) <br />
            {"//"} return &apos;BigNumber&apos; <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "identity",
    text: (
      <div>
        <h1>Function identity</h1>

        <p>
          Create a 2-dimensional identity matrix with size m x n or n x n. The matrix has ones on
          the diagonal and zeros elsewhere.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            identity(n) <br />
            identity(n, format) <br />
            identity(m, n) <br />
            identity(m, n, format) <br />
            identity([m, n]) <br />
            identity([m, n], format)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">size</code>
              </td>
              <td>…number | Matrix | Array</td>
              <td>The size for the matrix</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">format</code>
              </td>
              <td>string</td>
              <td>The Matrix storage format</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Matrix | Array | number</td>
              <td>A matrix with ones on the diagonal.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            identity(3) <br />
            {"//"} returns [[1, 0, 0], [0, 1, 0], [0, 0, 1]] <br /> <br />
            identity(3, 2) <br />
            {"//"} returns [[1, 0], [0, 1], [0, 0]] <br /> <br />
            const A = [[1, 2, 3], [4, 5, 6]] <br />
            identity(size(A)) <br />
            {"//"} returns [[1, 0, 0], [0, 1, 0]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "ifft",
    text: (
      <div>
        <h1>Function ifft</h1>

        <p>Calculate N-dimensional inverse fourier transform</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>ifft(arr)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">arr</code>
              </td>
              <td>Array | Matrix</td>
              <td>An array or matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>N-dimensional fourier transformation of the array</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            ifft([[2, 2], [0, 0]]) <br />
            {"// returns [[{re:1, im:0}, {re:0, im:0}],"} <br />
            {"// [{re:1, im:0}, {re:0, im:0}]]"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "inv",
    text: (
      <div>
        <h1>Function inv</h1>

        <p>Calculate the inverse of a square matrix.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>inv(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | Complex | Array | Matrix</td>
              <td>Matrix to be inversed</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Complex | Array | Matrix</td>
              <td>
                The inverse of <code className="language-plaintext highlighter-rouge">x</code>.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            inv([[1, 2], [3, 4]]) <br />
            {"//"} returns [[-2, 1], [1.5, -0.5]] <br /> <br />
            inv(4) <br />
            {"//"} returns 0.25 <br /> <br />
            {"1 / 4"} <br />
            {"//"} returns 0.25
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "kron",
    text: (
      <div>
        <h1>Function kron</h1>

        <p>Calculates the kronecker product of 2 matrices or vectors.</p>

        <p>
          NOTE: If a one dimensional vector / matrix is given, it will be wrapped so its two
          dimensions. See the examples.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>kron(x, y)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix</td>
              <td>First vector</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
              <td>Array | Matrix</td>
              <td>Second vector</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>
                Returns the kronecker product of{" "}
                <code className="language-plaintext highlighter-rouge">x</code> and{" "}
                <code className="language-plaintext highlighter-rouge">y</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            kron([[1, 0], [0, 1]], [[1, 2], [3, 4]]) <br />
            {"// returns [ [ 1, 2, 0, 0 ], [ 3, 4, 0, 0 ],"} <br /> <br />
            {"// [ 0, 0, 1, 2 ], [ 0, 0, 3, 4 ] ]"} <br /> <br />
            kron([1,1], [2,3,4]) <br />
            {"// returns [ [ 2, 3, 4, 2, 3, 4 ] ]"}
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "map",
    text: (
      <div>
        <h1>Function map</h1>

        <p>
          Create a new matrix or array with the results of a callback function executed on each
          entry of a given matrix/array.
        </p>

        <p>
          For each entry of the input, the callback is invoked with three arguments: the value of
          the entry, the index at which that entry occurs, and the full matrix/array being
          traversed. Note that because the matrix/array might be multidimensional, the “index”
          argument is always an array of numbers giving the index in each dimension. This is true
          even for vectors: the “index” argument is an array of length 1, rather than simply a
          number.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>map(x, callback)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Matrix | Array</td>
              <td>The input to iterate on.</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">callback</code>
              </td>
              <td>Function</td>
              <td>The function to call (as described above) on each entry of the input</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Matrix | array</td>
              <td>Transformed map of x; always has the same type and shape as x</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"map([1, 2, 3], function(value) {"} <br />
            {"    "}return value * value <br />
            {"})"} <br />
            {"//"} returns [1, 4, 9] <br /> <br />
            {"//"} The callback is normally called with three arguments: <br />
            {"//"} callback(value, index, Array) <br />
            {"//"} If you want to call with only one argument, use: <br />
            {"map([1, 2, 3], x => format(x))"} <br />
            {"//"} returns [&apos;1&apos;, &apos;2&apos;, &apos;3&apos;]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "matrixFromColumns",
    text: (
      <div>
        <h1>Function matrixFromColumns</h1>

        <p>
          Create a dense matrix from vectors as individual columns. If you pass row vectors, they
          will be transposed (but not conjugated!)
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            matrixFromColumns(...arr) <br />
            matrixFromColumns(col1, col2) <br />
            matrixFromColumns(col1, col2, col3)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table>
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">cols</code>
              </td>
              <td>… Array | Matrix</td>
              <td>Multiple columns</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number[][] | Matrix</td>
              <td>if at least one of the arguments is an array, an array will be returned</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            matrixFromColumns([1, 2, 3], [[4],[5],[6]]) <br />
            matrixFromColumns(...vectors)
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "matrixFromFunction",
    text: (
      <div>
        <h1>Function matrixFromFunction</h1>

        <p>
          Create a matrix by evaluating a generating function at each index. The simplest overload
          returns a multi-dimensional array as long as{" "}
          <code className="language-plaintext highlighter-rouge">size</code> is an array. Passing{" "}
          <code className="language-plaintext highlighter-rouge">size</code> as a Matrix or
          specifying a <code className="language-plaintext highlighter-rouge">format</code> will
          result in returning a Matrix.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            matrixFromFunction(size, fn) <br />
            matrixFromFunction(size, fn, format) <br />
            matrixFromFunction(size, fn, format, datatype) <br />
            matrixFromFunction(size, format, fn) <br />
            matrixFromFunction(size, format, datatype, fn) <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">size</code>
              </td>
              <td>Array | Matrix</td>
              <td>The size of the matrix to be created</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">fn</code>
              </td>
              <td>function</td>
              <td>Callback function invoked for every entry in the matrix</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">format</code>
              </td>
              <td>string</td>
              <td>
                The Matrix storage format, either{" "}
                <code className="language-plaintext highlighter-rouge">&apos;dense&apos;</code> or{" "}
                <code className="language-plaintext highlighter-rouge">&apos;sparse&apos;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">datatype</code>
              </td>
              <td>string</td>
              <td>Type of the values</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>Returns the created matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"matrixFromFunction([3,3], i => i[0] - i[1])"} <br />
            {"//"} an antisymmetric matrix <br /> <br />
            {"matrixFromFunction([5], i => random())"} <br />
            {"//"} a random vector
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "matrixFromRows",
    text: (
      <div>
        <h1>Function matrixFromRows</h1>

        <p>
          Create a dense matrix from vectors as individual rows. If you pass column vectors, they
          will be transposed (but not conjugated!)
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            matrixFromRows(...arr) <br />
            matrixFromRows(row1, row2) <br />
            matrixFromRows(row1, row2, row3) <br />
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">rows</code>
              </td>
              <td>… Array | Matrix</td>
              <td>Multiple rows</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number[][] | Matrix</td>
              <td>if at least one of the arguments is an array, an array will be returned</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            matrixFromRows([1, 2, 3], [[4],[5],[6]]) <br />
            matrixFromRows(...vectors)
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "ones",
    text: (
      <div>
        <h1>Function ones</h1>

        <p>
          Create a matrix filled with ones. The created matrix can have one or multiple dimensions.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            ones(m) <br />
            ones(m, format) <br />
            ones(m, n) <br />
            ones(m, n, format) <br />
            ones([m, n]) <br />
            ones([m, n], format) <br />
            ones([m, n, p, ...]) <br />
            ones([m, n, p, ...], format)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">size</code>
              </td>
              <td>…(number | BigNumber) | Array</td>
              <td>The size of each dimension of the matrix</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">format</code>
              </td>
              <td>string</td>
              <td>The Matrix storage format</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix | number</td>
              <td>A matrix filled with ones</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            ones() <br />
            {"//"} returns [] <br /> <br />
            ones(3) <br />
            {"//"} returns [1, 1, 1] <br /> <br />
            ones(3, 2) <br />
            {"//"} returns [[1, 1], [1, 1], [1, 1]] <br /> <br />
            ones(3, 2, &apos;dense&apos;) <br />
            {"//"} returns Dense Matrix [[1, 1], [1, 1], [1, 1]] <br /> <br />
            const A = [[1, 2, 3], [4, 5, 6]] <br />
            ones(size(A)) <br />
            {"//"} returns [[1, 1, 1], [1, 1, 1]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "partitionSelect",
    text: (
      <div>
        <h1>Function partitionSelect</h1>

        <p>
          Partition-based selection of an array or 1D matrix. Will find the kth smallest value, and
          mutates the input array. Uses Quickselect.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            partitionSelect(x, k) <br />
            partitionSelect(x, k, compare)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Matrix | Array</td>
              <td>A one dimensional matrix or array to sort</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">k</code>
              </td>
              <td>Number</td>
              <td>The kth smallest value to be retrieved zero-based index</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">compare</code>
              </td>
              <td>Function | ‘asc’ | ‘desc’</td>
              <td>
                An optional comparator function. The function is called as{" "}
                <code className="language-plaintext highlighter-rouge">compare(a, b)</code>, and
                must return 1 when a &gt; b, -1 when a &lt; b, and 0 when a == b. Default value:
                ‘asc’.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>*</td>
              <td>Returns the kth lowest value.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            partitionSelect([5, 10, 1], 2) <br />
            {"//"} returns 10 <br /> <br />
            partitionSelect([&apos;C&apos;, &apos;B&apos;, &apos;A&apos;, &apos;D&apos;], 1,
            compareText) <br />
            {"//"} returns &apos;B&apos; <br /> <br />
            function sortByLength (a, b) {"{"} <br />
            {"    "}return a.length - b.length <br />
            {"}"} <br />
            partitionSelect([&apos;Langdon&apos;, &apos;Tom&apos;, &apos;Sara&apos;], 2,
            sortByLength) <br />
            {"//"} returns &apos;Langdon&apos; <br /> <br />
            {"//"} the input array is mutated <br />
            arr = [5, 2, 1] <br />
            partitionSelect(arr, 0) <br />
            {"//"} returns 1, arr is now: [1, 2, 5] <br /> <br />
            partitionSelect(arr, 1, &apos;desc&apos;) <br />
            {"//"} returns 2, arr is now: [5, 2, 1]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "pinv",
    text: (
      <div>
        <h1>Function pinv</h1>

        <p>Calculate the Moore–Penrose inverse of a matrix.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>pinv(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>number | Complex | Array | Matrix</td>
              <td>Matrix to be inversed</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number | Complex | Array | Matrix</td>
              <td>
                The inverse of <code className="language-plaintext highlighter-rouge">x</code>.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            pinv([[1, 2], [3, 4]]) <br />
            {"//"} returns [[-2, 1], [1.5, -0.5]] <br />
            pinv([[1, 0], [0, 1], [0, 1]]) <br />
            {"//"} returns [[1, 0, 0], [0, 0.5, 0.5]] <br />
            pinv(4) <br />
            {"//"} returns 0.25
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "range",
    text: (
      <div>
        <h1>Function range</h1>

        <p>
          Create an array from a range. By default, the range end is excluded. This can be
          customized by providing an extra parameter{" "}
          <code className="language-plaintext highlighter-rouge">includeEnd</code>.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            range(str [, includeEnd]) <br />
            {"//"} Create a range from a string, <br />
            {"//"} where the string contains the <br />
            {"//"} start, optional step, and end, <br />
            {"//"} separated by a colon. <br />
            range(start, end [, includeEnd]) <br />
            {"//"} Create a range with start and <br />
            {"//"} end and a step size of 1. <br />
            range(start, end, step [, includeEnd]) <br />
            {"//"} Create a range with start, step, <br />
            {"//"} and end.
          </code>
        </pre>

        <h3>Where</h3>

        <ul>
          <li>
            <code className="language-plaintext highlighter-rouge">str: string</code>A string
            ‘start:end’ or ‘start:step:end’
          </li>
          <li>
            <code className="language-plaintext highlighter-rouge">
              start: {"{number | BigNumber | Unit}"}
            </code>
            Start of the range
          </li>
          <li>
            <code className="language-plaintext highlighter-rouge">
              end: number | BigNumber | Unit
            </code>
            End of the range, excluded by default, included when parameter includeEnd=true
          </li>
          <li>
            <code className="language-plaintext highlighter-rouge">
              step: number | BigNumber | Unit
            </code>
            Step size. Default value is 1.
          </li>
          <li>
            <code className="language-plaintext highlighter-rouge">includeEnd: boolean</code>
            Option to specify whether to include the end or not. False by default.
          </li>
        </ul>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">args</code>
              </td>
              <td>*</td>
              <td>
                Parameters describing the ranges{" "}
                <code className="language-plaintext highlighter-rouge">start</code>,{" "}
                <code className="language-plaintext highlighter-rouge">end</code>, and optional{" "}
                <code className="language-plaintext highlighter-rouge">step</code>.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>range</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            range(2, 6) <br />
            {"//"} [2, 3, 4, 5] <br /> <br />
            range(2, -3, -1) <br />
            {"//"} [2, 1, 0, -1, -2] <br /> <br />
            range(&apos;2:1:6&apos;) <br />
            {"//"} [2, 3, 4, 5] <br /> <br />
            range(2, 6, true) <br />
            {"//"} [2, 3, 4, 5, 6] <br /> <br />
            range(unit(2, &apos;m&apos;), unit(-3, &apos;m&apos;), unit(-1, &apos;m&apos;)) <br />
            {"//"} [2 m, 1 m, 0 m , -1 m, -2 m]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "reshape",
    text: (
      <div>
        <h1>Function reshape</h1>

        <p>Reshape a multi dimensional array to fit the specified dimensions</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>reshape(x, sizes)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix | *</td>
              <td>Matrix to be reshaped</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">sizes</code>
              </td>
              <td>number[]</td>
              <td>
                One dimensional array with integral sizes for each dimension. One -1 is allowed as
                wildcard, which calculates this dimension automatically.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>* | Array | Matrix</td>
              <td>
                A reshaped clone of matrix{" "}
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>TypeError</td>
              <td>
                If <code className="language-plaintext highlighter-rouge">sizes</code> does not
                contain solely integers
              </td>
            </tr>
            <tr>
              <td>DimensionError</td>
              <td>If the product of the new dimension sizes does not equal that of the old ones</td>
            </tr>
          </tbody>
        </table>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            reshape([1, 2, 3, 4, 5, 6], [2, 3]) <br />
            {"//"} returns Array [[1, 2, 3], [4, 5, 6]] <br /> <br />
            reshape([[1, 2], [3, 4]], [1, 4]) <br />
            {"//"} returns Array [[1, 2, 3, 4]] <br /> <br />
            reshape([[1, 2], [3, 4]], [4]) <br />
            {"//"} returns Array [1, 2, 3, 4] <br /> <br />
            const x = matrix([1, 2, 3, 4, 5, 6, 7, 8]) <br />
            reshape(x, [2, 2, 2]) <br />
            {"//"} returns Matrix [[[1, 2], [3, 4]], [[5, 6], [7, 8]]] <br /> <br />
            reshape([1, 2, 3, 4], [-1, 2]) <br />
            {"//"} returns Matrix [[1, 2], [3, 4]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "resize",
    text: (
      <div>
        <h1>Function resize</h1>

        <p>Resize a matrix</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            resize(x, size) <br />
            resize(x, size, defaultValue)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix | *</td>
              <td>Matrix to be resized</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">size</code>
              </td>
              <td>Array | Matrix</td>
              <td>One dimensional array with numbers</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">defaultValue</code>
              </td>
              <td>number | string</td>
              <td>
                Zero by default, except in case of a string, in that case defaultValue = ‘ ‘ Default
                value: 0.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>* | Array | Matrix</td>
              <td>
                A resized clone of matrix{" "}
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            resize([1, 2, 3, 4, 5], [3]) <br />
            {"//"} returns Array [1, 2, 3] <br /> <br />
            resize([1, 2, 3], [5], 0) <br />
            {"//"} returns Array [1, 2, 3, 0, 0] <br /> <br />
            resize(2, [2, 3], 0) <br />
            {"//"} returns Matrix [[2, 0, 0], [0, 0, 0]] <br /> <br />
            resize(&quot;hello&quot;, [8], &quot;!&quot;) <br />
            {"//"} returns string &apos;hello!!!&apos;
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "rotate",
    text: (
      <div>
        <h1>Function rotate</h1>

        <p>
          Rotate a vector of size 1x2 counter-clockwise by a given angle Rotate a vector of size 1x3
          counter-clockwise by a given angle around the given axis
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            rotate(w, theta) <br />
            rotate(w, theta, v)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">w</code>
              </td>
              <td>Array | Matrix</td>
              <td>Vector to rotate</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">theta</code>
              </td>
              <td>number | BigNumber | Complex | Unit</td>
              <td>Rotation angle</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">v</code>
              </td>
              <td>Array | Matrix</td>
              <td>Rotation axis</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>Multiplication of the rotation matrix and w</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            rotate([11, 12], pi / 2) <br />
            {"//"} returns [-12, 11] <br /> <br />
            rotate(matrix([11, 12]), pi / 2) <br />
            {"//"} returns [-12, 11] <br /> <br />
            rotate([1, 0, 0], unit(&apos;90deg&apos;), [0, 0, 1]) <br />
            {"//"} returns [0, 1, 0] <br /> <br />
            rotate(matrix([1, 0, 0]), unit(&apos;90deg&apos;), [0, 0, 1]) <br />
            {"//"} returns Matrix [0, 1, 0] <br /> <br />
            rotate([1, 0], complex(1 + i)) <br />
            {"//"} returns [cos(1 + i) - sin(1 + i), sin(1 + i) + cos(1 + i)]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "rotationMatrix",
    text: (
      <div>
        <h1>Function rotationMatrix</h1>

        <p>
          Create a 2-dimensional counter-clockwise rotation matrix (2x2) for a given angle
          (expressed in radians). Create a 2-dimensional counter-clockwise rotation matrix (3x3) by
          a given angle (expressed in radians) around a given axis (1x3).
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            rotationMatrix(theta) <br />
            rotationMatrix(theta, format) <br />
            rotationMatrix(theta, [v]) <br />
            rotationMatrix(theta, [v], format)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">theta</code>
              </td>
              <td>number | BigNumber | Complex | Unit</td>
              <td>Rotation angle</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">v</code>
              </td>
              <td>Array | Matrix</td>
              <td>Rotation axis</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">format</code>
              </td>
              <td>string</td>
              <td>Result Matrix storage format</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>Rotation matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            rotationMatrix(pi / 2) <br />
            {"//"} returns [[0, -1], [1, 0]] <br /> <br />
            rotationMatrix(bignumber(1)) <br />
            {"//"} returns [[bignumber(cos(1)), bignumber(-sin(1))], <br />
            {"//"} [bignumber(sin(1)), bignumber(cos(1))]] <br /> <br />
            rotationMatrix(complex(1 + i)) <br />
            {"//"} returns [[cos(1 + i), -sin(1 + i)], <br />
            {"//"} [sin(1 + i), cos(1 + i)]] <br /> <br />
            rotationMatrix(unit(&apos;1rad&apos;)) <br />
            {"//"} returns [[cos(1), -sin(1)], [sin(1), cos(1)]] <br /> <br />
            rotationMatrix(pi / 2, [0, 1, 0]) <br />
            {"//"} returns [[0, 0, 1], [0, 1, 0], [-1, 0, 0]] <br /> <br />
            rotationMatrix(pi / 2, matrix([0, 1, 0])) <br />
            {"//"} returns matrix([[0, 0, 1], [0, 1, 0], [-1, 0, 0]])
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "row",
    text: (
      <div>
        <h1>Function row</h1>

        <p>Return a row from a Matrix.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>row(value, index)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">value</code>
              </td>
              <td>Array | Matrix</td>
              <td>An array or matrix</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">row</code>
              </td>
              <td>number</td>
              <td>The index of the row</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>The retrieved row</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"//"} get a row <br />
            const d = [[1, 2], [3, 4]] <br />
            row(d, 1) <br />
            {"//"} returns [[3, 4]] <br />
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "size",
    text: (
      <div>
        <h1>Function size</h1>

        <p>Calculate the size of a matrix or scalar.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>size(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>boolean | number | Complex | Unit | string | Array | Matrix</td>
              <td>A matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>
                A vector with size of{" "}
                <code className="language-plaintext highlighter-rouge">x</code>.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            size(2.3) <br />
            {"//"} returns [] <br /> <br />
            size(&apos;hello world&apos;) <br />
            {"//"} returns [11] <br /> <br />
            const A = [[1, 2, 3], [4, 5, 6]] <br />
            size(A) <br />
            {"//"} returns [2, 3] <br /> <br />
            size(range(1,6)) <br />
            {"//"} returns [5]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "sort",
    text: (
      <div>
        <h1>Function sort</h1>

        <p>Sort the items in a matrix.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            sort(x) <br />
            sort(x, compare)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Matrix | Array</td>
              <td>A one dimensional matrix or array to sort</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">compare</code>
              </td>
              <td>Function | ‘asc’ | ‘desc’ | ‘natural’</td>
              <td>
                An optional _comparator function or name. The function is called as{" "}
                <code className="language-plaintext highlighter-rouge">compare(a, b)</code>, and
                must return 1 when a &gt; b, -1 when a &lt; b, and 0 when a == b. Default value:
                ‘asc’.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Matrix | Array</td>
              <td>Returns the sorted matrix.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            sort([5, 10, 1]) <br />
            {"//"} returns [1, 5, 10] sort([&apos;C&apos;, &apos;B&apos;, &apos;A&apos;,
            &apos;D&apos;], <br />
            {"//"} compareNatural) <br />
            {"//"} returns [&apos;A&apos;, &apos;B&apos;, &apos;C&apos;, &apos;D&apos;] function
            sortByLength (a, b) <br />
            {"{"} <br />
            {"    "}return a.length - b.length <br />
            {"}"} <br />
            sort([&apos;Langdon&apos;, &apos;Tom&apos;, &apos;Sara&apos;], sortByLength) <br />
            {"//"} returns [&apos;Tom&apos;, &apos;Sara&apos;, &apos;Langdon&apos;]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "sqrtm",
    text: (
      <div>
        <h1>Function sqrtm</h1>

        <p>
          Calculate the principal square root of a square matrix. The principal square root matrix{" "}
          <code className="language-plaintext highlighter-rouge">X</code> of another matrix{" "}
          <code className="language-plaintext highlighter-rouge">A</code> is such that{" "}
          <code className="language-plaintext highlighter-rouge">X * X = A</code>.
        </p>

        <p>https://en.wikipedia.org/wiki/Square_root_of_a_matrix</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>sqrtm(A)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">A</code>
              </td>
              <td>Array | Matrix</td>
              <td>
                The square matrix <code className="language-plaintext highlighter-rouge">A</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>
                The principal square root of matrix{" "}
                <code className="language-plaintext highlighter-rouge">A</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            sqrtm([[33, 24], [48, 57]]) <br />
            {"//"} returns [[5, 2], [4, 7]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "squeeze",
    text: (
      <div>
        <h1>Function squeeze</h1>

        <p>Squeeze a matrix, remove inner and outer singleton dimensions from a matrix.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>squeeze(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Matrix | Array</td>
              <td>Matrix to be squeezed</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Matrix | Array</td>
              <td>Squeezed matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            squeeze([3]) <br />
            {"//"} returns 3 <br /> <br />
            squeeze([[3]]) <br />
            {"//"} returns 3 <br /> <br />
            const A = zeros(3, 1) <br />
            {"//"} returns [[0], [0], [0]] (size 3x1) <br /> <br />
            squeeze(A) <br />
            {"//"} returns [0, 0, 0] (size 3) <br /> <br />
            const B = zeros(1, 3) <br />
            {"//"} returns [[0, 0, 0]] (size 1x3) <br /> <br />
            squeeze(B) <br />
            {"//"} returns [0, 0, 0] (size 3) <br /> <br />
            {"//"} only inner and outer dimensions are removed <br />
            const C = zeros(2, 1, 3) <br />
            {"//"} returns [[[0, 0, 0]], [[0, 0, 0]]] (size 2x1x3) <br /> <br />
            squeeze(C) <br />
            {"//"} returns [[[0, 0, 0]], [[0, 0, 0]]] (size 2x1x3)
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "subset",
    text: (
      <div>
        <h1>Function subset</h1>

        <p>Get or set a subset of a matrix or string.</p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            subset(value, index) <br />
            {"//"} retrieve a subset <br />
            subset(value, index, replacement [, defaultValue]) <br />
            {"//"} replace a subset
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">matrix</code>
              </td>
              <td>Array | Matrix | string</td>
              <td>An array, matrix, or string</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">index</code>
              </td>
              <td>Index</td>
              <td>
                For each dimension of the target, specifies an index or a list of indices to fetch
                or set. <code className="language-plaintext highlighter-rouge">subset</code> uses
                the cartesian product of the indices specified in each dimension.
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">replacement</code>
              </td>
              <td>*</td>
              <td>
                An array, matrix, or scalar. If provided, the subset is replaced with replacement.
                If not provided, the subset is returned
              </td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">defaultValue</code>
              </td>
              <td>*</td>
              <td>
                Default value, filled in on new entries when the matrix is resized. If not provided,
                matrix elements will be left undefined. Default value: undefined.
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix | string</td>
              <td>Either the retrieved subset or the updated matrix.</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            {"//"} get a subset <br />
            const d = [[1, 2], [3, 4]] <br />
            subset(d, index(1, 0)) <br />
            {"//"} returns 3 <br /> <br />
            subset(d, index([0, 1], 1)) <br />
            {"//"} returns [[2], [4]] <br /> <br />
            {"//"} replace a subset <br />
            const e = [] <br />
            const f = subset(e, index(0, [0, 2]), [5, 6]) <br />
            {"//"} f = [[5, 6]] <br /> <br />
            const g = subset(f, index(1, 1), 7, 0) <br />
            {"//"} g = [[5, 6], [0, 7]] <br /> <br />
            {"//"} get submatrix using ranges <br />
            const M = {"["} <br />
            {"  "}[1,2,3], <br />
            {"  "}[4,5,6], <br />
            {"  "}[7,8,9] <br />
            {"]"} <br />
            subset(M, index(range(0,2), range(0,3))) <br />
            {"//"} [[1,2,3],[4,5,6]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "trace",
    text: (
      <div>
        <h1>Function trace</h1>

        <p>
          Calculate the trace of a matrix: the sum of the elements on the main diagonal of a square
          matrix.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>trace(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix</td>
              <td>A matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>number</td>
              <td>
                The trace of <code className="language-plaintext highlighter-rouge">x</code>
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            trace([[1, 2], [3, 4]]) <br />
            {"//"} returns 5 <br /> <br />
            const A = {"["} <br />
            {"  "}[1, 2, 3], <br />
            {"  "}[-1, 2, 3], <br />
            {"  "}[2, 0, 3] <br />
            {"]"} <br />
            trace(A) <br />
            {"//"} returns 6
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "transpose",
    text: (
      <div>
        <h1>Function transpose</h1>

        <p>
          Transpose a matrix. All values of the matrix are reflected over its main diagonal. Only
          applicable to two dimensional matrices containing a vector (i.e. having size{" "}
          <code className="language-plaintext highlighter-rouge">[1,n]</code> or{" "}
          <code className="language-plaintext highlighter-rouge">[n,1]</code>). One dimensional
          vectors and scalars return the input unchanged.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>transpose(x)</code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">x</code>
              </td>
              <td>Array | Matrix</td>
              <td>Matrix to be transposed</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>The transposed matrix</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            const A = [[1, 2, 3], [4, 5, 6]] <br />
            transpose(A) <br />
            {"//"} returns [[1, 4], [2, 5], [3, 6]]
          </code>
        </pre>
      </div>
    ),
  },
  {
    math: "zeros",
    text: (
      <div>
        <h1>Function zeros</h1>

        <p>
          Create a matrix filled with zeros. The created matrix can have one or multiple dimensions.
        </p>

        <h2>Syntax</h2>

        <pre className="highlight">
          <code>
            zeros(m) <br />
            zeros(m, format) <br />
            zeros(m, n) <br />
            zeros(m, n, format) <br />
            zeros([m, n]) <br />
            zeros([m, n], format)
          </code>
        </pre>

        <h3>Parameters</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">size</code>
              </td>
              <td>…(number | BigNumber) | Array</td>
              <td>The size of each dimension of the matrix</td>
            </tr>
            <tr>
              <td>
                <code className="language-plaintext highlighter-rouge">format</code>
              </td>
              <td>string</td>
              <td>The Matrix storage format</td>
            </tr>
          </tbody>
        </table>

        <h3>Returns</h3>

        <table className="highlight-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Array | Matrix</td>
              <td>A matrix filled with zeros</td>
            </tr>
          </tbody>
        </table>

        <h3>Throws</h3>

        <p>Type | Description —- | ———–</p>

        <h2>Examples</h2>

        <pre className="highlight">
          <code>
            zeros() <br />
            {"//"} returns [] <br /> <br />
            zeros(3) <br />
            {"//"} returns [0, 0, 0] <br /> <br />
            zeros(3, 2) <br />
            {"//"} returns [[0, 0], [0, 0], [0, 0]] <br /> <br />
            zeros(3, &apos;dense&apos;) <br />
            {"//"} returns [0, 0, 0] <br /> <br />
            const A = [[1, 2, 3], [4, 5, 6]] <br />
            zeros(size(A)) <br />
            {"//"} returns [[0, 0, 0], [0, 0, 0]]
          </code>
        </pre>
      </div>
    ),
  },
];

export default definitionsTree;
